import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { motion } from 'framer-motion';
import {
  AlertCircle,
  ArrowLeft,
  BookOpen,
  Calculator,
  CheckCircle2,
  FileText,
  Loader2,
  Plus,
  Settings2,
  ShieldCheck,
  Sparkles,
  Trash2,
  Users,
  X,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Header from '../layout/Header';
import {
  createCyberProspect,
  applyCyberProposalDiscount,
  deleteCyberProspect,
  removeCyberProposalDiscount,
  exportCyberClientVisual,
  generateCyberDeliveryBriefing,
  generateCyberProposal,
  getCyberAdminSnapshot,
  getCyberProposalDetail,
  getCyberProposals,
  getCyberProspectDetail,
  getCyberProspects,
  getCyberQuestions,
  getCyberServices,
  publishCyberAdminVersion,
  saveCyberAdminEntity,
  saveCyberProposalDraft,
  saveCyberProspectAnswers,
  updateCyberProspect,
  updateCyberProposalLineItem,
  updateCyberProposalStatus,
  type CyberAdminSnapshot,
  type CyberAnswer,
  type CyberGeneratedProposal,
  type CyberProposal,
  type CyberProposalActivity,
  type CyberProposalLineItem,
  type CyberProspect,
  type CyberProspectDetail,
  type CyberQuestion,
  type CyberService,
} from '../services/cyberOffersService';

const statusLabels: Record<string, string> = {
  draft: 'Brouillon',
  qualification: 'Wizard',
  recommendation: 'Services recommandés',
  pricing: 'Prix',
  proposal: 'Proposition',
  ready: 'Prête à présenter',
  review: 'En négociation',
  presented: 'Présentée',
  accepted: 'Acceptée',
  refused: 'Refusée',
  converted: 'Convertie',
  sent: 'Offre envoyée',
  won: 'Acceptée',
  lost: 'Refusée',
};

function statusLabel(status: string) {
  return statusLabels[status] || status;
}

type CyberCatalogOffer = {
  code?: string;
  name: string;
  description: string;
  steps: { step: string; involved: string; flow: string }[];
  deliverables: string[];
  notes: string[];
};

function renderCatalogDescription(offer: CyberCatalogOffer) {
  if (offer.code === 'consultation') {
    const covered = [
      'Révision de documentation existante: politiques, procédures ou plans déjà en place.',
      'Validation de formulaires d’assurance et réponses aux questions spécifiques.',
      'Analyse de configurations techniques: pare-feu, VPN, Microsoft 365, MFA et systèmes critiques.',
      'Évaluation sommaire des contrôles existants pour identifier des failles ou écarts importants.',
      'Recommandations ciblées pour améliorer la posture de sécurité et prioriser les actions correctives.',
      'Conseils sur les accès, privilèges, sauvegardes, récupération, collaboration sécurisée et mots de passe.',
      'Validation ponctuelle liée à la Loi 25 ou aux risques tiers, sans accompagnement complet ni rapport formel.',
    ];
    const excluded = ['Rédaction de documentation.', 'Réalisation complète des autres services spécialisés offerts.'];
    return (
      <div className="mt-3 space-y-4">
        <p className="text-sm leading-relaxed text-slate-700">Le service de consultation répond à des besoins ponctuels en cybersécurité qui ne nécessitent pas un mandat complet. Il permet d’obtenir rapidement un avis d’expert, une analyse ciblée ou des recommandations pratiques selon les priorités du client.</p>
        <div className="rounded-2xl border border-blue-100 bg-blue-50 p-4">
          <h6 className="text-sm font-black text-blue-950">Ce qui peut être couvert</h6>
          <div className="mt-3 grid grid-cols-1 gap-2 lg:grid-cols-2">
            {covered.map((item) => <div key={item} className="rounded-xl bg-white p-3 text-sm leading-snug text-slate-700 ring-1 ring-blue-100">{item}</div>)}
          </div>
        </div>
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
          <h6 className="text-sm font-black text-amber-950">Hors portée de la consultation</h6>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-amber-900">{excluded.map((item) => <li key={item}>{item}</li>)}</ul>
        </div>
      </div>
    );
  }
  if (offer.code === 'sat-usecure') {
    const modules = [
      { title: 'Analyse des risques humains', detail: 'Évaluation initiale du niveau de maturité en sensibilisation.' },
      { title: 'uLearn', detail: 'Sélection, automatisation et suivi des formations.' },
      { title: 'uPhish', detail: 'Campagnes de phishing simulées: thèmes, fréquence et groupes cibles.' },
      { title: 'uBreach', detail: 'Surveillance du domaine, alertes et gestion des incidents de fuite d’identifiants.' },
      { title: 'Tableaux de bord et rapports', detail: 'Statistiques, complétion des formations, taux d’échec au phishing et tendances.' },
    ];
    return (
      <div className="mt-3 space-y-4">
        <p className="text-sm leading-relaxed text-slate-700">Le service SAT/uSecure renforce la posture de cybersécurité en ciblant le facteur humain. Il combine formation, simulations d’hameçonnage, surveillance des fuites d’identifiants et tableaux de bord de suivi.</p>
        <div className="rounded-2xl border border-cyan-100 bg-cyan-50 p-4">
          <h6 className="text-sm font-black text-cyan-950">Modules inclus dans SAT/uSecure</h6>
          <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-5">
            {modules.map((module) => <div key={module.title} className="rounded-xl bg-white p-3 ring-1 ring-cyan-100"><strong className="block text-sm text-slate-900">{module.title}</strong><span className="mt-1 block text-xs leading-relaxed text-slate-600">{module.detail}</span></div>)}
          </div>
        </div>
      </div>
    );
  }
  return <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-slate-700">{offer.description}</p>;
}

const cyberOfferCatalog: CyberCatalogOffer[] = [
  {
    code: "politique-cyber-uati",
    name: "Politique de cybersécurité sur l’utilisation des actifs TI",
    description: "La politique de cybersécurité – cadre d’utilisation des actifs TI – est un document officiel qui définit les règles, responsabilités, bonnes et mauvaises pratiques visant à encadrer l’usage des technologies de l’information (TI) et à prévenir les incidents liés aux menaces numériques. Elle s’adresse à tous les utilisateurs ayant accès aux ressources technologiques de l’organisation : employés, gestionnaires, contractuels et consultants. Son objectif principal est d’assurer une utilisation sécuritaire des actifs TI et de réduire les risques liés aux erreurs humaines\n\nÉléments couverts : Exemples d'éléments couverts. Gestion des accès et des mots de passe. Utilisation des courriels et des médias sociaux. Logiciels autorisés. Utilisation de l’intelligence artificielle. Comportements numériques sécuritaires. Mesures spécifiques pour le télétravail ou les déplacements (ex : VPN, verrouillage d’écran, limitation géographique).",
    steps: [
      {
        step: "1.Rencontre de collecte d’informations",
        involved: "Membres décideurs\nConseiller en cybersécurité",
        flow: "Votre conseiller en cybersécurité vous guidera à travers un questionnaire visant à définir la portée de la politique, les règles générales d’utilisation, les responsabilités des utilisateurs, ainsi que les pratiques permises et interdites."
      },
      {
        step: "2.Élaboration de la politique de cybersécurité",
        involved: "Conseiller en cybersécurité",
        flow: "À partir des informations recueillies, votre conseiller en cybersécurité élaborera une politique adaptée à votre réalité organisationnelle et alignée sur les pratiques courantes du secteur."
      },
      {
        step: "3.Remise et approbation de la politique de cybersécurité",
        involved: "Membres décideurs\nConseiller en cybersécurité",
        flow: "Votre conseiller en cybersécurité vous remettra votre politique pour révision et des correctifs pourront être apportés, le cas échéant."
      },
      {
        step: "4.Communication et adoption de la politique de cybersécurité",
        involved: "Membres décideurs\nEmployés",
        flow: "Bien que notre équipe ne soit pas impliquée directement à cette étape, nous recommandons de mettre en place une stratégie de communication interne pour assurer une bonne compréhension de la politique par les utilisateurs."
      }
    ],
    deliverables: [
      "Un document officiel et utilisable représentant leur politique de cybersécurité adaptée à l’organisation, couvrant l’utilisation sécuritaire des actifs TI par les employés."
    ],
    notes: [
      "Si le client désire une traduction ou des délacements pour des rencontres en personnes sont des points à considérer dans la grille des coûts finaux."
    ]
  },
  {
    code: "politique-cyber-gsti",
    name: "Politique de cybersécurité sur la gestion de la sécurité des actifs TI",
    description: "La politique de cybersécurité – Gestion de la sécurité des actifs TI est un cadre stratégique qui définit les règles et les pratiques nécessaires pour protéger efficacement les actifs technologiques de l’organisation (équipements, logiciels, données). Elle établit les responsabilités des parties prenantes et les exigences pour réduire les risques liés à la compromission des actifs, à améliorer la résilience organisationnelle et à assurer la conformité aux meilleures pratiques en cybersécurité.\n\nÉléments couverts : Exemples d'éléments couverts. Inventorier et classifier les actifs selon leur criticité (critique, sensible, public). Création, gestion et suppression des identités des accès. Procédure de configuration sécuritaire des systèmes. Gestion du cycle de vie des actifs (acquisition, déploiement, retrait sécurisé). Surveillance et détection des incidents.",
    steps: [
      {
        step: "1. Rencontre de collecte d’informations",
        involved: "Membres décideurs\nConseiller en cybersécurité",
        flow: "Votre conseiller en cybersécurité vous guidera à travers un questionnaire visant à définir la portée de la politique et à comprendre votre organisation et ses pratiques de cybersécurité."
      },
      {
        step: "2. Élaboration de la politique de cybersécurité",
        involved: "Conseiller en cybersécurité",
        flow: "À partir des informations recueillies, votre conseiller en cybersécurité élaborera une politique adaptée à votre réalité organisationnelle et alignée sur les pratiques courantes du secteur."
      },
      {
        step: "3. Remise et approbation de la politique de cybersécurité",
        involved: "Membres décideurs\nConseiller en cybersécurité",
        flow: "Votre conseiller en cybersécurité vous remettra votre politique pour révision et des correctifs pourront être apportés, le cas échéant."
      },
      {
        step: "4. Communication et adoption de la politique de cybersécurité",
        involved: "Membres décideurs\nEmployés",
        flow: "Bien que notre équipe ne soit pas impliquée directement à cette étape, nous recommandons de mettre en place une stratégie de communication à tous les intervenants ayant un rôle à jouer dans la gestion de la sécurité des actifs TI."
      }
    ],
    deliverables: [
      "Un document officiel et utilisable représentant leur politique de cybersécurité adaptée à l’organisation, couvrant l'ensemble de vos pratiques de gestion de la sécurité des actifs TI."
    ],
    notes: [
      "Si le client désire une traduction ou des délacements pour des rencontres en personnes sont des points à considérer dans la grille des coûts finaux."
    ]
  },
  {
    code: "questionnaire-nist",
    name: "Questionnaire NIST",
    description: "Ce rapport présente l’état actuel de la conformité d’une organisation au cadre de cybersécurité du NIST (National Institute of Standards and Technology). Il vise à dresser un portrait objectif de la posture de cybersécurité de l’entreprise, à identifier les écarts par rapport aux bonnes pratiques recommandées, et à proposer des recommandations initiales pour améliorer la sécurité des systèmes d’information. Le rapport s’appuie sur un questionnaire structuré de 33 questions, permettant d’évaluer votre posture globale en cybersécurité selon les six fonctions du cadre NIST : Gouvernance, Identification, Protection, Détection, Réponse et Reprise. Il sert de référence pour le suivi de la conformité et la planification des prochaines étapes en suivant des recommandations simples.",
    steps: [
      {
        step: "1.Rencontre de collecte d’informations",
        involved: "Membres décideurs\nConseiller en cybersécurité",
        flow: "Le conseiller en cybersécurité anime une rencontre pour expliquer le cadre NIST, définir la portée de l’évaluation et recueillir les informations nécessaires sur les pratiques actuelles en répondant aux 33 questions."
      },
      {
        step: "2.Analyse des réponses et rédaction du rapport",
        involved: "Conseiller en cybersécurité",
        flow: "Rédaction d’un rapport détaillé incluant le score par fonction, les écarts identifiés, les recommandations prioritaires et une feuille de route pour améliorer la posture de cybersécurité."
      },
      {
        step: "3.Remise et approbation de la politique de cybersécurité",
        involved: "Membres décideurs\nConseiller en cybersécurité",
        flow: "Présentation des résultats et recommandations, discussion des priorités et validation des prochaines étapes."
      }
    ],
    deliverables: [
      "Un rapport complet évaluant la conformité de l’organisation au cadre NIST, incluant le score par fonction, les écarts identifiés, des recommandations prioritaires et une feuille de route pour améliorer la posture de cybersécurité."
    ],
    notes: [
      "N'est pas vraiment impacté par la grosseur du client, nombre d'employés, etc.",
      "Peut être impacté par les connaissances de la personne interrogée.",
      "Si le client désire une traduction ou des délacements pour des rencontres en personnes sont des points à considérer dans la grille des coûts finaux."
    ]
  },
  {
    code: "politique-ia",
    name: "Politique d’utilisation de l’IA",
    description: "La politique d’utilisation de l’IA est un document qui définit les règles, responsabilités et bonnes pratiques pour encadrer l’usage des outils et technologies d’intelligence artificielle dans l’organisation. Elle vise à prévenir les risques liés à la confidentialité, à la sécurité des données et à l’éthique, tout en favorisant une utilisation responsable et conforme aux lois et normes applicables.\n\nÉléments couverts : Exemples d'éléments couverts. Utilisation des outils IA internes et externes. Protection des données sensibles et personnelles. Respect des lois et réglementations. Transparence et traçabilité des décisions automatisées. Limitation des biais et discrimination algorithmique. Formation et sensibilisation des employés.",
    steps: [
      {
        step: "1.Rencontre de collecte d’informations",
        involved: "Membres décideurs\nConseiller en cybersécurité",
        flow: "Votre conseiller en cybersécurité vous guidera à travers une rencontre pour définir la portée de la politique, identifier les cas d’usage de l’IA, les risques associés et les exigences légales et éthiques."
      },
      {
        step: "2. Élaboration de la politique d’utilisation de l’IA",
        involved: "Conseiller en cybersécurité",
        flow: "Rédaction d’une politique adaptée à l’organisation, incluant les règles d’utilisation, les contrôles de sécurité, les obligations légales et les recommandations éthiques."
      },
      {
        step: "3.Remise et approbation de la politique",
        involved: "Membres décideurs\nConseiller en cybersécurité",
        flow: "Présentation de la politique pour validation et ajustements selon les commentaires des parties prenantes."
      },
      {
        step: "4.Communication et adoption de la politique de cybersécurité",
        involved: "Membres décideurs\nEmployés",
        flow: "Bien que notre équipe ne soit pas impliquée directement à cette étape, nous recommandons de mettre en place une stratégie de communication interne pour assurer une bonne compréhension de la politique par les utilisateurs."
      }
    ],
    deliverables: [
      "Un document officiel et utilisable représentant la politique d’utilisation de l’IA, adapté à l’organisation, incluant les règles, les responsabilités et les recommandations pour une utilisation sécuritaire et éthique des technologies IA."
    ],
    notes: [
      "Prévoir une mise à jour annuelle ou lors de l’intégration de nouvelles solutions IA.",
      "Ajouter une séance de sensibilisation pour les employés.",
      "Si le client désire une traduction ou des délacements pour des rencontres en personnes sont des points à considérer dans la grille des coûts finaux."
    ]
  },
  {
    code: "pici",
    name: "Plan d’intervention en cas d’incident",
    description: "Le plan d’intervention en cas d’incident est un document stratégique qui définit les procédures à suivre pour détecter, analyser, contenir et résoudre un incident de cybersécurité. Il vise à réduire l’impact des incidents, assurer la continuité des opérations et respecter les obligations légales et réglementaires.\n\nÉléments couverts : Exemples d'éléments couverts. Identification des incidents : critères pour reconnaître un incident de cybersécurité. Classification et priorisation : niveaux de gravité et impact sur les opérations. Procédures de confinement : actions immédiates pour limiter la propagation. Communication interne et externe : messages types, obligations légales, notification aux autorités. Coordination des rôles : responsabilités des équipes TI, direction, fournisseurs. Documentation et suivi : rapport post-incident, leçons apprises, mise à jour du plan.",
    steps: [
      {
        step: "1.Rencontre de collecte d’informations",
        involved: "Membres décideurs\nConseiller en cybersécurité",
        flow: "Rencontre pour comprendre l’environnement TI, les risques, les obligations légales et les processus existants afin de définir les différentes procédures à suivre et les rôles de l'ensemble des intervenants internes et externes."
      },
      {
        step: "2. Élaboration du plan d’intervention",
        involved: "Conseiller en cybersécurité",
        flow: "Rédaction du plan incluant les procédures de détection, analyse, confinement, éradication et reprise adapté à la réalité de l’organisation."
      },
      {
        step: "3. Validation et ajustements",
        involved: "Membres décideurs\nConseiller en cybersécurité",
        flow: "Présentation du plan pour validation, ajustements selon les commentaires et intégration des recommandations."
      },
      {
        step: "4.Communication et adoptiondu plan",
        involved: "Membres décideurs\nEmployés",
        flow: "Recommandation d’un plan de communication interne et d’une formation pour assurer la compréhension et l’application du plan par les équipes (non inclus dans les heures)."
      }
    ],
    deliverables: [
      "Un document complet et utilisable représentant le plan d’intervention en cas d’incident, incluant les rôles et responsabilités et les procédures à suivre pour une réponse efficace aux incidents."
    ],
    notes: [
      "Prévoir une mise à jour annuelle ou après tout incident majeur.",
      "Ajouter une simulation d’incident (exercice pratique) pour tester le plan.",
      "Considérer les frais de traduction ou de déplacement si nécessaire."
    ]
  },
  {
    code: "plan-reprise",
    name: "Plan de reprise",
    description: "Le plan de reprise est un document stratégique qui définit les actions à entreprendre pour restaurer les systèmes et les opérations après un incident majeur. Il vise à assurer la continuité des activités, réduire les impacts financiers et opérationnels, et respecter les obligations légales et réglementaires.\n\nÉléments couverts : Exemples d'éléments couverts. Analyse d’impact et objectifs de reprise : identification des systèmes critiques, des dépendances et des objectifs de délai reliés à la reprise des activités. Procédures de restauration : étapes pour remettre les systèmes en production après un incident. Plan de communication : messages internes et externes pendant la reprise. Coordination des rôles : responsabilités des équipes TI, direction et fournisseurs. Tests et validation : scénarios de simulation pour vérifier l’efficacité du plan.",
    steps: [
      {
        step: "1. Rencontre et échanges pour documenter les sauvegardes",
        involved: "Membres décideurs\nConseiller en cybersécurité",
        flow: "Collecte des informations sur les systèmes critiques, les sauvegardes existantes et les objectifs de reprise (RTO/RPO)."
      },
      {
        step: "2. Documentation initiale",
        involved: "Conseiller en cybersécurité",
        flow: "Rédaction des sections préliminaires du plan (inventaire, dépendances, objectifs)."
      },
      {
        step: "3. Rencontre pour valider et discuter des options de reprise",
        involved: "Membres décideurs\nConseiller en cybersécurité",
        flow: "Validation des objectifs, discussion des scénarios de reprise et des ressources nécessaires."
      },
      {
        step: "4. Documentation des procédures de restauration",
        involved: "Conseiller en cybersécurité",
        flow: "Rédaction des étapes détaillées pour restaurer les systèmes, incluant les rôles et responsabilités."
      },
      {
        step: "5. Présentation du plan complet",
        involved: "Membres décideurs\nConseiller en cybersécurité",
        flow: "Présentation du plan pour validation finale et ajustements."
      },
      {
        step: "6. Documentation finale et remise",
        involved: "Conseiller en cybersécurité",
        flow: "Finalisation du document officiel et remise au client."
      }
    ],
    deliverables: [
      "Un document officiel et complet représentant le plan de reprise, incluant les procédures, les rôles et responsabilités, et les recommandations pour une restauration rapide et efficace des systèmes critiques."
    ],
    notes: [
      "Prévoir une mise à jour annuelle ou après tout incident majeur.",
      "Ajouter une simulation de reprise (exercice pratique) pour tester le plan.",
      "Considérer les frais de traduction ou de déplacement si nécessaire."
    ]
  },
  {
    code: "rer-tiers",
    name: "Rapport d’évaluation des risques tiers",
    description: "Ce service consiste à accompagner l’entreprise dans l’évaluation de la posture de sécurité d’un fournisseur ou partenaire externe, afin de réduire les risques liés à la chaîne d’approvisionnement et assurer la conformité aux exigences réglementaires et contractuelles. L’objectif est de guider le client dans le processus, en expliquant les critères, en analysant ensemble les réponses et en co-construisant les recommandations.\n\nÉléments couverts : Exemples d'éléments couverts. Questionnaire de sécurité à envoyer au fournisseur et/ou partenaire. Analyse des politiques et pratiques du fournisseur (gestion des accès, sauvegardes, chiffrement, etc.). Évaluation des risques liés aux données sensibles et à la conformité (par exemple: Loi 25). Vérification des certifications et attestations (ISO 27001, SOC 2, etc.). Rapport avec score de risque et recommandations.",
    steps: [
      {
        step: "1. Rencontre de cadrage",
        involved: "Membres décideurs\nConseiller en cybersécurité",
        flow: "Définir la portée de l’évaluation, identifier les fournisseurs critiques et expliquer la méthodologie."
      },
      {
        step: "2. Explication du questionnaire et des critères",
        involved: "Conseiller en cybersécurité\nClient",
        flow: "Présenter le questionnaire, expliquer chaque section et les critères d’évaluation pour que le client comprenne la logique derrière les questions."
      },
      {
        step: "3. Collecte des réponses et documents",
        involved: "Client\nFournisseur/partenaire",
        flow: "Le client transmet le questionnaire au fournisseur et récupère les documents (politiques, certifications)."
      },
      {
        step: "4. Analyse collaborative des réponses",
        involved: "Conseiller en cybersécurité\nClient",
        flow: "Réviser ensemble les réponses et documents, identifier les écarts et discuter des risques associés."
      },
      {
        step: "5. Élaboration conjointe des recommandations",
        involved: "Conseiller en cybersécurité\nClient",
        flow: "Co-construire un plan d’action ou des exigences contractuelles pour réduire les risques identifiés."
      }
    ],
    deliverables: [
      "Un rapport complet d’évaluation des risques tiers incluant le score de risque, les écarts identifiés, les recommandations et une feuille de route pour sécuriser la relation avec le fournisseur ou partenaire."
    ],
    notes: [
      "Prévoir une mise à jour annuelle ou lors de l’ajout d’un nouveau fournisseur critique.",
      "Possibilité d’intégrer un suivi continu (monitoring) pour les fournisseurs stratégiques.",
      "Considérer les frais de traduction ou de déplacement si nécessaire."
    ]
  },
  {
    code: "posture-365",
    name: "Évaluation de l’environnement Microsoft 365 unique",
    description: "Ce service vous permet de vérifier la configuration et de mesurer la sécurité de votre environnement Microsoft 365, d’identifier les écarts par rapport aux bonnes pratiques de cybersécurité et de prioriser les actions à mettre en place pour réduire vos risques. Vous recevez un diagnostic clair de votre posture en cybersécurité, ainsi qu’un plan d’action de correctifs concrets classés par ordre de priorité/criticité.\nL’évaluation s’appuie notamment sur Maester365, qui analyse les contrôles de sécurité clés de Microsoft 365, incluant entre autres :\nLa gestion des accès et des privilèges (utilisateurs, administrateurs, comptes à haut risque);\nLes mécanismes de protection des identités (authentification multifacteur, accès conditionnel);\nLa sécurisation des données et des environnements de collaboration (Exchange, SharePoint, Teams);\nLes paramètres de sécurité recommandés par Microsoft et les standards de l’industrie.\n \nDeux façons d’offrir le service\n1. Évaluation unique\nCette option consiste en une analyse complète réalisée une seule fois. Elle est idéale pour obtenir un portrait clair de la situation actuelle et identifier les priorités d’amélioration. Le temps requis varie selon la complexité de votre environnement.\nEffort nécessaire: 1x, entre 6h et 20h, selon la complexité\n2. Évaluation récurrente\nCette option vise un accompagnement dans le temps. Chaque mois, nous consacrons un nombre d'heures fixe pour suivre l'évolution de votre posture Microsoft 365, valider les changements et vous conseiller de façon continue. À mesure que votre entreprise évolue (nouveaux employés, nouveaux outils, changements organisationnels), votre exposition aux risques change aussi. Cette approche permet de gérer votre posture de sécurité au même rythme que l'évolution de votre entreprise.\nEffort nécessaire: 3h/mois x 12 mois minimum :",
    steps: [
      {
        step: "1. Demande d’accès",
        involved: "Administrateur M365, conseiller en cybersécurité",
        flow: "Demander et obtenir les accès nécessaires à l’environnement Microsoft 365."
      },
      {
        step: "2. Évaluation",
        involved: "Conseiller en cybersécurité",
        flow: "Réaliser l’évaluation complète de la posture de sécurité et des configurations."
      },
      {
        step: "3. Rédaction du rapport",
        involved: "Conseiller en cybersécurité",
        flow: "Rédiger un rapport synthétique présentant les constats et recommandations."
      },
      {
        step: "4. Présentation",
        involved: "Administrateur M365, conseiller en cybersécurité",
        flow: "Présenter le rapport au client, expliquer les résultats et conseiller sur la mise en œuvre."
      }
    ],
    deliverables: [
      "Une évaluation complète de l’environnement Microsoft 365 est réalisée. Un rapport synthétique est ensuite rédigé, présentant les constats et recommandations clés. Enfin, ce rapport est présenté au client, avec explications et conseils pour faciliter la mise en œuvre des mesures proposées."
    ],
    notes: [
      "Prévoir une réévaluation périodique (trimestrielle ou annuelle).",
      "Mettre en œuvre les recommandations prioritaires dès que possible (Possibilité de facturation TI?).",
      "Former les administrateurs sur les bonnes pratiques de sécurité M365 (Possibilité d'heures de consultation additionnelles)."
    ]
  },
  {
    code: "posture-365",
    name: "Évaluation de l’environnement Microsoft 365 récurrente",
    description: "Ce service vous permet de vérifier la configuration et de mesurer la sécurité de votre environnement Microsoft 365, d’identifier les écarts par rapport aux bonnes pratiques de cybersécurité et de prioriser les actions à mettre en place pour réduire vos risques. Vous recevez un diagnostic clair de votre posture en cybersécurité, ainsi qu’un plan d’action de correctifs concrets classés par ordre de priorité/criticité.\nL’évaluation s’appuie notamment sur Maester365, qui analyse les contrôles de sécurité clés de Microsoft 365, incluant entre autres :\nLa gestion des accès et des privilèges (utilisateurs, administrateurs, comptes à haut risque);\nLes mécanismes de protection des identités (authentification multifacteur, accès conditionnel);\nLa sécurisation des données et des environnements de collaboration (Exchange, SharePoint, Teams);\nLes paramètres de sécurité recommandés par Microsoft et les standards de l’industrie.\n \nDeux façons d’offrir le service\n1. Évaluation unique\nCette option consiste en une analyse complète réalisée une seule fois. Elle est idéale pour obtenir un portrait clair de la situation actuelle et identifier les priorités d’amélioration. Le temps requis varie selon la complexité de votre environnement.\nEffort nécessaire: 1x, entre 6h et 20h, selon la complexité\n2. Évaluation récurrente\nCette option vise un accompagnement dans le temps. Chaque mois, nous consacrons un nombre d'heures fixe pour suivre l'évolution de votre posture Microsoft 365, valider les changements et vous conseiller de façon continue. À mesure que votre entreprise évolue (nouveaux employés, nouveaux outils, changements organisationnels), votre exposition aux risques change aussi. Cette approche permet de gérer votre posture de sécurité au même rythme que l'évolution de votre entreprise.\nEffort nécessaire: 3h/mois x 12 mois minimum :",
    steps: [
      {
        step: "1. Demande d’accès",
        involved: "Administrateur M365, conseiller en cybersécurité",
        flow: "Demander et obtenir les accès nécessaires à l’environnement Microsoft 365."
      },
      {
        step: "2. Évaluation",
        involved: "Conseiller en cybersécurité",
        flow: "Réaliser l’évaluation complète de la posture de sécurité et des configurations."
      },
      {
        step: "3. Rédaction du rapport",
        involved: "Conseiller en cybersécurité",
        flow: "Rédiger un rapport synthétique présentant les constats et recommandations."
      },
      {
        step: "4. Présentation",
        involved: "Administrateur M365, conseiller en cybersécurité",
        flow: "Présenter le rapport au client, expliquer les résultats et conseiller sur la mise en œuvre."
      }
    ],
    deliverables: [
      "Une évaluation complète de l’environnement Microsoft 365 est réalisée. Un rapport synthétique est ensuite rédigé, présentant les constats et recommandations clés. Enfin, ce rapport est présenté au client, avec explications et conseils pour faciliter la mise en œuvre des mesures proposées."
    ],
    notes: [
      "Prévoir une réévaluation périodique (trimestrielle ou annuelle).",
      "Mettre en œuvre les recommandations prioritaires dès que possible (Possibilité de facturation TI?).",
      "Former les administrateurs sur les bonnes pratiques de sécurité M365 (Possibilité d'heures de consultation additionnelles).",
      "Version récurrente : prévoir une réévaluation périodique et un suivi de l’évolution des constats dans le temps."
    ]
  },
  {
    code: "audit-rer",
    name: "Rapport d’évaluation des risques TI (Audit de sécurité)",
    description: "L’évaluation des risques TI (ou l'audit de sécurité) vise à analyser, documenter et prioriser les risques liés aux actifs technologiques essentiels à l’organisation. Elle s’appuie sur des inspections sur site, des entretiens avec les responsables TI, l’analyse des données de sécurité (ex. Microsoft 365), l’examen des incidents antérieurs, l’intégration des résultats de balayage de vulnérabilités et la prise en compte des risques documentés dans la littérature spécialisée.\nCe service permet d’obtenir une vue d’ensemble des menaces, vulnérabilités et risques, ainsi que des recommandations concrètes pour renforcer la posture de cybersécurité et assurer la continuité des activités.\n\nAudit de sécurité/Rapport d'évaluation des risques: \nL’audit de sécurité et le rapport d’évaluation des risques reposent sur la même méthodologie et le même livrable. \nLa distinction se fait uniquement au niveau du positionnement : l’audit sert de point d’entrée pour un nouveau client, tandis que l’évaluation des risques s’inscrit dans une démarche continue d’accompagnement.\n\nÉléments couverts : Exemples d'éléments couverts. Identification des actifs TI critiques. Analyse des menaces : naturelles, accidentelles, délibérées. Identification des vulnérabilités technologiques, organisationnelles et humaines. Classification des risques selon leur niveau de gravité (critique, élevé, moyen, faible). Revue des contrôles de sécurité en place. Intégration des résultats de balayage de vulnérabilités. Recommandations de mesures d'atténuation des risques.",
    steps: [
      {
        step: "1. Rencontre initiale",
        involved: "Membres décideurs, Conseiller en cybersécurité",
        flow: "Démarrage de l’audit, collecte des informations clés et définition de la portée."
      },
      {
        step: "2. Questionnaire NIST",
        involved: "Membres décideurs, Intervenants TI, Conseiller en cybersécurité",
        flow: "Évaluation du niveau de maturité en cybersécurité (gouvernance, contrôles, processus)."
      },
      {
        step: "3. Inventaire des actifs",
        involved: "Intervenants TI, Conseiller en cybersécurité",
        flow: "Identification des systèmes, applications et données critiques."
      },
      {
        step: "4. Analyse documentaire",
        involved: "Conseiller en cybersécurité",
        flow: "Revue des politiques, procédures et documentation existante."
      },
      {
        step: "5. Posture Microsoft 365",
        involved: "Conseiller en cybersécurité",
        flow: "Analyse du score de sécurité, des configurations et des contrôles."
      },
      {
        step: "6. Analyse des domaines",
        involved: "Conseiller en cybersécurité",
        flow: "Vérification des domaines, DNS, exposition externe et sécurisation."
      },
      {
        step: "7. Analyse de l’infrastructure TI",
        involved: "Intervenants TI, Conseiller en cybersécurité",
        flow: "Évaluation des systèmes on-prem, accès et configurations."
      },
      {
        step: "8. Échange avec intervenants techniques",
        involved: "Intervenants TI, Conseiller en cybersécurité",
        flow: "Entrevues pour valider les pratiques et configurations."
      },
      {
        step: "9. Analyse de la sécurité réseau",
        involved: "Intervenants TI, Conseiller en cybersécurité",
        flow: "Revue des pare-feu, segmentation, accès distant et filtrage."
      },
      {
        step: "10. Analyse de la sécurité physique",
        involved: "Intervenants TI, Conseiller en cybersécurité",
        flow: "Vérification des contrôles d’accès physique."
      },
      {
        step: "11. Analyse de la sécurité opérationnelle",
        involved: "Conseiller en cybersécurité",
        flow: "Évaluation des processus, gestion des incidents et sensibilisation."
      },
      {
        step: "12. Campagne d’hameçonnage",
        involved: "Utilisateurs, Conseiller en cybersécurité",
        flow: "Simulation et/ou analyse des comportements face aux attaques."
      },
      {
        step: "13. Évaluation du Dark Web",
        involved: "Conseiller en cybersécurité",
        flow: "Recherche de fuites de données et identifiants compromis."
      },
      {
        step: "14. Analyse de la stratégie de sauvegarde",
        involved: "Intervenants TI, Conseiller en cybersécurité",
        flow: "Évaluation des sauvegardes, restauration et résilience."
      },
      {
        step: "15. Balayage de vulnérabilités",
        involved: "Conseiller en cybersécurité",
        flow: "Balayage complet et analyse des vulnérabilités."
      },
      {
        step: "16. Analyse des menaces et vulnérabilités",
        involved: "Conseiller en cybersécurité",
        flow: "Corrélation des constats et identification des risques."
      },
      {
        step: "17. Évaluation des impacts et calcul du risque",
        involved: "Conseiller en cybersécurité",
        flow: "Construction de la matrice des risques."
      },
      {
        step: "18. Rédaction du rapport d’audit",
        involved: "Conseiller en cybersécurité",
        flow: "Compilation des constats et recommandations."
      },
      {
        step: "19. Présentation et validation",
        involved: "Membres décideurs, Conseiller en cybersécurité",
        flow: "Présentation, priorisation des actions et ajustements."
      }
    ],
    deliverables: [
      "Le livrable consiste en un rapport détaillé qui présente l’ensemble des risques identifiés pour les actifs technologiques essentiels à l’organisation. Ce document offre une analyse structurée des menaces et des vulnérabilités, une classification des risques selon leur niveau de gravité, ainsi que des recommandations concrètes et adaptées pour renforcer la posture de cybersécurité. Il inclut également des tableaux synthétiques, un sommaire des actions prioritaires à mettre en œuvre et des annexes pertinentes, permettant à l’organisation de disposer d’une vue d’ensemble claire pour orienter ses efforts et assurer la continuité de ses opérations."
    ],
    notes: [
      "Prévoir une mise à jour annuelle ou après tout changement majeur.",
      "Considérer les frais de traduction ou de déplacement si nécessaire."
    ]
  },
  {
    code: "balayage-vulnerabilites",
    name: "Balayage de vulnérabilités",
    description: "Le balayage de vulnérabilités consiste à analyser les systèmes présents sur le réseau afin d’identifier les failles de sécurité qui pourraient être exploitées par des cyberattaques. Ce service inclut la configuration des outils de scan, l’exécution des tests, l’analyse des résultats et la remise d’un rapport détaillé avec recommandations.",
    steps: [
      {
        step: "1. Rencontre de prise de besoins",
        involved: "Membres décideurs\nConseiller en cybersécurité",
        flow: "Rencontre pour définir la portée du balayage (IP internes, externes, site web), valider les accès et les contraintes techniques."
      },
      {
        step: "2. Configuration initiale du balayage",
        involved: "Conseiller en cybersécurité",
        flow: "Mise en place des outils de balayage, configuration des paramètres et des cibles à analyser."
      },
      {
        step: "3. Exécution du balayage",
        involved: "Conseiller en cybersécurité",
        flow: "Lancement des balayages internes et externes, collecte des résultats et vérification de la complétude des données."
      },
      {
        step: "4. Analyse des résultats",
        involved: "Conseiller en cybersécurité",
        flow: "Analyse des vulnérabilités détectées, classification par criticité et impact, préparation des recommandations."
      },
      {
        step: "5. Rédaction du rapport",
        involved: "Conseiller en cybersécurité",
        flow: "Création d’un rapport détaillé incluant les vulnérabilités, les risques associés et les recommandations pour corriger les failles."
      },
      {
        step: "6. Présentation et validation",
        involved: "Membres décideurs\nConseiller en cybersécurité",
        flow: "Présentation des résultats et discussion des priorités pour la mise en œuvre des correctifs."
      }
    ],
    deliverables: [
      "Un rapport complet incluant la liste des vulnérabilités détectées, leur niveau de criticité et des recommandations pour corriger les failles."
    ],
    notes: [
      "Prévoir des balayages réguliers (trimestriels ou semestriels) pour maintenir le niveau de sécurité.",
      "Considérer les frais de déplacement ou de configuration avancée si nécessaire."
    ]
  },
  {
    code: "restauration-sauvegardes",
    name: "Tests de restauration de sauvegardes",
    description: "Ce service valide concrètement que les sauvegardes critiques peuvent être restaurées et utilisées lors d’un incident, d’une panne ou d’une corruption de données. Il transforme la sauvegarde d’une mesure théorique en capacité de reprise démontrable.",
    steps: [
      { step: "1. Cadrage de la portée", involved: "Client\nConseiller en cybersécurité", flow: "Déterminer les systèmes, types de restauration, volumes, dépendances et niveaux de validation attendus." },
      { step: "2. Préparation des accès et de l’environnement", involved: "Client\nBZ\nFournisseur au besoin", flow: "Valider les accès, la solution de sauvegarde et l’environnement de restauration à utiliser." },
      { step: "3. Exécution des tests", involved: "Conseiller en cybersécurité", flow: "Réaliser les restaurations partielles ou complètes prévues, selon les priorités critiques." },
      { step: "4. Validation des données", involved: "Client\nConseiller en cybersécurité", flow: "Confirmer l’intégrité, l’ouverture des fichiers ou applications et l’utilisabilité des données restaurées." },
      { step: "5. Rapport et recommandations", involved: "Conseiller en cybersécurité", flow: "Documenter les résultats, les écarts, les preuves et les actions d’amélioration recommandées." }
    ],
    deliverables: [
      "Un rapport de tests de restauration incluant la portée, les scénarios testés, les résultats, les écarts observés et les recommandations.",
      "Une validation concrète de la capacité de reprise des sauvegardes critiques."
    ],
    notes: [
      "Le prix varie selon les types de restauration, le nombre de systèmes, le nombre de tests, le volume, l’horaire, l’urgence et le niveau de rapport attendu.",
      "Ce service complète naturellement le Plan de reprise et les activités de résilience opérationnelle."
    ]
  },
  {
    code: "loi-25",
    name: "Accompagnement Loi 25",
    description: "Ce service vise à :\nFaciliter votre conformité aux exigences de la Loi 25 et éviter des sanctions : accompagnement structuré pour mettre en place les politiques, registres et procédures exigés par la réglementation.\nRéduire les risques d’incidents de confidentialité : identification des pratiques à risque, mise en œuvre de contrôles et formation du personnel pour protéger les renseignements personnels.\nDémontrer votre engagement envers la protection des renseignements personnels et gagner la confiance de vos parties prenantes : documentation complète, communication transparente et adoption de bonnes pratiques pour rassurer clients, partenaires et employés. :\n\nÉléments couverts : Le client recevra l’ensemble des documents et outils nécessaires pour assurer la conformité de son organisation à la Loi 25. \nLe client bénéficiera également d’explications claires et d’un soutien personnalisé pour comprendre ses obligations et agir efficacement à titre de responsable de la protection des renseignements personnels. Grâce à cette démarche, le client sera en mesure de démontrer sa conformité, de répondre aux demandes des parties prenantes et d’adopter les meilleures pratiques en matière de gouvernance des renseignements personnels.",
    steps: [
      {
        step: "1. Inventaire des renseignements personnels",
        involved: "Membres décideurs\nConseiller en cybersécurité",
        flow: "Compléter l’inventaire des renseignements personnels et vérifier que les pratiques actuelles respectent les standards de la Loi (accès, utilisation, communication des données)."
      },
      {
        step: "2. Politique de confidentialité",
        involved: "Membres décideurs\nConseiller en cybersécurité",
        flow: "Élaborer la politique, la publier (site web ou autre moyen) et obtenir la signature des employés pour preuve de lecture."
      },
      {
        step: "3. Matrice RACI",
        involved: "Membres décideurs\nConseiller en cybersécurité",
        flow: "Définir les rôles et responsabilités liés à la protection des renseignements personnels et les mettre en œuvre."
      },
      {
        step: "4. Élaborer des politiques et pratiques de gouvernance à l’égard de la protection des renseignements personnels",
        involved: "Membres décideurs\nConseiller en cybersécurité",
        flow: "Rédiger les politiques de gouvernance encadrant, entres autres, les rôles et responsabilités, les règles de conservation et destruction ainsi que le processus de réponse aux plaintes."
      },
      {
        step: "5. Registre des incidents",
        involved: "Membres décideurs\nConseiller en cybersécurité",
        flow: "Comprendre le fonctionnement du registre et élaborer une politique d’utilisation."
      },
      {
        step: "6. Registre des plaintes",
        involved: "Membres décideurs\nConseiller en cybersécurité",
        flow: "Comprendre le fonctionnement du registre et élaborer une politique d’utilisation."
      },
      {
        step: "7. Plan d’intervention en cas d’incident de confidentialité",
        involved: "Membres décideurs\nConseiller en cybersécurité",
        flow: "Créer la procédure de gestion des incidents et la communiquer à tous les employés concernés."
      },
      {
        step: "8. Formation et sensibilisation",
        involved: "Membres décideurs\nEmployés\nConseiller en cybersécurité",
        flow: "Former les employés sur la Loi 25, leurs rôles et responsabilités, et le plan d’intervention."
      },
      {
        step: "9. Formulaire de consentement (employés)",
        involved: "Membres décideurs\nConseiller en cybersécurité",
        flow: "Élaborer un formulaire pour obtenir le consentement des employés concernant la collecte, l’utilisation et la communication de leurs données."
      },
      {
        step: "10. Formulaire de consentement (clients)",
        involved: "Membres décideurs\nConseiller en cybersécurité",
        flow: "Élaborer un formulaire pour obtenir le consentement des clients concernant la collecte, l’utilisation et la communication de leurs données."
      },
      {
        step: "11. Évaluation des facteurs relatifs à la vie privée",
        involved: "Membres décideurs\nConseiller en cybersécurité",
        flow: "Réaliser des ÉFVP pour les données hors Québec (ex. M365) et pour tout projet impactant les renseignements personnels (ex. changement de système ou études/statistiques)."
      },
      {
        step: "12. Logiciel de gestion de la Loi 25",
        involved: "Responsable de la protection des renseignements personnels de votre organisation",
        flow: "Utiliser notre outil pour : \n•\tAccéder à l’ensemble des documents rédigés dans le cadre du programme\n•\tAccéder à l’inventaire des renseignements personnels\n•\tAccéder aux matrices RACI élaborées dans le cadre du programme\n•\tAccéder au registre des incidents\n•\tAccéder au registre des plaintes."
      }
    ],
    deliverables: [
      "Le client recevra l’ensemble des documents et outils nécessaires pour assurer la conformité de son organisation à la Loi 25.",
      "Le client bénéficiera également d’explications claires et d’un soutien personnalisé pour comprendre ses obligations et agir efficacement à titre de responsable de la protection des renseignements personnels. Grâce à cette démarche, le client sera en mesure de démontrer sa conformité, de répondre aux demandes des parties prenantes et d’adopter les meilleures pratiques en matière de gouvernance des renseignements personnels."
    ],
    notes: [
      "Prévoir une hausse du prix selon le nombre de NEQ.",
      "La rédaction de documentation par le client ou un autre fournisseur ne réduit pas la charge de travail, donc n'influence pas le prix. Nous pourrons en faire une lecture pour aider la collecte d'informations, mais nous pourrons lui fournir des nouveaux documents rédigés à notre manière.",
      "Considérer les frais de traduction ou de déplacement si nécessaire."
    ]
  },
  {
    code: "consultation",
    name: "Consultation",
    description: "Le service de consultation répond à des besoins ponctuels en cybersécurité qui ne nécessitent pas un mandat complet d’audit, de rédaction ou d’accompagnement. Il permet d’obtenir un avis d’expert, une analyse ciblée ou des recommandations pratiques selon les priorités du client.\n\nLa consultation peut couvrir la révision de documents existants, la validation de formulaires d’assurance, l’analyse de configurations techniques, la vérification de paramètres de sécurité, l’évaluation sommaire de contrôles, la priorisation d’actions correctives et le soutien à des questions de conformité ou de configuration.\n\nLa consultation n’inclut pas la rédaction de documentation ni la réalisation complète des autres services spécialisés offerts.",
    steps: [
      {
        step: "1. Rencontre initiale",
        involved: "Client\nConseiller en cybersécurité",
        flow: "Comprendre le besoin du client et répondre aux premières questions."
      },
      {
        step: "2. Réponse à la demande de consultation",
        involved: "Conseiller en cybersécurité",
        flow: "Analyse, conseils et recommandations selon les besoins exprimés."
      },
      {
        step: "3. Avis au client",
        involved: "Conseiller en cybersécurité",
        flow: "Un avis par courriel est envoyé au client deux heures avant la fin du nombre d’heures prédéterminées au contrat."
      }
    ],
    deliverables: [
      "Aucun livrable tangible n’est remis : le client reçoit uniquement des réponses à ses questions et une meilleure compréhension de la problématique abordée, selon la demande spécifique."
    ],
    notes: [
      "Ce service est idéal pour des besoins ponctuels ou des questions ciblées. Il est recommandé de compléter la consultation par des services spécialisés pour répondre aux besoins/recommandations soulevées lors de la consultation."
    ]
  },
  {
    code: "sat-usecure",
    name: "SAT",
    description: "Le service SAT/uSecure renforce la posture de cybersécurité en ciblant le facteur humain. Il combine la formation, les campagnes d’hameçonnage simulées, la surveillance des fuites d’identifiants et des tableaux de bord de suivi.\n\nGrâce à une plateforme automatisée, l’organisation peut mesurer les risques humains, déployer uLearn, configurer uPhish, activer uBreach et suivre les résultats dans des rapports clairs.",
    steps: [
      {
        step: "1. Rencontre initiale et analyse des besoins",
        involved: "Membres décideurs\nConseiller en cybersécurité",
        flow: "Identification des objectifs : formations, simulation de phishing, surveillance uBreach. Structure des équipes, intégration SSO, politiques internes."
      },
      {
        step: "2. Configuration de la plateforme uSecure",
        involved: "Conseiller en cybersécurité",
        flow: "Configuration initiale : groupes, rôles, paramètres de notifications, intégration des employés."
      },
      {
        step: "3. Déploiement de uLearn",
        involved: "Conseiller en cybersécurité\nMembres décideurs",
        flow: "Sélection des modules, assignation automatique, paramétrage des rappels, calendrier annuel de formation."
      },
      {
        step: "4. Déploiement de uPhish",
        involved: "Conseiller en cybersécurité",
        flow: "Création des campagnes : scénarios, fréquence, niveaux de difficulté, groupes cibles."
      },
      {
        step: "5. Activation de uBreach et gestion des alertes",
        involved: "Conseiller en cybersécurité",
        flow: "Mise en place de la surveillance du domaine et explication des actions à prendre lors d’une fuite d'identifiants."
      },
      {
        step: "6. Formation des administrateurs internes",
        involved: "Conseiller en cybersécurité",
        flow: "Présentation du portail, gestion autonome des campagnes, consultation des rapports, bonnes pratiques."
      },
      {
        step: "7. Remise des rapports et optimisation",
        involved: "Conseiller en cybersécurité",
        flow: "Production d’un rapport biannnuel ou annuel incluant statistiques : complétion, vulnérabilités humaines, fuites (uBreach)."
      }
    ],
    deliverables: [
      "Vous recevrez une plateforme entièrement configurée, incluant les modules uLearn, uPhish et uBreach. Le déploiement comprend la création des groupes d’utilisateurs, la mise en place des formations, des campagnes d'hameçonnage ainsi que l’activation de la surveillance des fuites de données."
    ],
    notes: [
      "Proposer aux clients des campagnes d’hameçonnage (uPhish) automatisées au minimum une fois par mois, étant donné la facilité de configuration et la possibilité de mettre le tout en place à une grande fréquence, les résultats se constatent rapidement.",
      "Recommander au moins 2x/année des campagnes personnalisées selon le secteur (ex. finance, légal, manufacturier), adaptées à la réalité du client, afin d’augmenter la pertinence des simulations et d’évaluer l'efficacité des mesures de sensibilisation en place.",
      "Suggérer d’activer uBreach pour tous les clients afin de détecter rapidement toute fuite d’identifiants liée à leur domaine.",
      "Pour les clients qui ont plus d'employés que d'adresses courriel, nous pouvons créer des alias (ex: 10 personnes travaillent avec le courriel réception)."
    ]
  }
];
export default function SolutionsAffairesOffresCybersecurite() {
  const navigate = useNavigate();
  const [prospects, setProspects] = useState<CyberProspect[]>([]);
  const [questions, setQuestions] = useState<CyberQuestion[]>([]);
  const [services, setServices] = useState<CyberService[]>([]);
  const [proposals, setProposals] = useState<CyberProposal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [selectedProspectId, setSelectedProspectId] = useState<string | null>(null);
  const [prospectDetail, setProspectDetail] = useState<CyberProspectDetail | null>(null);
  const [selectedServiceCodes, setSelectedServiceCodes] = useState<string[]>([]);
  const [answerValues, setAnswerValues] = useState<Record<string, string | number | boolean | Array<string | number | boolean>>>({});
  const [generatedProposal, setGeneratedProposal] = useState<CyberGeneratedProposal | null>(null);
  const [discountForm, setDiscountForm] = useState({ percent: '', amount: '', reason: '' });
  const [tacticalDeviationForm, setTacticalDeviationForm] = useState({ percent: '', reason: '' });
  const [proposalMode, setProposalMode] = useState<'services' | 'budget'>('services');
  const [targetBudget, setTargetBudget] = useState('');
  const [budgetPeriod, setBudgetPeriod] = useState<'monthly' | 'annual'>('annual');
  const [contractTermMonths, setContractTermMonths] = useState('12');
  const [proposalScenario, setProposalScenario] = useState<'essential' | 'recommended' | 'complete'>('recommended');
  const [proposalCustomName, setProposalCustomName] = useState('');
  const [applyingDiscount, setApplyingDiscount] = useState(false);
  const [updatingLineItemId, setUpdatingLineItemId] = useState<string | null>(null);
  const [isClientVisualOpen, setIsClientVisualOpen] = useState(false);
  const [proposalActionLoading, setProposalActionLoading] = useState<string | null>(null);
  const [archiveProposalTarget, setArchiveProposalTarget] = useState<CyberProposal | null>(null);
  const [isOfferEmailConfirmOpen, setIsOfferEmailConfirmOpen] = useState(false);
  const [archiveJustification, setArchiveJustification] = useState('');
  const [archivingProposal, setArchivingProposal] = useState(false);
  const [advisorStep, setAdvisorStep] = useState(0);
  const [baseQuestionsExpanded, setBaseQuestionsExpanded] = useState(false);
  const [archivesExpanded, setArchivesExpanded] = useState(false);
  const [isProfileEditOpen, setIsProfileEditOpen] = useState(false);
  const [deletingProspect, setDeletingProspect] = useState(false);
  const [savingProspectProfile, setSavingProspectProfile] = useState(false);
  const [profileEditForm, setProfileEditForm] = useState({ organizationName: '', contactName: '', contactEmail: '', contactPhone: '', notes: '' });
  const [prospectWorkspaceMode, setProspectWorkspaceMode] = useState<'dossier' | 'proposal'>('dossier');
  const [adminSnapshot, setAdminSnapshot] = useState<CyberAdminSnapshot | null>(null);
  const [isAdminOpen, setIsAdminOpen] = useState(false);
  const [isCatalogOpen, setIsCatalogOpen] = useState(false);
  const [activeCatalogIndex, setActiveCatalogIndex] = useState(0);
  const [isCompareOpen, setIsCompareOpen] = useState(false);
  const [prospectSearch, setProspectSearch] = useState('');
  const [prospectStatusFilter, setProspectStatusFilter] = useState<'active' | 'all' | 'accepted' | 'lost' | 'archived'>('active');
  const [adminTab, setAdminTab] = useState<'services' | 'descriptions' | 'deliverables' | 'parameters' | 'calculation' | 'recommendation' | 'discounts' | 'versions'>('parameters');
  const [adminSaving, setAdminSaving] = useState(false);
  const [editingComplexityFactor, setEditingComplexityFactor] = useState<any | null>(null);
  const [complexityFactorForm, setComplexityFactorForm] = useState({ low: '', high: '', weight: '' });
  const [auditRerDrafts, setAuditRerDrafts] = useState<Record<string, string>>({});
  const [adminForm, setAdminForm] = useState({
    serviceName: '',
    serviceCode: '',
    serviceType: 'one_time',
    serviceStatus: 'draft',
    selectedServiceId: '',
    descriptionAudience: 'client',
    descriptionText: '',
    deliverableTitle: '',
    parameterCode: '',
    parameterLabel: '',
    parameterValue: '',
    calculationModel: 'hourly_complexity',
    calculationFormula: '{"model":"hourly_complexity"}',
    recommendationCondition: '{"question":"","operator":"","value":""}',
    recommendationExplanation: '',
    discountName: '',
    discountType: 'manual',
    discountPercent: '',
  });
  const [detailLoading, setDetailLoading] = useState(false);
  const [savingAnswers, setSavingAnswers] = useState(false);
  const [generatingProposal, setGeneratingProposal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ organizationName: '', contactName: '', contactEmail: '', contactPhone: '', notes: '' });

  async function loadData() {
    setLoading(true);
    setError(null);
    try {
      const [prospectsData, questionsData, servicesData, proposalsData, adminData] = await Promise.all([
        getCyberProspects(),
        getCyberQuestions(),
        getCyberServices(),
        getCyberProposals(),
        getCyberAdminSnapshot(),
      ]);
      setProspects(prospectsData);
      setQuestions(questionsData);
      setServices(servicesData);
      setProposals(proposalsData);
      setAdminSnapshot(adminData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur inconnue lors du chargement des offres de cybersécurité.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadData();
  }, []);

  const stats = useMemo(() => {
    return {
      activeProspects: prospects.filter((p) => !['won', 'lost', 'archived'].includes(p.status)).length,
      proposalDrafts: proposals.filter((p) => p.status === 'draft' || p.status === 'review').length,
      questionsAlways: questions.filter((q) => q.questionScope === 'always').length,
      questionsConditional: questions.filter((q) => q.questionScope === 'service_specific').length,
      services: services.length,
    };
  }, [prospects, proposals, questions, services]);

  const filteredProspects = useMemo(() => {
    const term = prospectSearch.trim().toLowerCase();
    return prospects
      .filter((prospect) => {
        const matchesSearch = !term || [prospect.organizationName, prospect.contactName, prospect.contactEmail]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(term));
        const relatedProposals = proposals.filter((proposal) => proposal.prospectId === prospect.id);
        const latestProposal = [...relatedProposals].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())[0];
        const statuses = [prospect.status, latestProposal?.status].filter(Boolean).map((status) => String(status).toLowerCase());
        const isAccepted = statuses.some((status) => ['accepted', 'converted', 'won'].includes(status));
        const isLost = statuses.some((status) => ['lost', 'refused'].includes(status));
        const isArchived = statuses.includes('archived');
        const isActive = !isAccepted && !isLost && !isArchived;
        const matchesStatus = (prospectStatusFilter === 'all' && !isArchived)
          || (prospectStatusFilter === 'active' && isActive)
          || (prospectStatusFilter === 'accepted' && isAccepted)
          || (prospectStatusFilter === 'lost' && isLost)
          || (prospectStatusFilter === 'archived' && isArchived);
        return matchesSearch && matchesStatus;
      })
      .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
  }, [prospects, proposals, prospectSearch, prospectStatusFilter]);

  const activeProposalCount = useMemo(() => proposals.filter((proposal) => ['draft', 'review', 'proposal', 'ready', 'presented'].includes(proposal.status)).length, [proposals]);
  const readyProposalCount = useMemo(() => proposals.filter((proposal) => proposal.status === 'ready').length, [proposals]);
  const recentProposalCount = useMemo(() => proposals.filter((proposal) => ['draft', 'review', 'proposal'].includes(proposal.status)).length, [proposals]);
  const latestUpdatedProspect = useMemo(() => [...prospects].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())[0] || null, [prospects]);
  const recentProposals = useMemo(() => [...proposals].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()).slice(0, 4), [proposals]);
  const latestProspectProposal = useMemo(() => prospectDetail?.proposals?.[0] || null, [prospectDetail]);
  const serviceNameByCode = useMemo(() => new Map(services.map((service) => [service.code, service.name])), [services]);

  const activePipelineValue = useMemo(() => proposals.filter((proposal) => ['draft', 'review', 'proposal', 'ready', 'presented'].includes(proposal.status)).reduce((total, proposal) => total + Number(proposal.finalOneTimeTotal || 0), 0), [proposals]);
  const acceptedProposalCount = useMemo(() => proposals.filter((proposal) => ['accepted', 'converted', 'won'].includes(proposal.status)).length, [proposals]);
  const acceptedProposalTotal = useMemo(() => proposals.filter((proposal) => ['accepted', 'converted', 'won'].includes(proposal.status)).reduce((total, proposal) => total + Number(proposal.finalOneTimeTotal || 0), 0), [proposals]);
  const cyberOpportunities = useMemo(() => {
    const proposalItems = recentProposals.slice(0, 3).map((proposal, index) => ({
      id: proposal.id,
      prospectId: proposal.prospectId,
      tone: index === 0 ? 'red' : index === 1 ? 'amber' : 'purple',
      tag: index === 0 ? 'Priorité' : index === 1 ? 'À saisir' : 'Upsell',
      account: proposal.organizationName || 'Prospect',
      title: proposalDisplayTitle(proposal),
      description: proposal.calculationTrace?.proposalMode === 'budget' ? 'Valider les services proposés selon le budget et préparer le visuel client.' : 'Finaliser les services, rabais et prochaines étapes avant présentation.',
      amount: proposal.finalOneTimeTotal,
    }));
    if (proposalItems.length) return proposalItems;
    return filteredProspects.slice(0, 3).map((prospect, index) => ({
      id: prospect.id,
      prospectId: prospect.id,
      tone: index === 0 ? 'red' : index === 1 ? 'amber' : 'purple',
      tag: index === 0 ? 'Priorité' : index === 1 ? 'À qualifier' : 'Potentiel',
      account: prospect.organizationName,
      title: 'Dossier à qualifier',
      description: 'Compléter le profil prospect pour qu’Edith recommande les bons services cyber.',
      amount: 0,
    }));
  }, [recentProposals, filteredProspects]);
  const priorityProspects = useMemo(() => filteredProspects.slice(0, 4), [filteredProspects]);
  const pipelineStages = useMemo(() => ([
    { label: 'Prospects', count: stats.activeProspects, tone: 'blue' },
    { label: 'Propositions', count: activeProposalCount, tone: 'blue' },
    { label: 'Prêtes', count: readyProposalCount, tone: 'blue' },
    { label: 'Acceptées', count: acceptedProposalCount, tone: 'green' },
  ]), [stats.activeProspects, activeProposalCount, readyProposalCount, acceptedProposalCount]);

  const kanbanStages = useMemo(() => {
    const allStageDefinitions = [
      { code: 'new', title: 'Nouveau prospect', hint: 'Dossier créé, premier contact à qualifier', accent: 'border-blue-200', panel: 'bg-blue-50/80', badge: 'bg-blue-700 text-white', action: 'bg-blue-50 text-blue-800 ring-blue-100', card: 'hover:border-blue-300' },
      { code: 'qualification', title: 'Qualification', hint: 'Besoins et contexte à préciser', accent: 'border-amber-300', panel: 'bg-amber-50/85', badge: 'bg-amber-600 text-white', action: 'bg-amber-50 text-amber-900 ring-amber-100', card: 'hover:border-amber-300' },
      { code: 'preparation', title: 'Offre en préparation', hint: 'Services, prix et visuel à finaliser', accent: 'border-blue-300', panel: 'bg-blue-50/80', badge: 'bg-blue-700 text-white', action: 'bg-blue-50 text-blue-800 ring-blue-100', card: 'hover:border-blue-300' },
      { code: 'sent', title: 'Offre envoyée', hint: 'Proposition prête ou transmise au client', accent: 'border-blue-300', panel: 'bg-blue-50/80', badge: 'bg-blue-700 text-white', action: 'bg-blue-50 text-blue-800 ring-blue-100', card: 'hover:border-blue-300' },
      { code: 'followup', title: 'Négociation / suivi', hint: 'Relance, ajustements ou décision à obtenir', accent: 'border-purple-300', panel: 'bg-purple-50/85', badge: 'bg-purple-700 text-white', action: 'bg-purple-50 text-purple-900 ring-purple-100', card: 'hover:border-purple-300' },
      { code: 'accepted', title: 'Acceptés', hint: 'Offres acceptées par le client', accent: 'border-emerald-300', panel: 'bg-emerald-50/85', badge: 'bg-emerald-700 text-white', action: 'bg-emerald-50 text-emerald-900 ring-emerald-100', card: 'hover:border-emerald-300' },
      { code: 'refused', title: 'Refusés', hint: 'Offres refusées par le client', accent: 'border-rose-300', panel: 'bg-rose-50/85', badge: 'bg-rose-700 text-white', action: 'bg-rose-50 text-rose-900 ring-rose-100', card: 'hover:border-rose-300' },
      { code: 'archived', title: 'Archivés', hint: 'Offres retirées du pipeline actif', accent: 'border-slate-300', panel: 'bg-slate-50/85', badge: 'bg-slate-700 text-white', action: 'bg-slate-100 text-slate-800 ring-slate-200', card: 'hover:border-slate-300' },
    ];
    const visibleStageCodesByFilter: Record<typeof prospectStatusFilter, string[]> = {
      all: ['new', 'qualification', 'preparation', 'sent', 'followup', 'accepted', 'refused'],
      active: ['new', 'qualification', 'preparation', 'sent', 'followup'],
      accepted: ['accepted'],
      lost: ['refused'],
      archived: ['archived'],
    };
    const stageDefinitions = allStageDefinitions.filter((stage) => visibleStageCodesByFilter[prospectStatusFilter].includes(stage.code));
    type PipelineOfferCard = { prospect: CyberProspect; proposal?: CyberProposal; total: number; score: number; recommendation: string; chips: string[] };
    const grouped = new Map(allStageDefinitions.map((stage) => [stage.code, { ...stage, items: [] as PipelineOfferCard[] }]));
    const prospectsById = new Map(prospects.map((prospect) => [prospect.id, prospect]));
    const term = prospectSearch.trim().toLowerCase();
    const prospectMatchesSearch = (prospect?: CyberProspect | null, proposal?: CyberProposal) => !term || [prospect?.organizationName, prospect?.contactName, prospect?.contactEmail, proposal?.organizationName, proposal ? proposalDisplayTitle(proposal) : '']
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(term));
    const matchesPipelineFilter = (status: string) => {
      const normalized = String(status || '').toLowerCase();
      const isAccepted = ['accepted', 'converted', 'won'].includes(normalized);
      const isLost = ['lost', 'refused'].includes(normalized);
      const isArchived = normalized === 'archived';
      const isActive = !isAccepted && !isLost && !isArchived;
      return (prospectStatusFilter === 'all' && !isArchived)
        || (prospectStatusFilter === 'active' && isActive)
        || (prospectStatusFilter === 'accepted' && isAccepted)
        || (prospectStatusFilter === 'lost' && isLost)
        || (prospectStatusFilter === 'archived' && isArchived);
    };
    const proposalStage = (status: string) => {
      const normalized = String(status || '').toLowerCase();
      if (['accepted', 'converted', 'won'].includes(normalized)) return 'accepted';
      if (['refused', 'lost'].includes(normalized)) return 'refused';
      if (normalized === 'archived') return 'archived';
      if (['review', 'negotiation'].includes(normalized)) return 'followup';
      if (['ready', 'presented', 'sent'].includes(normalized)) return 'sent';
      return 'preparation';
    };

    proposals.forEach((proposal, index) => {
      const prospect = prospectsById.get(proposal.prospectId) || { id: proposal.prospectId, organizationName: proposal.organizationName || 'Prospect', status: 'draft', createdAt: proposal.createdAt, updatedAt: proposal.updatedAt } as CyberProspect;
      if (!prospectMatchesSearch(prospect, proposal) || !matchesPipelineFilter(proposal.status)) return;
      const stageCode = proposalStage(proposal.status);
      const total = Number(proposal.finalOneTimeTotal || 0);
      const serviceSummary = proposalServiceSummary(proposal);
      const score = Math.min(96, 34 + (total > 0 ? 18 : 0) + (proposalServiceCodes(proposal).length * 8) + (stageCode === 'accepted' ? 24 : 0) + (index % 3) * 6);
      const chips = [statusLabel(proposal.status), proposalModeLabel(proposal), pluralize(prospect.proposalCount || 1, 'offre au dossier', 'offres au dossier')];
      grouped.get(stageCode)?.items.push({ prospect, proposal, total, score, recommendation: serviceSummary, chips });
    });

    prospects.forEach((prospect, index) => {
      const hasProposal = proposals.some((proposal) => proposal.prospectId === prospect.id);
      if (hasProposal || !prospectMatchesSearch(prospect) || !matchesPipelineFilter(prospect.status)) return;
      const normalizedStatus = String(prospect.status || '').toLowerCase();
      const stageCode = ['qualification', 'evaluation'].includes(normalizedStatus) ? 'qualification' : 'new';
      const chips = [statusLabel(prospect.status), 'Aucune offre créée', prospect.contactEmail || 'Courriel à préciser'];
      grouped.get(stageCode)?.items.push({ prospect, total: 0, score: Math.min(78, 28 + (index % 4) * 8), recommendation: stageCode === 'qualification' ? 'Qualifier les besoins cyber' : 'Créer une première offre', chips });
    });

    return stageDefinitions.map((definition) => {
      const stage = grouped.get(definition.code)!;
      return { ...stage, total: stage.items.reduce((sum, item) => sum + item.total, 0) };
    });
  }, [prospects, proposals, prospectSearch, prospectStatusFilter, serviceNameByCode]);

  const hasVisibleKanbanItems = useMemo(() => kanbanStages.some((stage) => stage.items.length > 0), [kanbanStages]);

  function proposalServiceCodes(proposal: CyberProposal) {
    const trace = proposal.calculationTrace || {};
    const recommended = Array.isArray(trace.recommendedServiceCodes) ? trace.recommendedServiceCodes : [];
    const selected = Array.isArray(trace.selectedServiceCodes) ? trace.selectedServiceCodes : [];
    return [...new Set([...recommended, ...selected].filter(Boolean).map(String))];
  }

  function proposalModeLabel(proposal: CyberProposal) {
    return proposal.calculationTrace?.proposalMode === 'budget' ? 'Budget client' : 'Services ciblés';
  }

  function proposalServiceSummary(proposal: CyberProposal) {
    const names = proposalServiceCodes(proposal).map((code) => serviceNameByCode.get(code) || code).filter(Boolean);
    if (!names.length) return proposal.status === 'draft' ? 'À compléter' : 'Services à confirmer';
    return names.length <= 2 ? names.join(' + ') : `${names.slice(0, 2).join(' + ')} + ${pluralize(names.length - 2, 'autre')}`;
  }

  function proposalScenarioLabel(value?: unknown) {
    if (value === 'essential') return 'Essentielle';
    if (value === 'complete') return 'Complète';
    return 'Recommandée';
  }

  function proposalDisplayTitle(proposal: CyberProposal) {
    const trace = proposal.calculationTrace || {};
    if (trace.proposalCustomName && typeof trace.proposalCustomName === 'string') return trace.proposalCustomName;
    const scenario = trace.proposalScenario ? `Option ${proposalScenarioLabel(trace.proposalScenario)}` : null;
    const summary = proposalServiceSummary(proposal);
    if (scenario && summary !== 'À compléter' && summary !== 'Services à confirmer') return `${scenario} · ${summary}`;
    return summary === 'À compléter' || summary === 'Services à confirmer'
      ? `${scenario || proposalModeLabel(proposal)} · ${summary}`
      : summary;
  }

  function proposalSubtitle(proposal: CyberProposal) {
    const trace = proposal.calculationTrace || {};
    const budget = trace.targetBudgetInput ? `${Number(trace.targetBudgetInput).toFixed(0)} $ ${trace.budgetPeriod === 'monthly' ? '/ mois' : '/ an'}` : null;
    const term = trace.contractTermMonths ? `${trace.contractTermMonths} mois` : null;
    const scenario = trace.proposalScenario ? `Option ${proposalScenarioLabel(trace.proposalScenario)}` : null;
    return [scenario, proposalModeLabel(proposal), statusLabel(proposal.status), budget, term].filter(Boolean).join(' · ');
  }

  function budgetStatusLabel(proposal?: CyberProposal | null) {
    if (!proposal?.calculationTrace?.targetBudget) return 'Budget non défini';
    const budget = Number(proposal.calculationTrace.targetBudget || 0);
    const total = Number(proposal.finalOneTimeTotal || 0);
    if (!budget) return 'Budget non défini';
    if (total <= budget) return `Dans le budget · marge ${(budget - total).toFixed(0)} $`;
    return `Dépassement · ${(total - budget).toFixed(0)} $`;
  }

  const generatedProposalDisplayName = useMemo(() => generatedProposal ? proposalDisplayTitle(generatedProposal.proposal) : '', [generatedProposal, serviceNameByCode]);

  function servicePhaseForCode(code?: string) {
    if (['audit-rer', 'questionnaire-nist', 'posture-365', 'balayage-vulnerabilites', 'rer-tiers'].includes(String(code))) return { phaseName: 'Comprendre les risques', order: 1 };
    if (['pici', 'plan-reprise', 'politique-cyber-uati', 'politique-cyber-gsti', 'politique-ia', 'loi-25'].includes(String(code))) return { phaseName: 'Réduire les risques prioritaires', order: 2 };
    return { phaseName: 'Maintenir et accompagner', order: 3 };
  }

  function buildProposalActionPlan(lines: CyberProposalLineItem[]) {
    const selected = lines.filter((line) => line.selected);
    const groups = new Map<string, { phaseName: string; order: number; services: CyberProposalLineItem[] }>();
    for (const line of selected) {
      const phase = servicePhaseForCode(line.serviceCode);
      const current = groups.get(phase.phaseName) || { ...phase, services: [] };
      current.services.push(line);
      groups.set(phase.phaseName, current);
    }
    return [...groups.values()].sort((a, b) => a.order - b.order);
  }

  function actionPlanTitle(phase: { phaseName: string }, index: number, total: number) {
    if (total <= 1) {
      const term = generatedProposal?.proposal.calculationTrace?.contractTermMonths || contractTermMonths || 12;
      return `Mandat ${term} mois — Services inclus`;
    }
    return `Phase ${index + 1} — ${phase.phaseName}`;
  }

  function actionPlanSubtitle(phase: { phaseName: string }, total: number) {
    return total <= 1 ? phase.phaseName : 'Groupe de services dans la feuille de route';
  }

  function reportedServices(detail?: CyberGeneratedProposal | null) {
    if (!detail) return [] as CyberProposalLineItem[];
    return detail.lineItems.filter((line) => !line.selected);
  }

  function executiveSummary(detail?: CyberGeneratedProposal | null) {
    if (!detail) return '';
    const serviceCount = detail.lineItems.filter((line) => line.selected).length;
    const budgetText = detail.proposal.calculationTrace?.targetBudgetInput
      ? `L’offre tient compte d’un budget ${detail.proposal.calculationTrace?.budgetPeriod === 'monthly' ? 'mensuel' : 'annuel'} de ${Number(detail.proposal.calculationTrace.targetBudgetInput).toFixed(0)} $.`
      : 'L’offre est construite à partir des services retenus par le conseiller.';
    const planText = buildProposalActionPlan(detail.lineItems).length > 1
      ? 'Le plan d’action est présenté en phases pour faciliter la décision et la transition vers la livraison.'
      : 'Les services sont présentés comme un seul mandat cohérent plutôt que comme une phase isolée.';
    return `Cette proposition regroupe ${pluralize(serviceCount, 'service cybersécurité', 'services cybersécurité')} afin de clarifier les risques, prioriser les actions et soutenir la mise en œuvre avec BZ. ${budgetText} ${planText}`;
  }

  const activeActionPlan = useMemo(() => buildProposalActionPlan(generatedProposal?.lineItems || []), [generatedProposal]);
  const activeReportedServices = useMemo(() => reportedServices(generatedProposal), [generatedProposal]);
  const activeExecutiveSummary = useMemo(() => executiveSummary(generatedProposal), [generatedProposal]);
  const clientEmailSubject = useMemo(() => generatedProposal ? `Proposition cybersécurité BZ — ${generatedProposal.proposal.organizationName}` : '', [generatedProposal]);
  const selectedProposalLines = useMemo(() => (generatedProposal?.lineItems || []).filter((line) => line.selected), [generatedProposal]);
  const clientEmailBody = useMemo(() => {
    if (!generatedProposal) return '';
    const contactName = prospectDetail?.prospect.contactName?.trim();
    const greeting = contactName ? `Bonjour ${contactName},` : 'Bonjour,';
    const serviceLines = selectedProposalLines.length
      ? selectedProposalLines.map((line) => `- ${line.serviceName}${line.clientDescription ? `: ${line.clientDescription}` : ''}`).join('\n')
      : '- Services cybersécurité BZ détaillés dans la proposition jointe.';
    return [
      greeting,
      '',
      `À la suite de nos échanges, vous trouverez ci-joint notre proposition cybersécurité pour ${generatedProposal.proposal.organizationName}.`,
      '',
      'L’offre inclut notamment :',
      serviceLines,
      '',
      'Les documents joints présentent le détail de l’offre, les inclusions, les livrables et les éléments budgétaires. Une version sans prix est aussi incluse si vous souhaitez la partager à l’interne avant la décision finale.',
      '',
      'J’aimerais prévoir une rencontre ou un court appel avec vous afin de présenter la proposition, répondre aux questions et valider les prochaines étapes.',
      '',
      'Est-ce que vous auriez une disponibilité cette semaine ou la semaine prochaine?',
      '',
      'Merci,',
      '',
    ].join('\n');
  }, [generatedProposal, prospectDetail, selectedProposalLines]);
  const fullBriefingOutput = useMemo(() => generatedProposal?.outputs?.find((output) => output.title?.toLowerCase().includes('complète')) || null, [generatedProposal]);
  const noPriceBriefingOutput = useMemo(() => generatedProposal?.outputs?.find((output) => output.title?.toLowerCase().includes('sans prix')) || null, [generatedProposal]);

  function attachmentHref(html?: string | null) {
    if (!html) return '#';
    return `data:text/html;charset=utf-8,${encodeURIComponent(html)}`;
  }

  function safeAttachmentName(label: string) {
    const safeName = String(generatedProposal?.proposal.organizationName || 'client').replace(/[^a-z0-9_-]+/gi, '_').replace(/^_+|_+$/g, '') || 'client';
    return `Briefing_Offre_${safeName}_${label}.html`;
  }

  async function copyClientEmail() {
    if (!clientEmailBody) return;
    const text = [`Sujet: ${clientEmailSubject}`, '', clientEmailBody].join('\n');
    try {
      await navigator.clipboard.writeText(text);
      setSuccessMessage('Courriel client copié dans le presse-papiers. Télécharge les pièces jointes et colle le message dans Gmail ou Outlook.');
    } catch {
      setError('Impossible de copier automatiquement. Sélectionne le texte du courriel et copie-le manuellement.');
    }
  }

  const insightCards = useMemo(() => {
    const recommendedServiceCounts = new Map<string, number>();
    const servicePipelineTotals = new Map<string, number>();
    const activeStatuses = ['draft', 'review', 'proposal', 'ready', 'presented'];
    const activeProposals = proposals.filter((proposal) => activeStatuses.includes(proposal.status));

    for (const proposal of proposals) {
      const serviceCodes = proposalServiceCodes(proposal);
      for (const code of serviceCodes) {
        recommendedServiceCounts.set(code, (recommendedServiceCounts.get(code) || 0) + 1);
      }
    }

    for (const proposal of activeProposals) {
      const serviceCodes = proposalServiceCodes(proposal);
      const amountShare = serviceCodes.length ? Number(proposal.finalOneTimeTotal || 0) / serviceCodes.length : 0;
      for (const code of serviceCodes) {
        servicePipelineTotals.set(code, (servicePipelineTotals.get(code) || 0) + amountShare);
      }
    }

    const topRecommended = [...recommendedServiceCounts.entries()].sort((a, b) => b[1] - a[1])[0];
    const topPipeline = [...servicePipelineTotals.entries()].sort((a, b) => b[1] - a[1])[0];
    const readyOrPresented = proposals.filter((proposal) => ['ready', 'presented'].includes(proposal.status)).length;
    const followups = proposals.filter((proposal) => ['review', 'presented'].includes(proposal.status)).length;
    const acceptedTotal = acceptedProposalTotal;
    const maxCount = Math.max(1, topRecommended?.[1] || 0, proposals.length);
    const maxAmount = Math.max(1, topPipeline?.[1] || 0, activePipelineValue);

    const cards = [] as Array<{ badge: string; title: string; subtitle: string; width: number; sourceLabel: string }>;
    if (topRecommended) {
      cards.push({
        badge: 'REC',
        title: serviceNameByCode.get(topRecommended[0]) || topRecommended[0],
        subtitle: `Service le plus recommandé: ${pluralize(topRecommended[1], 'proposition')}.`,
        width: Math.max(18, Math.round((topRecommended[1] / maxCount) * 100)),
        sourceLabel: 'Source: propositions générées',
      });
    }
    if (topPipeline) {
      cards.push({
        badge: '$',
        title: serviceNameByCode.get(topPipeline[0]) || topPipeline[0],
        subtitle: `Plus fort potentiel actif: ${topPipeline[1].toLocaleString('fr-CA', { maximumFractionDigits: 0 })} $.`,
        width: Math.max(18, Math.round((topPipeline[1] / maxAmount) * 100)),
        sourceLabel: 'Source: montants en pipeline',
      });
    }
    cards.push({
      badge: readyOrPresented ? 'GO' : 'CRM',
      title: readyOrPresented ? 'À présenter maintenant' : 'Pipeline à nourrir',
      subtitle: readyOrPresented ? pluralize(readyOrPresented, 'proposition prête ou présentée', 'propositions prêtes ou présentées') + '.' : `${pluralize(stats.activeProspects, 'prospect actif', 'prospects actifs')}, ${pluralize(activeProposals.length, 'proposition en cours', 'propositions en cours')}.`,
      width: Math.max(18, Math.min(100, readyOrPresented ? readyOrPresented * 33 : stats.activeProspects * 10)),
      sourceLabel: 'Source: statuts prospects/propositions',
    });
    if (followups > 0 && cards.length < 4) {
      cards.push({
        badge: 'SUI',
        title: 'Relances à prioriser',
        subtitle: pluralize(followups, 'proposition en suivi ou en négociation', 'propositions en suivi ou en négociation') + '.',
        width: Math.max(18, Math.min(100, followups * 30)),
        sourceLabel: 'Source: statuts des propositions',
      });
    }
    if (acceptedTotal > 0 && cards.length < 4) {
      cards.push({
        badge: 'OK',
        title: 'Valeur acceptée',
        subtitle: `${acceptedTotal.toLocaleString('fr-CA', { maximumFractionDigits: 0 })} $ en valeur acceptée à convertir en livraison.`,
        width: Math.max(18, Math.min(100, Math.round((acceptedTotal / Math.max(acceptedTotal, activePipelineValue || 1)) * 100))),
        sourceLabel: 'Source: propositions acceptées',
      });
    }
    return cards.slice(0, 4);
  }, [proposals, serviceNameByCode, acceptedProposalTotal, activePipelineValue, stats.activeProspects]);

  const baseQuestions = useMemo(() => questions.filter((question) => question.questionScope === 'always'), [questions]);
  const budgetDiagnosticQuestions = useMemo(() => questions.filter((question) => question.questionScope === 'budget_diagnostic'), [questions]);
  const proposalQuestions = useMemo(() => questions.filter((question) => question.questionScope === 'service_specific' && question.serviceCodes.some((code) => selectedServiceCodes.includes(code))), [questions, selectedServiceCodes]);
  const isAnswerMissing = (value: string | number | boolean | Array<string | number | boolean> | undefined | null) => value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0);
  const backupRestoreMandateQuestion = useMemo(() => questions.find((question) => question.code === 'backup_restore_mandate_type'), [questions]);
  const backupRestoreMandateValue = backupRestoreMandateQuestion ? answerValues[backupRestoreMandateQuestion.id] : undefined;
  function isQuestionApplicable(question: CyberQuestion) {
    if (question.code === 'backup_restore_frequency') return backupRestoreMandateValue === 'recurring';
    return true;
  }
  function isQuestionRequiredForProposal(question: CyberQuestion) {
    if (!isQuestionApplicable(question)) return false;
    if (question.code === 'backup_restore_frequency') return backupRestoreMandateValue === 'recurring';
    return question.required || question.questionScope === 'service_specific' || question.questionScope === 'budget_diagnostic';
  }
  const activeProposalQuestions = useMemo(() => (proposalMode === 'budget' ? budgetDiagnosticQuestions : proposalQuestions).filter(isQuestionApplicable), [proposalMode, budgetDiagnosticQuestions, proposalQuestions, backupRestoreMandateValue]);
  const visibleQuestions = useMemo(() => (prospectWorkspaceMode === 'proposal' ? activeProposalQuestions : baseQuestions), [prospectWorkspaceMode, activeProposalQuestions, baseQuestions]);

  const missingBaseQuestions = useMemo(() => baseQuestions.filter((question) => question.required && isAnswerMissing(answerValues[question.id])), [baseQuestions, answerValues]);
  const missingProposalQuestions = useMemo(() => activeProposalQuestions.filter((question) => isQuestionRequiredForProposal(question) && isAnswerMissing(answerValues[question.id])), [activeProposalQuestions, answerValues, backupRestoreMandateValue]);
  const conditionalQuestionCount = useMemo(() => activeProposalQuestions.length, [activeProposalQuestions]);
  const shouldShowQualificationStep = proposalMode === 'budget' || activeProposalQuestions.length > 0;
  const unansweredVisibleQuestionCount = useMemo(() => visibleQuestions.filter((question) => isAnswerMissing(answerValues[question.id])).length, [visibleQuestions, answerValues]);
  const questionSections = useMemo(() => {
    const sections = new Map<string, { name: string; questions: CyberQuestion[]; missing: number; answered: number }>();
    for (const question of visibleQuestions) {
      const sectionName = question.sectionName || 'Questions';
      const section = sections.get(sectionName) || { name: sectionName, questions: [], missing: 0, answered: 0 };
      section.questions.push(question);
      if (isAnswerMissing(answerValues[question.id])) section.missing += 1;
      else section.answered += 1;
      sections.set(sectionName, section);
    }
    return Array.from(sections.values());
  }, [visibleQuestions, answerValues]);
  const firstMissingQuestion = useMemo(() => visibleQuestions.find((question) => isQuestionRequiredForProposal(question) && isAnswerMissing(answerValues[question.id])), [visibleQuestions, answerValues, backupRestoreMandateValue]);
  const missingRequiredQuestions = useMemo(() => [...missingBaseQuestions, ...missingProposalQuestions], [missingBaseQuestions, missingProposalQuestions]);
  const baseQuestionsAnsweredCount = useMemo(() => baseQuestions.filter((question) => !isAnswerMissing(answerValues[question.id])).length, [baseQuestions, answerValues]);
  const baseQuestionsCompletionPercent = baseQuestions.length ? Math.round((baseQuestionsAnsweredCount / baseQuestions.length) * 100) : 0;
  const proposalReadiness = useMemo(() => {
    const items = [
      { label: 'Profil prospect complété', done: missingBaseQuestions.length === 0 },
      { label: 'Services sélectionnés', done: proposalMode === 'budget' || selectedServiceCodes.length > 0 },
      { label: proposalMode === 'budget' ? 'Mini-diagnostic complété' : 'Questions conditionnelles complétées', done: missingProposalQuestions.length === 0 },
      { label: 'Prix généré', done: Boolean(generatedProposal) },
      { label: 'Rabais justifiés', done: Boolean(generatedProposal) && (generatedProposal?.discounts || []).every((discount) => discount.discountType !== 'tactical_deviation' || Boolean(discount.reason)) },
      { label: 'Mensualité calculée', done: Boolean(generatedProposal?.proposal.finalRecurringMonthlyTotal) },
      { label: 'Visuel client disponible', done: Boolean(generatedProposal?.clientVisualHtml) },
    ];
    return { items, missing: items.filter((item) => !item.done) };
  }, [missingBaseQuestions.length, missingProposalQuestions.length, proposalMode, selectedServiceCodes.length, generatedProposal]);

  const proposalTimeline = useMemo(() => {
    const activityEvents = [
      ...(prospectDetail?.activities || []),
      ...(generatedProposal?.activities || []),
    ];
    const uniqueEvents = new Map<string, CyberProposalActivity>();
    for (const event of activityEvents) uniqueEvents.set(event.id, event);
    const events = [...uniqueEvents.values()].map((event) => ({
      label: event.label,
      detail: `${event.actorInitials || 'ED'} · ${event.detail || 'Action enregistrée'}`,
      date: event.createdAt,
    }));
    if (prospectDetail?.prospect.createdAt) events.push({ label: 'Prospect créé', detail: prospectDetail.prospect.organizationName, date: prospectDetail.prospect.createdAt });
    return events.sort((a, b) => new Date(b.date || '').getTime() - new Date(a.date || '').getTime()).slice(0, 8);
  }, [prospectDetail, generatedProposal]);

  function pluralize(count: number, singular: string, plural?: string) {
    return `${count} ${count > 1 ? (plural || `${singular}s`) : singular}`;
  }

  function isActiveProposalStatus(status?: string) {
    return !['accepted', 'converted', 'refused', 'lost', 'archived'].includes(String(status || '').toLowerCase());
  }

  function formatQuestionAnswer(question: CyberQuestion) {
    const raw = answerValues[question.id];
    if (raw === undefined || raw === null || raw === '') return 'Non répondu';
    if (question.answerType === 'boolean') return raw === true || raw === 'true' ? 'Oui' : 'Non';
    if (question.answerType === 'multi_choice') {
      const selected = Array.isArray(raw) ? raw : String(raw).split(',').map((item) => item.trim()).filter(Boolean);
      return selected.map((value) => question.options?.find((item) => String(item.value) === String(value))?.label || String(value)).join(', ') || 'Non répondu';
    }
    const option = question.options?.find((item) => String(item.value) === String(raw));
    return `${option?.label || raw}${question.unit ? ` ${question.unit}` : ''}`;
  }

  const baseQuestionPreview = useMemo(() => baseQuestions.slice(0, 5), [baseQuestions]);
  const activeDossierProposals = useMemo(() => (prospectDetail?.proposals || []).filter((proposal) => isActiveProposalStatus(proposal.status)), [prospectDetail]);
  const activeDossierProposal = useMemo(() => activeDossierProposals[0] || null, [activeDossierProposals]);
  const secondaryDossierProposals = useMemo(() => activeDossierProposals.filter((proposal) => proposal.id !== activeDossierProposal?.id), [activeDossierProposals, activeDossierProposal]);
  const archivedDossierProposals = useMemo(() => prospectDetail?.archivedProposals || [], [prospectDetail]);
  const canDeleteSelectedProspect = Boolean(prospectDetail) && (prospectDetail?.proposals?.length || 0) === 0 && archivedDossierProposals.length === 0;

  const selectedAdminService = useMemo(() => services.find((service) => service.id === adminForm.selectedServiceId) || services[0], [services, adminForm.selectedServiceId]);


  const advisorChecklist = useMemo(() => [
    { label: 'Prospect sélectionné', done: Boolean(selectedProspectId), help: 'Ouvrir ou créer un prospect.' },
    { label: 'Services choisis', done: proposalMode === 'budget' || selectedServiceCodes.length > 0, help: proposalMode === 'budget' ? 'Edith proposera les services qui entrent dans le budget.' : 'Choisir au moins un service à proposer.' },
    { label: 'Questions requises complétées', done: (proposalMode === 'budget' || selectedServiceCodes.length > 0) && missingRequiredQuestions.length === 0, help: missingBaseQuestions.length ? `${pluralize(missingBaseQuestions.length, 'question de base à compléter', 'questions de base à compléter')} dans le dossier.` : missingProposalQuestions.length ? `${pluralize(missingProposalQuestions.length, 'question conditionnelle à compléter', 'questions conditionnelles à compléter')}.` : 'Qualification complète.' },
    { label: 'Proposition générée', done: Boolean(generatedProposal), help: 'Générer prix + visuel client.' },
    { label: 'Rabais confirmé', done: Boolean(generatedProposal), help: generatedProposal ? 'Rabais facultatif, proposition prête à réviser.' : 'Générer une proposition avant rabais.' },
    { label: 'Visuel client prêt', done: Boolean(generatedProposal?.clientVisualHtml), help: 'Ouvrir l’aperçu plein écran avant présentation.' },
  ], [selectedProspectId, proposalMode, selectedServiceCodes.length, missingRequiredQuestions.length, missingBaseQuestions.length, missingProposalQuestions.length, generatedProposal]);

  const advisorStepStatus = useMemo(() => {
    const hasProposal = Boolean(generatedProposal);
    return [
      { title: 'Prospect', detail: selectedProspectId ? 'Sélectionné' : 'À choisir', state: selectedProspectId ? 'completed' : 'current' },
      { title: 'Qualification', detail: !shouldShowQualificationStep ? 'Aucune question conditionnelle' : missingRequiredQuestions.length ? `${missingRequiredQuestions.length} à compléter` : 'Complète', state: !shouldShowQualificationStep ? 'completed' : !selectedProspectId ? 'pending' : missingRequiredQuestions.length ? 'current' : 'completed' },
      { title: 'Services', detail: selectedServiceCodes.length ? pluralize(selectedServiceCodes.length, 'service') : 'À choisir', state: selectedServiceCodes.length ? 'completed' : 'pending' },
      { title: 'Proposition', detail: hasProposal ? statusLabel(generatedProposal!.proposal.status) : 'À générer', state: hasProposal ? 'completed' : 'pending' },
      { title: 'Visuel', detail: generatedProposal?.clientVisualHtml ? 'Prêt' : 'À produire', state: generatedProposal?.clientVisualHtml ? 'completed' : 'pending' },
      { title: 'Suivi client', detail: generatedProposal ? statusLabel(generatedProposal.proposal.status) : 'À venir', state: ['accepted', 'refused', 'converted'].includes(generatedProposal?.proposal.status || '') ? 'completed' : generatedProposal ? 'current' : 'pending' },
    ];
  }, [selectedProspectId, shouldShowQualificationStep, missingRequiredQuestions.length, selectedServiceCodes.length, generatedProposal]);

  const adminServiceHealth = useMemo(() => {
    return services.map((service) => {
      const descriptions = adminSnapshot?.descriptions?.filter((item: any) => item.service_id === service.id).length || 0;
      const deliverables = adminSnapshot?.deliverables?.filter((item: any) => item.service_id === service.id).length || 0;
      const calculations = adminSnapshot?.calculationRules?.filter((item: any) => item.service_id === service.id).length || 0;
      const recommendations = adminSnapshot?.recommendationRules?.filter((item: any) => item.service_id === service.id).length || 0;
      const missing = [
        descriptions ? null : 'texte client',
        deliverables ? null : 'livrable',
        calculations ? null : 'prix',
        recommendations ? null : 'recommandation',
      ].filter(Boolean) as string[];
      return { service, descriptions, deliverables, calculations, recommendations, missing, complete: missing.length === 0 };
    });
  }, [services, adminSnapshot]);

  const incompleteAdminServices = useMemo(() => adminServiceHealth.filter((item) => !item.complete), [adminServiceHealth]);
  const selectedAdminServiceHealth = useMemo(() => adminServiceHealth.find((item) => item.service.id === selectedAdminService?.id), [adminServiceHealth, selectedAdminService]);
  const complexityFactors = useMemo(() => {
    return (adminSnapshot?.parameters || [])
      .map((parameter: any) => {
        const config = parameter.value_json || parameter.valueJson || {};
        if (config.factorType !== 'complexity') return null;
        return {
          id: parameter.id,
          code: parameter.code,
          label: parameter.label,
          section: config.section,
          questionCode: config.questionCode,
          type: config.type,
          low: config.low,
          high: config.high,
          weight: Number(parameter.value_numeric ?? config.weight ?? 0),
          raw: config,
        };
      })
      .filter(Boolean) as Array<any>;
  }, [adminSnapshot]);
  const groupedComplexityFactors = useMemo(() => ({
    general: complexityFactors.filter((factor) => factor.section === 'general'),
    loi25: complexityFactors.filter((factor) => factor.section === 'loi25'),
  }), [complexityFactors]);
  const auditRerAdminGroups = useMemo(() => ([
    {
      title: 'Modules composant l’audit',
      description: 'Ces lignes reprennent la logique de l’onglet Audit-RER: sous-services, inventaire, hameçonnage, balayage et Dark Web.',
      fields: [
        ['audit_rer_inventory_min_hours', 'Inventaire — heures min.', 'h'],
        ['audit_rer_inventory_max_hours', 'Inventaire — heures max.', 'h'],
        ['audit_rer_phishing_fee_per_employee', 'Hameçonnage — frais par employé', '$/employé'],
        ['audit_rer_phishing_base_hours', 'Hameçonnage — heures fixes', 'h'],
        ['audit_rer_phishing_hours_per_employee', 'Hameçonnage — heures par employé', 'h/employé'],
        ['audit_rer_vulnerability_fixed_fee', 'Balayage — frais fixes', '$'],
        ['audit_rer_vulnerability_hours', 'Balayage — heures', 'h'],
        ['audit_rer_darkweb_fee_per_domain', 'Dark Web — frais par domaine', '$/domaine'],
        ['audit_rer_darkweb_base_hours', 'Dark Web — heures fixes', 'h'],
        ['audit_rer_darkweb_hours_per_domain', 'Dark Web — heures par domaine', 'h/domaine'],
      ],
    },
    {
      title: 'Production du rapport',
      description: 'Couches ajoutées après le sous-total des modules, comme dans les lignes Conclusion, Révision, Présentation et Charge de projet.',
      fields: [
        ['audit_rer_conclusion_percent', 'Conclusion et recommandations', '%'],
        ['audit_rer_review_percent', 'Révision', '%'],
        ['audit_rer_presentation_hours', 'Présentation', 'h'],
        ['audit_rer_project_hours', 'Charge de projet', 'h'],
        ['audit_rer_minimum_amount', 'Minimum facturable', '$'],
      ],
    },
  ]), []);
  function adminParameter(code: string) {
    return (adminSnapshot?.parameters || []).find((parameter: any) => parameter.code === code);
  }
  function adminParameterValue(code: string, fallback = '') {
    if (auditRerDrafts[code] !== undefined) return auditRerDrafts[code];
    const parameter = adminParameter(code);
    const value = parameter?.value_numeric ?? parameter?.valueNumeric;
    return value !== undefined && value !== null ? String(value) : fallback;
  }
  const activeCatalogOffer = cyberOfferCatalog[Math.min(activeCatalogIndex, cyberOfferCatalog.length - 1)] || cyberOfferCatalog[0];

  const advisorWizardSteps = ['Services', 'Qualification', 'Proposition', 'Rabais', 'Visuel client', 'Suivi client'];
  const currentAdvisorStep = Math.min(advisorStep, advisorWizardSteps.length - 1);

  function nextAdvisorStep(fromStep = currentAdvisorStep) {
    if (fromStep === 0 && !shouldShowQualificationStep) return 2;
    return Math.min(advisorWizardSteps.length - 1, fromStep + 1);
  }

  function previousAdvisorStep(fromStep = currentAdvisorStep) {
    if (fromStep === 2 && !shouldShowQualificationStep) return 0;
    return Math.max(0, fromStep - 1);
  }

  function closeProspectWorkspace() {
    setSelectedProspectId(null);
    setProspectDetail(null);
    setGeneratedProposal(null);
    setProspectWorkspaceMode('dossier');
    setAdvisorStep(0);
    setError(null);
  }

  useEffect(() => {
    if (!selectedProspectId || prospectWorkspaceMode !== 'proposal') return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [selectedProspectId, prospectWorkspaceMode]);

  function scrollToQuestion(questionId?: string) {
    if (!questionId) return;
    document.getElementById(`question-${questionId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  useEffect(() => {
    if (!selectedAdminService) return;
    setAdminForm((current) => ({
      ...current,
      selectedServiceId: current.selectedServiceId || selectedAdminService.id,
      serviceName: selectedAdminService.name || current.serviceName,
      serviceType: selectedAdminService.serviceType || current.serviceType,
      serviceStatus: selectedAdminService.status || current.serviceStatus,
    }));
  }, [selectedAdminService?.id]);

  function answersToMap(answers: CyberAnswer[] = []) {
    const answerMap: Record<string, string | number | boolean | Array<string | number | boolean>> = {};
    for (const answer of answers) {
      if (answer.questionId && answer.value !== undefined && answer.value !== null) answerMap[answer.questionId] = answer.value as string | number | boolean | Array<string | number | boolean>;
    }
    return answerMap;
  }

  function normalizeAnswerForSave(question: CyberQuestion, value: string | number | boolean | Array<string | number | boolean>): CyberAnswer {
    if (question.answerType === 'multi_choice') {
      return { questionId: question.id, valueJson: Array.isArray(value) ? value : String(value ?? '').split(',').map((item) => item.trim()).filter(Boolean) };
    }
    if (question.answerType === 'boolean') {
      return { questionId: question.id, valueBoolean: value === true || value === 'true' };
    }
    if (question.answerType === 'number') {
      const numericValue = Number(value);
      return { questionId: question.id, valueNumber: Number.isFinite(numericValue) ? numericValue : null };
    }
    return { questionId: question.id, valueText: String(value ?? '') };
  }

  async function openProspect(prospectId: string, options: { initialStep?: number; openLatestProposal?: boolean; mode?: 'dossier' | 'proposal' } = {}) {
    setSelectedProspectId(prospectId);
    setProspectWorkspaceMode(options.mode || (options.openLatestProposal ? 'proposal' : 'dossier'));
    setGeneratedProposal(null);
    setAdvisorStep(options.initialStep ?? 0);
    setArchivesExpanded(false);
    setDetailLoading(true);
    setError(null);
    try {
      const detail = await getCyberProspectDetail(prospectId);
      setProspectDetail(detail);
      setProfileEditForm({
        organizationName: detail.prospect.organizationName || '',
        contactName: detail.prospect.contactName || '',
        contactEmail: detail.prospect.contactEmail || '',
        contactPhone: detail.prospect.contactPhone || '',
        notes: detail.prospect.notes || '',
      });
      const answerMap = answersToMap(detail.answers);
      setAnswerValues(answerMap);
      const hasMissingBaseQuestions = baseQuestions.some((question) => question.required && (answerMap[question.id] === undefined || answerMap[question.id] === ''));
      setBaseQuestionsExpanded(options.mode === 'proposal' || options.openLatestProposal ? false : hasMissingBaseQuestions);
      const codesFromAnswers = new Set<string>();
      for (const question of questions) {
        if (question.questionScope === 'service_specific' && answerMap[question.id] !== undefined) {
          question.serviceCodes.forEach((code) => codesFromAnswers.add(code));
        }
      }
      setSelectedServiceCodes(Array.from(codesFromAnswers));

      if (options.openLatestProposal && detail.proposals?.[0]?.id) {
        const proposalDetail = await getCyberProposalDetail(detail.proposals[0].id);
        setGeneratedProposal(proposalDetail);
        setSelectedServiceCodes(proposalDetail.lineItems.map((line) => line.serviceCode).filter(Boolean));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impossible d’ouvrir le prospect.');
    } finally {
      setDetailLoading(false);
    }
  }

  function openProfileEditor() {
    if (!prospectDetail?.prospect) return;
    setProfileEditForm({
      organizationName: prospectDetail.prospect.organizationName || '',
      contactName: prospectDetail.prospect.contactName || '',
      contactEmail: prospectDetail.prospect.contactEmail || '',
      contactPhone: prospectDetail.prospect.contactPhone || '',
      notes: prospectDetail.prospect.notes || '',
    });
    setIsProfileEditOpen(true);
    setError(null);
  }

  async function saveProspectProfile() {
    if (!selectedProspectId) return;
    if (!profileEditForm.organizationName.trim()) {
      setError('Le nom de l’organisation est requis.');
      return;
    }
    setSavingProspectProfile(true);
    setError(null);
    try {
      await updateCyberProspect(selectedProspectId, {
        organizationName: profileEditForm.organizationName.trim(),
        contactName: profileEditForm.contactName.trim(),
        contactEmail: profileEditForm.contactEmail.trim(),
        contactPhone: profileEditForm.contactPhone.trim(),
        notes: profileEditForm.notes.trim(),
      });
      await openProspect(selectedProspectId, { mode: 'dossier' });
      await loadData();
      setIsProfileEditOpen(false);
      setSuccessMessage('Profil du prospect mis à jour.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impossible de modifier le profil du prospect.');
    } finally {
      setSavingProspectProfile(false);
    }
  }

  async function deleteSelectedProspect() {
    if (!selectedProspectId || !prospectDetail?.prospect || !canDeleteSelectedProspect) return;
    const confirmed = window.confirm(`Supprimer le prospect ${prospectDetail.prospect.organizationName}? Cette action est disponible seulement parce qu’aucune offre n’est liée à ce prospect.`);
    if (!confirmed) return;
    setDeletingProspect(true);
    setError(null);
    try {
      await deleteCyberProspect(selectedProspectId);
      const deletedName = prospectDetail.prospect.organizationName;
      setSelectedProspectId(null);
      setProspectDetail(null);
      setAnswerValues({});
      setGeneratedProposal(null);
      await loadData();
      setSuccessMessage(`Prospect ${deletedName} supprimé.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impossible de supprimer ce prospect.');
    } finally {
      setDeletingProspect(false);
    }
  }

  function buildVisibleAnswersForSave() {
    return visibleQuestions
      .filter((question) => !isAnswerMissing(answerValues[question.id]))
      .map((question) => normalizeAnswerForSave(question, answerValues[question.id]));
  }

  function buildBaseAnswersForSave() {
    return baseQuestions
      .filter((question) => !isAnswerMissing(answerValues[question.id]))
      .map((question) => normalizeAnswerForSave(question, answerValues[question.id]));
  }

  function buildAllRequiredAnswersForSave() {
    return [...baseQuestions, ...activeProposalQuestions]
      .filter((question) => !isAnswerMissing(answerValues[question.id]))
      .map((question) => normalizeAnswerForSave(question, answerValues[question.id]));
  }

  async function saveBaseAnswers() {
    if (!selectedProspectId) return;
    setSavingAnswers(true);
    setError(null);
    try {
      await saveCyberProspectAnswers(selectedProspectId, buildBaseAnswersForSave());
      await openProspect(selectedProspectId, { mode: 'dossier' });
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impossible d’enregistrer les questions de base.');
    } finally {
      setSavingAnswers(false);
    }
  }

  function startNewProposal() {
    setGeneratedProposal(null);
    setSelectedServiceCodes([]);
    setProposalMode('services');
    setTargetBudget('');
    setBudgetPeriod('annual');
    setContractTermMonths('12');
    setProposalScenario('recommended');
    setProposalCustomName('');
    setAdvisorStep(0);
    setProspectWorkspaceMode('proposal');
  }

  function addCatalogServiceToProposal(code?: string) {
    if (!code || !selectedProspectId) return;
    setSelectedServiceCodes((current) => Array.from(new Set([...current, code])));
    setProposalMode('services');
    setProspectWorkspaceMode('proposal');
    setAdvisorStep(0);
    setIsCatalogOpen(false);
  }

  function openAdminServiceFromCatalog(code?: string) {
    if (!code) return;
    const linkedService = services.find((service) => service.code === code);
    if (!linkedService) return;
    setAdminForm((current) => ({ ...current, selectedServiceId: linkedService.id }));
    setAdminTab('services');
    setIsCatalogOpen(false);
    setIsAdminOpen(true);
  }

  async function duplicateProposal(proposalId: string) {
    if (!selectedProspectId) return;
    setGeneratingProposal(true);
    setError(null);
    try {
      const detail = await getCyberProposalDetail(proposalId);
      const serviceCodes = detail.lineItems.filter((line) => line.selected).map((line) => line.serviceCode).filter(Boolean);
      const trace = detail.proposal.calculationTrace || {};
      const result = await generateCyberProposal(selectedProspectId, serviceCodes, {
        proposalMode: 'services',
        contractTermMonths: Number(trace.contractTermMonths || contractTermMonths || 12),
      });
      setGeneratedProposal(result);
      setSelectedServiceCodes(serviceCodes);
      setProposalCustomName(`Copie de ${proposalDisplayTitle(detail.proposal)}`);
      setSuccessMessage('Proposition dupliquée. Tu peux maintenant ajuster le nom, le terme, les services ou les montants avant de présenter.');
      setProspectWorkspaceMode('proposal');
      setAdvisorStep(2);
      await loadData();
      const refreshed = await getCyberProspectDetail(selectedProspectId);
      setProspectDetail(refreshed);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impossible de dupliquer la proposition.');
    } finally {
      setGeneratingProposal(false);
    }
  }


  function continueLatestProposal() {
    if (latestProspectProposal?.id) {
      openProposal(latestProspectProposal.id);
      return;
    }
    startNewProposal();
  }

  async function saveWizardAnswers(options: { closeAfterSave?: boolean } = {}) {
    if (!selectedProspectId) return;
    setSavingAnswers(true);
    setError(null);
    try {
      await saveCyberProspectAnswers(selectedProspectId, buildAllRequiredAnswersForSave());
      const isEditingPricedProposal = generatedProposal && !generatedProposal.proposal.calculationTrace?.proposalDraft;
      if (!isEditingPricedProposal && (selectedServiceCodes.length > 0 || proposalMode === 'budget')) {
        const draft = await saveCyberProposalDraft(selectedProspectId, selectedServiceCodes, {
          proposalMode,
          targetBudget: targetBudget ? Number(targetBudget) : 0,
          budgetPeriod,
          contractTermMonths: contractTermMonths ? Number(contractTermMonths) : 12,
          proposalScenario,
          proposalCustomName: proposalCustomName.trim(),
        });
        setGeneratedProposal(draft);
      }
      await loadData();
      if (options.closeAfterSave) {
        setSuccessMessage('Proposition en cours sauvegardée.');
        closeProspectWorkspace();
        return;
      }
      await openProspect(selectedProspectId, { mode: 'proposal', initialStep: advisorStep, openLatestProposal: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impossible d’enregistrer les réponses.');
    } finally {
      setSavingAnswers(false);
    }
  }

  async function handleGenerateProposal() {
    if (!selectedProspectId) return;
    if (missingRequiredQuestions.length > 0) {
      if (missingBaseQuestions.length > 0) setProspectWorkspaceMode('dossier');
      setAdvisorStep(1);
      setError(`Toutes les questions doivent être répondues avant de générer une proposition. Il reste ${pluralize(missingRequiredQuestions.length, 'question à compléter', 'questions à compléter')}.`);
      return;
    }
    setGeneratingProposal(true);
    setError(null);
    try {
      await saveCyberProspectAnswers(selectedProspectId, buildAllRequiredAnswersForSave());
      const result = await generateCyberProposal(selectedProspectId, selectedServiceCodes, {
        proposalMode,
        targetBudget: targetBudget ? Number(targetBudget) : 0,
        budgetPeriod,
        contractTermMonths: contractTermMonths ? Number(contractTermMonths) : 12,
        proposalScenario,
        proposalCustomName: proposalCustomName.trim(),
        draftProposalId: generatedProposal?.proposal.calculationTrace?.proposalDraft ? generatedProposal.proposal.id : undefined,
      });
      setGeneratedProposal(result);
      setAdvisorStep(2);
      setDiscountForm({ percent: '', amount: '', reason: '' });
      await loadData();
      const detail = await getCyberProspectDetail(selectedProspectId);
      setProspectDetail(detail);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impossible de générer la proposition.');
    } finally {
      setGeneratingProposal(false);
    }
  }

  async function createVariant(scenario: 'essential' | 'recommended' | 'complete') {
    if (!selectedProspectId) return;
    const scenarioName = proposalScenarioLabel(scenario);
    const serviceCodes = generatedProposal
      ? generatedProposal.lineItems.filter((line) => line.selected).map((line) => line.serviceCode).filter(Boolean)
      : selectedServiceCodes;
    setProposalScenario(scenario);
    setProposalCustomName(`Option ${scenarioName}`);
    setSelectedServiceCodes(serviceCodes);
    setGeneratedProposal(null);
    setAdvisorStep(2);
  }

  async function openProposal(proposalId: string) {
    setError(null);
    try {
      const detail = await getCyberProposalDetail(proposalId);
      setGeneratedProposal(detail);
      setSelectedProspectId(detail.proposal.prospectId);
      setAnswerValues(answersToMap(detail.answers || []));
      setSelectedServiceCodes(detail.lineItems.map((line) => line.serviceCode).filter(Boolean));
      setProposalMode(detail.proposal.calculationTrace?.proposalMode === 'budget' ? 'budget' : 'services');
      setBudgetPeriod(detail.proposal.calculationTrace?.budgetPeriod === 'monthly' ? 'monthly' : 'annual');
      setTargetBudget(detail.proposal.calculationTrace?.targetBudgetInput ? String(detail.proposal.calculationTrace.targetBudgetInput) : '');
      setContractTermMonths(String(detail.proposal.calculationTrace?.contractTermMonths || 12));
      setProposalScenario(detail.proposal.calculationTrace?.proposalScenario === 'essential' || detail.proposal.calculationTrace?.proposalScenario === 'complete' ? detail.proposal.calculationTrace.proposalScenario : 'recommended');
      setProposalCustomName(typeof detail.proposal.calculationTrace?.proposalCustomName === 'string' ? detail.proposal.calculationTrace.proposalCustomName : '');
      setProspectWorkspaceMode('proposal');
      setAdvisorStep(['ready', 'presented', 'sent', 'review', 'negotiation'].includes(String(detail.proposal.status || '').toLowerCase()) ? 5 : 2);
      setDiscountForm({ percent: '', amount: '', reason: '' });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impossible d’ouvrir la proposition.');
    }
  }

  async function openPipelineOffer(prospectId: string, proposalId?: string) {
    await openProspect(prospectId, { mode: proposalId ? 'proposal' : 'dossier' });
    if (proposalId) await openProposal(proposalId);
  }

  function requestArchiveProposal(proposal: CyberProposal) {
    setArchiveProposalTarget(proposal);
    setArchiveJustification('');
    setError(null);
  }

  async function confirmArchiveProposal() {
    if (!archiveProposalTarget) return;
    const justification = archiveJustification.trim();
    if (!justification) {
      setError('Une justification est requise pour archiver une proposition.');
      return;
    }
    setArchivingProposal(true);
    setError(null);
    try {
      const detail = await updateCyberProposalStatus(archiveProposalTarget.id, 'archived', justification);
      if (generatedProposal?.proposal.id === archiveProposalTarget.id) {
        setGeneratedProposal(detail);
        setProspectWorkspaceMode('dossier');
      }
      if (selectedProspectId) await openProspect(selectedProspectId, { mode: 'dossier' });
      await loadData();
      setSuccessMessage('Proposition archivée avec justification.');
      setArchiveProposalTarget(null);
      setArchiveJustification('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impossible d’archiver la proposition.');
    } finally {
      setArchivingProposal(false);
    }
  }

  async function handleApplyDiscount() {
    if (!generatedProposal?.proposal.id) return;
    setApplyingDiscount(true);
    setError(null);
    try {
      const detail = await applyCyberProposalDiscount(generatedProposal.proposal.id, {
        discountType: 'multi_service',
        percent: 5,
        reason: discountForm.reason || 'Escompte multiservice: plusieurs services BZ en une signature',
      });
      setGeneratedProposal(detail);
      setDiscountForm({ percent: '', amount: '', reason: '' });
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impossible d’appliquer l’escompte multiservice.');
    } finally {
      setApplyingDiscount(false);
    }
  }

  async function removeDiscount(discountId: string) {
    if (!generatedProposal?.proposal.id) return;
    setApplyingDiscount(true);
    setError(null);
    try {
      const detail = await removeCyberProposalDiscount(generatedProposal.proposal.id, discountId);
      setGeneratedProposal(detail);
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impossible de supprimer le rabais.');
    } finally {
      setApplyingDiscount(false);
    }
  }

  async function applyTacticalDeviation() {
    if (!generatedProposal?.proposal.id) return;
    const percent = Number(tacticalDeviationForm.percent || 0);
    const reason = tacticalDeviationForm.reason.trim();
    if (percent <= 0 || percent > 10) {
      setError('La déviation tactique doit être supérieure à 0 % et ne peut pas dépasser 10 %.');
      return;
    }
    if (!reason) {
      setError('Une justification est obligatoire pour appliquer une déviation tactique.');
      return;
    }
    setApplyingDiscount(true);
    setError(null);
    try {
      const detail = await applyCyberProposalDiscount(generatedProposal.proposal.id, {
        discountType: 'tactical_deviation',
        percent,
        reason,
      });
      setGeneratedProposal(detail);
      setTacticalDeviationForm({ percent: '', reason: '' });
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impossible d’appliquer la déviation tactique.');
    } finally {
      setApplyingDiscount(false);
    }
  }

  async function toggleProposalLine(lineId: string, selected: boolean) {
    if (!generatedProposal?.proposal.id) return;
    setUpdatingLineItemId(lineId);
    setError(null);
    try {
      const detail = await updateCyberProposalLineItem(generatedProposal.proposal.id, lineId, selected);
      setGeneratedProposal(detail);
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impossible de mettre à jour le produit.');
    } finally {
      setUpdatingLineItemId(null);
    }
  }

  async function changeProposalStatus(status: string) {
    if (!generatedProposal?.proposal.id) return;
    const confirmationMessages: Record<string, string> = {
      accepted: 'Marquer cette proposition comme acceptée? Le prospect sera marqué comme gagné.',
      refused: 'Marquer cette proposition comme refusée? Le prospect sera marqué comme perdu.',
      converted: 'Convertir cette proposition? Le briefing delivery sera généré pour l’équipe delivery.',
    };
    if (confirmationMessages[status] && !window.confirm(confirmationMessages[status])) return;
    setProposalActionLoading(status);
    setError(null);
    try {
      const detail = await updateCyberProposalStatus(generatedProposal.proposal.id, status);
      setGeneratedProposal(detail);
      if (status === 'accepted') {
        const response = await fetch('/api/projet-v2/accepted-to-project', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ proposalId: generatedProposal.proposal.id }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.error || `Offre acceptée, mais impossible de créer le projet de livraison (HTTP ${response.status})`);
        setSuccessMessage(data.created ? 'Offre acceptée. Projet de livraison créé dans Projets V2 — À cadrer / attribuer.' : 'Offre acceptée. Le projet de livraison existait déjà dans Projets V2.');
      }
      await loadData();
      if (selectedProspectId) {
        const prospect = await getCyberProspectDetail(selectedProspectId);
        setProspectDetail(prospect);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impossible de changer le statut de la proposition.');
    } finally {
      setProposalActionLoading(null);
    }
  }

  async function createDeliveryBriefing() {
    if (!generatedProposal?.proposal.id) return;
    setProposalActionLoading('briefing');
    setError(null);
    try {
      await generateCyberDeliveryBriefing(generatedProposal.proposal.id);
      const detail = await getCyberProposalDetail(generatedProposal.proposal.id);
      setGeneratedProposal(detail);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impossible de générer le briefing delivery.');
    } finally {
      setProposalActionLoading(null);
    }
  }

  async function createDeliveryProjectFromAcceptedOffer() {
    if (!generatedProposal?.proposal.id) return;
    setProposalActionLoading('delivery-project');
    setError(null);
    setSuccessMessage(null);
    try {
      const response = await fetch('/api/projet-v2/accepted-to-project', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ proposalId: generatedProposal.proposal.id }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || `Impossible de créer le projet de livraison (HTTP ${response.status})`);
      setSuccessMessage(data.created ? 'Projet de livraison créé dans Projets V2 — À cadrer / attribuer.' : 'Le projet de livraison existait déjà dans Projets V2.');
      if (data.project?.id) navigate(`/projets-v2/${data.project.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impossible de créer le projet de livraison.');
    } finally {
      setProposalActionLoading(null);
    }
  }

  async function exportClientVisual() {
    if (!generatedProposal?.proposal.id) return;
    setProposalActionLoading('export');
    setError(null);
    try {
      const output = await exportCyberClientVisual(generatedProposal.proposal.id, { includePrices: true, titleSuffix: 'Version complète' });
      const detail = await getCyberProposalDetail(generatedProposal.proposal.id);
      setGeneratedProposal(detail);
      if (output.htmlSnapshot) {
        const exportWindow = window.open('', '_blank');
        exportWindow?.document.write(output.htmlSnapshot);
        exportWindow?.document.close();
        exportWindow?.focus();
        exportWindow?.print();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impossible d’exporter le visuel client.');
    } finally {
      setProposalActionLoading(null);
    }
  }

  async function requestOfferEmail() {
    if (!generatedProposal?.proposal.id) return;
    setProposalActionLoading('offer-email');
    setError(null);
    setSuccessMessage(null);
    try {
      await exportCyberClientVisual(generatedProposal.proposal.id, { includePrices: true, titleSuffix: 'Version complète' });
      await exportCyberClientVisual(generatedProposal.proposal.id, { includePrices: false, titleSuffix: 'Version sans prix' });
      const detail = await getCyberProposalDetail(generatedProposal.proposal.id);
      setGeneratedProposal(detail);
      setIsOfferEmailConfirmOpen(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impossible de préparer le courriel client et les pièces jointes.');
    } finally {
      setProposalActionLoading(null);
    }
  }

  function toggleService(code: string) {
    setSelectedServiceCodes((current) => current.includes(code) ? current.filter((item) => item !== code) : [...current, code]);
  }

  async function saveAdmin(path: string, payload: Record<string, unknown>) {
    setAdminSaving(true);
    setError(null);
    try {
      await saveCyberAdminEntity(path, payload);
      const refreshed = await getCyberAdminSnapshot();
      setAdminSnapshot(refreshed);
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impossible d’enregistrer la configuration Admin.');
    } finally {
      setAdminSaving(false);
    }
  }

  function adjustComplexityFactor(factor: any) {
    setEditingComplexityFactor(factor);
    setComplexityFactorForm({
      low: factor.type === 'number' ? String(factor.low ?? '') : '',
      high: factor.type === 'number' ? String(factor.high ?? '') : '',
      weight: String(factor.weight ?? 0),
    });
  }

  async function saveComplexityFactor() {
    if (!editingComplexityFactor) return;
    const nextWeight = Number(complexityFactorForm.weight);
    const nextLow = complexityFactorForm.low === '' ? null : Number(complexityFactorForm.low);
    const nextHigh = complexityFactorForm.high === '' ? null : Number(complexityFactorForm.high);
    if (!Number.isFinite(nextWeight) || (complexityFactorForm.low !== '' && !Number.isFinite(nextLow)) || (complexityFactorForm.high !== '' && !Number.isFinite(nextHigh))) {
      setError('Valeur invalide pour le facteur de complexité.');
      return;
    }
    await saveAdmin('parameters', {
      code: editingComplexityFactor.code,
      label: editingComplexityFactor.label,
      valueNumeric: nextWeight,
      unit: 'facteur',
      valueJson: { factorType: 'complexity', section: editingComplexityFactor.section, questionCode: editingComplexityFactor.questionCode, type: editingComplexityFactor.type, low: nextLow, high: nextHigh, weight: nextWeight },
    });
    setEditingComplexityFactor(null);
  }

  async function saveAuditRerParameter(code: string, label: string, unit: string) {
    const value = Number(adminParameterValue(code));
    if (!Number.isFinite(value)) {
      setError('Valeur invalide pour la règle Audit-RER.');
      return;
    }
    await saveAdmin('parameters', {
      code,
      label,
      valueNumeric: value,
      unit,
      valueJson: { factorType: 'audit_rer_module' },
    });
    setAuditRerDrafts((current) => {
      const next = { ...current };
      delete next[code];
      return next;
    });
  }

  function selectedServiceId() {
    return adminForm.selectedServiceId || services[0]?.id || '';
  }

  function focusAdminCorrection(serviceId: string, missing: string) {
    const tabByMissing: Record<string, typeof adminTab> = {
      'texte client': 'descriptions',
      livrable: 'descriptions',
      prix: 'calculation',
      recommandation: 'recommendation',
    };
    setAdminForm((current) => ({ ...current, selectedServiceId: serviceId }));
    setAdminTab(tabByMissing[missing] || 'services');
  }

  async function handleAdminSave() {
    const serviceId = selectedServiceId();
    if (adminTab === 'services') {
      await saveAdmin('services', { name: adminForm.serviceName, code: adminForm.serviceCode, serviceType: adminForm.serviceType, status: adminForm.serviceStatus });
    } else if (adminTab === 'descriptions') {
      await saveAdmin('descriptions', { serviceId, audience: adminForm.descriptionAudience, title: adminForm.serviceName || undefined, description: adminForm.descriptionText });
    } else if (adminTab === 'deliverables') {
      await saveAdmin('deliverables', { serviceId, title: adminForm.deliverableTitle, description: adminForm.descriptionText, isClientVisible: true });
    } else if (adminTab === 'parameters') {
      await saveAdmin('parameters', { code: adminForm.parameterCode, label: adminForm.parameterLabel || adminForm.parameterCode, valueNumeric: Number(adminForm.parameterValue), unit: '' });
    } else if (adminTab === 'calculation') {
      await saveAdmin('calculation-rules', { serviceId, pricingModel: adminForm.calculationModel, formulaJson: adminForm.calculationFormula, status: 'draft' });
    } else if (adminTab === 'recommendation') {
      await saveAdmin('recommendation-rules', { serviceId, conditionJson: adminForm.recommendationCondition, explanationTemplate: adminForm.recommendationExplanation, priority: 100, isActive: true });
    } else if (adminTab === 'discounts') {
      await saveAdmin('discount-rules', { name: adminForm.discountName, discountType: adminForm.discountType, percent: Number(adminForm.discountPercent), status: 'draft' });
    }
  }

  async function handlePublishLatest() {
    const latestSet = adminSnapshot?.parameterSets?.find((set) => set.status === 'draft') || adminSnapshot?.parameterSets?.[0];
    await publishCyberAdminVersion({ parameterSetId: latestSet?.id });
    setAdminSnapshot(await getCyberAdminSnapshot());
  }

  function setQuestionAnswer(questionId: string, value: string | number | boolean | Array<string | number | boolean>) {
    setAnswerValues((current) => ({ ...current, [questionId]: value }));
  }

  function renderQuestionField(question: CyberQuestion) {
    const currentValue = answerValues[question.id];
    if (question.code === 'personal_info_scope') {
      return <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">{[{ label: 'Employés', value: 1 }, { label: 'Employés et clients', value: 2 }, { label: 'Employés, clients, et autres', value: 3 }].map((option) => <button key={option.value} type="button" onClick={() => setQuestionAnswer(question.id, option.value)} className={`min-h-[52px] flex-1 basis-40 rounded-xl border px-4 py-3 text-center text-sm font-black leading-snug ${Number(currentValue) === option.value ? 'border-blue-700 bg-blue-700 text-white' : 'border-slate-200 bg-white text-slate-700 hover:border-blue-300'}`}>{option.label}</button>)}</div>;
    }
    if (question.answerType === 'choice') {
      const options = question.options?.length ? question.options : [{ label: 'Français', value: 'fr' }, { label: 'Anglais', value: 'en' }, { label: 'Français et anglais', value: 'fr_en' }];
      return <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">{options.map((option) => <button key={String(option.value)} type="button" onClick={() => setQuestionAnswer(question.id, option.value)} className={`min-h-[52px] flex-1 basis-40 rounded-xl border px-4 py-3 text-center text-sm font-black leading-snug ${currentValue === option.value ? 'border-blue-700 bg-blue-700 text-white' : 'border-slate-200 bg-white text-slate-700 hover:border-blue-300'}`}>{option.label}</button>)}</div>;
    }
    if (question.answerType === 'multi_choice') {
      const selectedValues = Array.isArray(currentValue) ? currentValue.map(String) : [];
      const options = question.options || [];
      return <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">{options.map((option) => {
        const selected = selectedValues.includes(String(option.value));
        return <button key={String(option.value)} type="button" onClick={() => {
          const optionValue = String(option.value);
          setAnswerValues((current) => {
            const rawCurrentValues = current[question.id];
            const currentValues = Array.isArray(rawCurrentValues) ? rawCurrentValues.map((value) => String(value)) : [];
            const nextValues = currentValues.includes(optionValue) ? currentValues.filter((value) => value !== optionValue) : [...currentValues, optionValue];
            return { ...current, [question.id]: nextValues };
          });
        }} className={`min-h-[52px] flex-1 basis-40 rounded-xl border px-4 py-3 text-center text-sm font-black leading-snug ${selected ? 'border-blue-700 bg-blue-700 text-white' : 'border-slate-200 bg-white text-slate-700 hover:border-blue-300'}`}>{option.label}</button>;
      })}</div>;
    }
    if (question.answerType === 'boolean') {
      return <div className="grid grid-cols-2 gap-2">{[{ label: 'Oui', value: true }, { label: 'Non', value: false }].map((option) => <button key={option.label} type="button" onClick={() => setQuestionAnswer(question.id, option.value)} className={`rounded-xl border px-3 py-2 text-sm font-black ${currentValue === option.value ? 'border-blue-700 bg-blue-700 text-white' : 'border-slate-200 bg-white text-slate-700 hover:border-blue-300'}`}>{option.label}</button>)}</div>;
    }
    return <input type={question.answerType === 'number' ? 'number' : 'text'} value={String(currentValue ?? '')} onChange={(event) => setQuestionAnswer(question.id, question.answerType === 'number' ? Number(event.target.value) : event.target.value)} className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder={question.unit || 'Réponse'} />;
  }

  async function handleCreateProspect(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const organizationName = form.organizationName.trim();
    if (!organizationName) return;
    setCreating(true);
    setError(null);
    try {
      const createdProspect = await createCyberProspect({
        organizationName,
        contactName: form.contactName.trim(),
        contactEmail: form.contactEmail.trim(),
        contactPhone: form.contactPhone.trim(),
        notes: form.notes.trim(),
      });
      setForm({ organizationName: '', contactName: '', contactEmail: '', contactPhone: '', notes: '' });
      setIsCreateOpen(false);
      await loadData();
      await openProspect(createdProspect.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impossible de créer le prospect.');
    } finally {
      setCreating(false);
    }
  }

  return (
    <div className="flex-1 w-full max-w-none min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/50 to-cyan-50">
      <Header
        title="Offres de cybersécurité"
        subtitle="Prospects, services, pricing, propositions client, rabais et moteur de recommandations Edith."
      />

      <div className="w-full max-w-none px-6 py-6 xl:px-8 2xl:px-10 space-y-8">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <button
            type="button"
            onClick={() => navigate('/solutions-affaires')}
            className="inline-flex w-fit items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:border-blue-300 hover:text-blue-700"
          >
            <ArrowLeft className="h-4 w-4" />
            Retour à Solutions d’affaires
          </button>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setIsCatalogOpen(true)}
              className="inline-flex w-fit items-center gap-2 rounded-full border border-blue-200 bg-white px-5 py-2.5 text-sm font-black text-blue-700 shadow-sm hover:border-blue-400 hover:bg-blue-50"
            >
              <BookOpen className="h-4 w-4" />
              Catalogue des offres
            </button>
            <button
              type="button"
              onClick={() => setIsCreateOpen(true)}
              className="inline-flex w-fit items-center gap-2 rounded-full bg-blue-700 px-5 py-2.5 text-sm font-black text-white shadow-sm hover:bg-blue-800"
            >
              <Plus className="h-4 w-4" />
              Créer un prospect cyber
            </button>
          </div>
        </div>

        {error && (
          <div className="flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
            <div>
              <p className="font-bold">Erreur Offres de cybersécurité</p>
              <p>{error}</p>
            </div>
          </div>
        )}
        {successMessage && (
          <div className="flex items-start gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
            <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" />
            <div>
              <p className="font-bold">Action Edith terminée</p>
              <p>{successMessage}</p>
            </div>
          </div>
        )}

        <section className="grid grid-cols-1 gap-4 xl:grid-cols-5">
          <div className="rounded-[1.35rem] border border-slate-800/10 bg-gradient-to-br from-slate-950 via-blue-950 to-blue-800 p-5 text-white shadow-lg xl:col-span-1">
            <p className="text-[11px] font-black uppercase tracking-wide text-blue-200">Pipeline actif</p>
            <p className="mt-2 text-3xl font-black">{activePipelineValue.toLocaleString('fr-CA', { maximumFractionDigits: 0 })} $</p>
            <p className="mt-2 text-xs font-bold text-blue-200">Opportunités et propositions en cours</p>
          </div>
          <StatCard label="Prospects actifs" value={stats.activeProspects} />
          <StatCard label="Propositions en cours" value={activeProposalCount} />
          <StatCard label="Prêtes à présenter" value={readyProposalCount} />
          <div className="relative overflow-hidden rounded-[1.35rem] border border-emerald-200 bg-white p-5 shadow-md shadow-emerald-950/5 ring-1 ring-emerald-50">
            <div className="absolute inset-y-0 left-0 w-1.5 bg-gradient-to-b from-emerald-700 to-green-400" />
            <p className="text-[11px] font-black uppercase tracking-wide text-emerald-700">Acceptées</p>
            <p className="mt-2 text-3xl font-black text-slate-950">{acceptedProposalCount}</p>
            <p className="mt-2 text-xs font-black text-emerald-700">{acceptedProposalTotal.toLocaleString('fr-CA', { maximumFractionDigits: 0 })} $ gagnés</p>
          </div>
        </section>

        <section className="rounded-[1.5rem] border border-blue-100 bg-white p-5 shadow-xl shadow-blue-950/5">
          <div className="mb-5 flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
            <div>
              <h2 className="flex items-center gap-2 text-xl font-black text-slate-900"><span aria-hidden="true">📁</span>Toutes les offres</h2>
              <p className="mt-1 text-sm text-slate-600">Vue selon les filtres appliqués. Tous affiche les offres actives, acceptées et refusées; les archives restent dans le filtre Archivés.</p>
            </div>
            <div className="flex flex-col gap-2 md:flex-row md:items-center">
              <input value={prospectSearch} onChange={(event) => setProspectSearch(event.target.value)} className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-blue-300 focus:bg-white md:w-80" placeholder="Rechercher un prospect…" />
              <div className="flex flex-wrap gap-2">
                {[
                  ['all', 'Tous'],
                  ['active', 'Actifs'],
                  ['accepted', 'Acceptés'],
                  ['lost', 'Refusés'],
                  ['archived', 'Archivés'],
                ].map(([value, label]) => (
                  <button key={value} type="button" onClick={() => setProspectStatusFilter(value as typeof prospectStatusFilter)} className={`rounded-full px-4 py-2 text-xs font-black ${prospectStatusFilter === value ? 'bg-blue-700 text-white' : 'bg-slate-100 text-slate-700 hover:bg-blue-50 hover:text-blue-700'}`}>{label}</button>
                ))}
              </div>
            </div>
          </div>

          {!hasVisibleKanbanItems && !loading ? (
            <div className="rounded-3xl border border-dashed border-slate-200 bg-slate-50 p-8 text-center">
              <Users className="mx-auto mb-4 h-12 w-12 text-slate-400" />
              <p className="font-black text-slate-900">Aucune offre dans cette vue</p>
              <p className="mt-2 text-sm text-slate-600">Ajuste la recherche, change de filtre ou crée un nouveau prospect cyber.</p>
            </div>
          ) : (
            <div className="grid min-w-[1180px] gap-3 overflow-x-auto pb-2" style={{ gridTemplateColumns: `repeat(${kanbanStages.length}, minmax(190px, 1fr))` }}>
              {kanbanStages.map((stage) => (
                <div key={stage.code} className={`min-h-[520px] rounded-[1.5rem] border ${stage.accent} ${stage.panel} p-3 shadow-inner shadow-slate-900/5`}>
                  <div className="mb-3 flex items-start justify-between gap-2">
                    <div>
                      <p className="font-black text-slate-900">{stage.title}</p>
                      <p className="text-xs font-black text-slate-700">{pluralize(stage.items.length, 'offre')} · {stage.total.toLocaleString('fr-CA', { maximumFractionDigits: 0 })} $</p>
                    </div>
                    <span className={`rounded-full px-2.5 py-1 text-xs font-black shadow-sm ${stage.badge}`}>{stage.items.length}</span>
                  </div>
                  <div className="space-y-3">
                    {stage.items.length === 0 ? (
                      <div className="rounded-2xl border border-dashed border-slate-300 bg-white/60 p-4 text-center text-xs font-bold text-slate-500">Aucune offre</div>
                    ) : stage.items.map(({ prospect, proposal, total, score, recommendation, chips }) => {
                      const scoreTone = score >= 75 ? 'text-emerald-700 ring-emerald-100' : score >= 50 ? 'text-amber-700 ring-amber-100' : 'text-red-700 ring-red-100';
                      return (
                        <article key={proposal?.id || prospect.id} className={`rounded-[1.25rem] border border-white/80 bg-white p-4 shadow-lg shadow-slate-900/5 ring-1 ring-slate-900/5 transition hover:-translate-y-0.5 hover:shadow-xl ${stage.card}`}>
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <h3 className="truncate text-[15px] font-black text-slate-900">{prospect.organizationName}</h3>
                              <p className="mt-1 truncate text-xs font-black text-slate-700">{proposal ? proposalDisplayTitle(proposal) : prospect.contactName || 'Dossier prospect à qualifier'}</p>
                            </div>
                            <div className={`grid h-12 w-12 shrink-0 place-items-center rounded-full bg-white text-[11px] font-black ring-4 ${scoreTone}`}><span>{score}%</span></div>
                          </div>
                          <div className="mt-3 flex flex-wrap gap-1.5">
                            {chips.slice(0, 3).map((chip) => <span key={chip} className={`max-w-full truncate rounded-full px-2 py-1 text-[11px] font-black shadow-sm ring-1 ${stage.action}`}>{chip}</span>)}
                          </div>
                          <div className={`mt-3 rounded-2xl p-3 text-xs font-black ring-1 ${stage.action}`}>{recommendation}</div>
                          <div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-3">
                            <strong className="text-sm text-slate-900">{total > 0 ? `${total.toLocaleString('fr-CA', { maximumFractionDigits: 0 })} $` : 'À chiffrer'}</strong>
                            <button type="button" onClick={() => openPipelineOffer(prospect.id, proposal?.id)} className="rounded-xl border border-blue-200 bg-white px-3 py-2 text-xs font-black text-blue-700 hover:bg-blue-50">{proposal ? 'Ouvrir l’offre' : 'Ouvrir'}</button>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>


        <section className="grid grid-cols-1 gap-6 xl:grid-cols-[1.55fr_0.85fr]">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="rounded-[1.5rem] border border-blue-100 bg-white p-6 shadow-xl shadow-blue-950/5">
            <div className="mb-5 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <h2 className="flex items-center gap-2 text-xl font-black text-slate-900"><span aria-hidden="true">🎯</span>Opportunités détectées automatiquement</h2>
                <p className="mt-1 text-sm text-slate-600">Ce que le conseiller devrait prioriser selon les dossiers, propositions et potentiel commercial.</p>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
              {cyberOpportunities.map((opportunity) => (
                <button key={opportunity.id} type="button" onClick={() => opportunity.amount > 0 ? openPipelineOffer(String(opportunity.prospectId), String(opportunity.id)) : openPipelineOffer(String(opportunity.prospectId || opportunity.id))} className={`relative overflow-hidden rounded-[1.25rem] border border-blue-100 bg-gradient-to-b from-white to-blue-50/40 p-4 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-blue-300 hover:shadow-lg before:absolute before:inset-y-0 before:left-0 before:w-1.5 ${opportunity.tone === 'red' ? 'before:bg-red-500' : opportunity.tone === 'amber' ? 'before:bg-amber-500' : 'before:bg-purple-500'}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="rounded-full bg-blue-700 px-2.5 py-1 text-[11px] font-black text-white shadow-sm">{opportunity.tag}</span>
                    <span className="text-xs font-black text-slate-700">{opportunity.account}</span>
                  </div>
                  <h3 className="mt-4 text-base font-black text-slate-900">{opportunity.title}</h3>
                  <p className="mt-2 min-h-[54px] text-sm leading-relaxed text-slate-700">{opportunity.description}</p>
                  <p className="mt-3 text-2xl font-black text-slate-900">{Number(opportunity.amount || 0).toLocaleString('fr-CA', { maximumFractionDigits: 0 })} $</p>
                  <p className="text-xs font-bold text-slate-500">Potentiel estimé</p>
                </button>
              ))}
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.06 }} className="rounded-[1.5rem] border border-blue-100 bg-white p-6 shadow-xl shadow-blue-950/5">
            <div className="mb-4 flex items-start justify-between gap-3">
              <div>
                <h2 className="flex items-center gap-2 text-xl font-black text-slate-900"><span aria-hidden="true">💡</span>Edith Insights</h2>
                <p className="mt-1 text-sm text-slate-600">Recommandations de vente à garder visibles.</p>
              </div>
              {loading && <Loader2 className="h-5 w-5 animate-spin text-blue-600" />}
            </div>
            <div className="space-y-3">
              {insightCards.map((insight) => (
                <div key={`${insight.badge}-${insight.title}`} className="flex gap-3 rounded-[1.25rem] border border-blue-100 bg-blue-50/50 p-3 shadow-sm">
                  <div className="grid h-10 w-10 shrink-0 place-items-center rounded-2xl bg-blue-700 text-sm font-black text-white shadow-sm">{insight.badge}</div>
                  <div className="min-w-0 flex-1">
                    <p className="font-black text-slate-900">{insight.title}</p>
                    <p className="text-xs font-bold text-slate-700">{insight.subtitle}</p>
                    <p className="mt-1 text-[11px] font-black text-slate-500">{insight.sourceLabel}</p>
                    <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-200"><div className="h-full rounded-full bg-gradient-to-r from-blue-600 to-cyan-400" style={{ width: `${insight.width}%` }} /></div>
                  </div>
                </div>
              ))}
              {!insightCards.length && <p className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-4 text-sm font-bold text-slate-500">Basé sur les propositions et services réels. Les insights apparaîtront dès qu’il y aura des propositions ou prospects actifs.</p>}
            </div>
          </motion.div>
        </section>

        <div className="rounded-[1.35rem] bg-gradient-to-br from-slate-950 to-blue-900 p-5 text-white shadow-xl shadow-slate-900/10">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <strong>⚙ Paramètres de l’offre cyber</strong>
              <p className="mt-1 text-sm text-slate-300">Modifier services, prix, règles Edith, questionnaires, rabais et visuel client.</p>
            </div>
            <button type="button" onClick={() => setIsAdminOpen(true)} className="rounded-2xl bg-white px-4 py-3 text-sm font-black text-blue-900">Ouvrir les paramètres</button>
          </div>
        </div>      </div>

      {isCompareOpen && prospectDetail && (
        <div className="fixed inset-0 z-[55] flex items-center justify-center bg-slate-950/50 p-4 backdrop-blur-sm">
          <div className="max-h-[90vh] w-full max-w-5xl overflow-y-auto rounded-3xl bg-white p-6 shadow-2xl">
            <div className="mb-5 flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-black uppercase tracking-wide text-blue-600">Comparaison</p>
                <h3 className="text-2xl font-black text-slate-900">Comparer les propositions — {prospectDetail.prospect.organizationName}</h3>
                <p className="mt-1 text-sm text-slate-600">Vue rapide pour choisir entre des options essentielle, recommandée ou complète.</p>
              </div>
              <button type="button" onClick={() => setIsCompareOpen(false)} className="rounded-full p-2 text-slate-500 hover:bg-slate-100"><X className="h-5 w-5" /></button>
            </div>
            <div className="overflow-x-auto rounded-2xl border border-slate-100">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr><th className="p-3">Proposition</th><th className="p-3">Statut</th><th className="p-3">Total</th><th className="p-3">Mensualité</th><th className="p-3">Rabais</th><th className="p-3">Action</th></tr>
                </thead>
                <tbody>
                  {prospectDetail.proposals.map((proposal) => (
                    <tr key={proposal.id} className="border-t border-slate-100">
                      <td className="p-3">
                        <p className="font-black text-slate-900">{proposalDisplayTitle(proposal)}</p>
                        <p className="mt-0.5 text-xs font-bold text-slate-500">{proposalSubtitle(proposal)} · {new Date(proposal.updatedAt).toLocaleDateString('fr-CA')}</p>
                      </td>
                      <td className="p-3">{statusLabel(proposal.status)}</td>
                      <td className="p-3 font-black">{proposal.finalOneTimeTotal.toFixed(0)} $</td>
                      <td className="p-3">{proposal.finalRecurringMonthlyTotal.toFixed(0)} $ / mois</td>
                      <td className="p-3">{proposal.discountTotal.toFixed(0)} $</td>
                      <td className="p-3"><button type="button" onClick={() => { setIsCompareOpen(false); openProposal(proposal.id); }} className="rounded-full bg-blue-700 px-3 py-1.5 text-xs font-black text-white">Ouvrir</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {isCatalogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4 backdrop-blur-sm">
          <div className="max-h-[92vh] w-full max-w-6xl overflow-y-auto rounded-3xl bg-white p-6 shadow-2xl">
            <div className="sticky top-0 z-10 mb-6 flex items-start justify-between gap-4 border-b border-slate-100 bg-white/95 pb-4 backdrop-blur">
              <div>
                <p className="text-xs font-black uppercase tracking-wide text-blue-600">Catalogue lecture seule</p>
                <h3 className="text-3xl font-black text-slate-900">Catalogue des offres de cybersécurité</h3>
                <p className="mt-1 text-sm text-slate-600">Synthèse structurée des {cyberOfferCatalog.length} services provenant du catalogue: nom, description complète, étapes, livrables, notes et recommandations.</p>
              </div>
              <button type="button" onClick={() => setIsCatalogOpen(false)} className="rounded-full p-2 text-slate-500 hover:bg-slate-100"><X className="h-5 w-5" /></button>
            </div>

            <div className="sticky top-[94px] z-10 -mx-2 mb-5 flex gap-2 overflow-x-auto bg-white/95 px-2 py-2 backdrop-blur">
              {cyberOfferCatalog.map((offer, index) => (
                <button
                  key={offer.name}
                  type="button"
                  onClick={() => setActiveCatalogIndex(index)}
                  className={`shrink-0 rounded-full px-4 py-2 text-xs font-black transition ${activeCatalogIndex === index ? 'bg-blue-700 text-white shadow-sm' : 'bg-slate-100 text-slate-700 hover:bg-blue-50 hover:text-blue-700'}`}
                >
                  {offer.name}
                </button>
              ))}
            </div>

            {activeCatalogOffer && (
              <article className="rounded-3xl border border-slate-200 bg-slate-50 p-5">
                <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="text-xs font-black uppercase tracking-wide text-blue-600">Offre {activeCatalogIndex + 1} / {cyberOfferCatalog.length}</p>
                    <h4 className="mt-1 text-2xl font-black text-slate-900">{activeCatalogOffer.name}</h4>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {activeCatalogOffer.code && services.some((service) => service.code === activeCatalogOffer.code) && <button type="button" onClick={() => openAdminServiceFromCatalog(activeCatalogOffer.code)} className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-black text-emerald-700 ring-1 ring-emerald-100 hover:bg-emerald-100">Fiche Admin liée</button>}
                    <button type="button" onClick={() => addCatalogServiceToProposal(activeCatalogOffer.code)} disabled={!selectedProspectId || !activeCatalogOffer.code} title={!selectedProspectId ? 'Ouvre un dossier prospect avant d’ajouter un service à une proposition.' : undefined} className="rounded-full bg-blue-700 px-3 py-1 text-xs font-black text-white disabled:bg-slate-300">{selectedProspectId ? 'Ajouter à la proposition' : 'Ouvrir un dossier pour ajouter'}</button>
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-4 xl:grid-cols-[1fr_1fr]">
                  <section className="rounded-2xl bg-white p-4 ring-1 ring-slate-100 xl:col-span-2"><h5 className="font-black text-slate-900">Description du service</h5>
                    {renderCatalogDescription(activeCatalogOffer)}
                  </section>
                  <section className="rounded-2xl bg-white p-4 ring-1 ring-slate-100 xl:col-span-2">
                    <h5 className="font-black text-slate-900">Étapes du service</h5>
                    <div className="mt-3 overflow-x-auto rounded-2xl border border-slate-100">
                      <table className="w-full text-left text-sm">
                        <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                          <tr>
                            <th className="p-3">Étapes</th>
                            <th className="p-3">Qui est impliqué</th>
                            <th className="p-3">Déroulement</th>
                          </tr>
                        </thead>
                        <tbody>
                          {activeCatalogOffer.steps.map((step) => (
                            <tr key={`${step.step}-${step.flow}`} className="border-t border-slate-100 align-top">
                              <td className="p-3 font-black text-slate-900">{step.step}</td>
                              <td className="p-3 text-slate-700">{step.involved}</td>
                              <td className="p-3 leading-relaxed text-slate-700">{step.flow}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </section>
                  <section className="rounded-2xl bg-white p-4 ring-1 ring-slate-100">
                    <h5 className="font-black text-slate-900">Livrables</h5>
                    <ul className="mt-2 list-disc space-y-1 pl-5 text-sm leading-relaxed text-slate-700">
                      {activeCatalogOffer.deliverables.map((deliverable) => <li key={deliverable}>{deliverable}</li>)}
                    </ul>
                  </section>
                  <section className="rounded-2xl bg-white p-4 ring-1 ring-slate-100">
                    <h5 className="font-black text-slate-900">Notes et recommandations</h5>
                    <ul className="mt-2 list-disc space-y-1 pl-5 text-sm leading-relaxed text-slate-700">
                      {activeCatalogOffer.notes.map((note) => <li key={note}>{note}</li>)}
                    </ul>
                  </section>
                </div>
              </article>
            )}
          </div>
        </div>
      )}

      {isAdminOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4 backdrop-blur-sm">
          <div className="max-h-[92vh] w-full max-w-6xl overflow-y-auto rounded-3xl bg-white p-6 shadow-2xl">
            <div className="mb-6 flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-black uppercase tracking-wide text-blue-600">Admin Offres cyber</p>
                <h3 className="text-3xl font-black text-slate-900">Paramètres de vente cybersécurité</h3>
                <p className="mt-1 text-sm text-slate-600">Une interface de configuration simple, inspirée de l’onglet Paramètres: on modifie les valeurs métier, pas les codes internes.</p>
              </div>
              <button type="button" onClick={() => setIsAdminOpen(false)} className="rounded-full p-2 text-slate-500 hover:bg-slate-100"><X className="h-5 w-5" /></button>
            </div>

            <div className="mb-5 flex flex-wrap gap-2">
              {[
                ['services', 'Services'], ['parameters', 'Prix généraux'], ['calculation', 'Prix du service'], ['discounts', 'Rabais'], ['descriptions', 'Texte et livrables'], ['recommendation', 'Recommandation'], ['versions', 'Publication']
              ].map(([key, label]) => (
                <button key={key} type="button" onClick={() => setAdminTab(key as typeof adminTab)} className={`rounded-full px-4 py-2 text-sm font-black ${adminTab === key ? 'bg-blue-700 text-white' : 'bg-slate-100 text-slate-700'}`}>{label}</button>
              ))}
            </div>

            <div className="mb-5 rounded-3xl border border-blue-100 bg-blue-50 p-4">
              <label className="text-sm font-black text-blue-950">Service à configurer</label>
              <select value={adminForm.selectedServiceId || selectedAdminService?.id || ''} onChange={(e) => setAdminForm({ ...adminForm, selectedServiceId: e.target.value })} className="mt-2 w-full rounded-2xl border border-blue-100 bg-white px-4 py-3 text-sm font-bold text-slate-800">
                <option value="">Choisir un service</option>
                {services.map((service) => <option key={service.id} value={service.id}>{service.name}</option>)}
              </select>
              {selectedAdminService && <p className="mt-2 text-sm text-blue-900">Configuration active: <strong>{selectedAdminService.name}</strong></p>}
            </div>

            <div className="mb-5 rounded-3xl border border-amber-200 bg-amber-50 p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h4 className="font-black text-amber-950">Services à compléter</h4>
                  <p className="text-sm text-amber-800">L’admin voit immédiatement ce qui manque avant publication.</p>
                </div>
                <span className="rounded-full bg-white px-3 py-1 text-xs font-black text-amber-800">{incompleteAdminServices.length} à corriger</span>
              </div>
              <div className="mt-3 grid grid-cols-1 gap-2 md:grid-cols-2">
                {incompleteAdminServices.slice(0, 6).map(({ service, missing }) => (
                  <div key={service.id} className="rounded-2xl bg-white p-3 text-sm">
                    <button type="button" onClick={() => setAdminForm({ ...adminForm, selectedServiceId: service.id })} className="text-left">
                      <p className="font-black text-slate-900">{service.name}</p>
                      <p className="mt-1 text-xs text-amber-800">À compléter: {missing.join(', ')}</p>
                    </button>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {missing.map((item) => <button key={item} type="button" onClick={() => focusAdminCorrection(service.id, item)} className="rounded-full bg-amber-100 px-3 py-1 text-xs font-black text-amber-900">Corriger {item}</button>)}
                    </div>
                  </div>
                ))}
                {incompleteAdminServices.length === 0 && <p className="rounded-2xl bg-white p-3 text-sm font-bold text-emerald-700">Tous les services semblent complets.</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 gap-5 xl:grid-cols-[1fr_0.8fr]">
              <div className="rounded-3xl border border-slate-200 bg-slate-50 p-5">
                {adminTab === 'services' && (
                  <div className="space-y-4">
                    <h4 className="text-xl font-black text-slate-900">Identité du service</h4>
                    <HelpText>Modifie le nom, le type et le statut du service sélectionné. Le code technique reste stable en arrière-plan.</HelpText>
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                      <label className="text-sm font-bold text-slate-700 md:col-span-2">Nom du service<input value={adminForm.serviceName} onChange={(e) => setAdminForm({ ...adminForm, serviceName: e.target.value })} className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 font-normal" placeholder="Ex: Accompagnement Loi 25" /></label>
                      <label className="text-sm font-bold text-slate-700">Type<select value={adminForm.serviceType} onChange={(e) => setAdminForm({ ...adminForm, serviceType: e.target.value })} className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 font-normal"><option value="one_time">Ponctuel</option><option value="recurring">Mensuel</option><option value="bundle">Forfait</option><option value="manual">À définir</option></select></label>
                      <label className="text-sm font-bold text-slate-700">Statut<select value={adminForm.serviceStatus} onChange={(e) => setAdminForm({ ...adminForm, serviceStatus: e.target.value })} className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 font-normal"><option value="draft">Brouillon</option><option value="published">Publié</option><option value="archived">Archivé</option></select></label>
                    </div>
                    <button type="button" onClick={() => saveAdmin('services', { id: selectedAdminService?.id, code: selectedAdminService?.code, name: adminForm.serviceName, serviceType: adminForm.serviceType, status: adminForm.serviceStatus })} disabled={adminSaving || !adminForm.serviceName.trim()} className="rounded-xl bg-blue-700 px-4 py-2 text-sm font-black text-white disabled:bg-slate-300">Enregistrer la fiche service</button>
                  </div>
                )}

                {adminTab === 'parameters' && (
                  <div className="space-y-4">
                    <h4 className="text-xl font-black text-slate-900">Prix généraux</h4>
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                      <label className="text-sm font-bold text-slate-700">Taux horaire<input type="number" value={adminForm.parameterValue} onChange={(e) => setAdminForm({ ...adminForm, parameterValue: e.target.value })} className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 font-normal" placeholder="206" /></label>
                      <button type="button" onClick={() => saveAdmin('parameters', { code: 'hourly_rate', label: 'Taux horaire', valueNumeric: Number(adminForm.parameterValue), unit: '$/h' })} disabled={adminSaving || !adminForm.parameterValue} className="self-end rounded-xl bg-blue-700 px-4 py-2 text-sm font-black text-white disabled:bg-slate-300">Enregistrer le taux</button>
                    </div>
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                      <label className="text-sm font-bold text-slate-700">Impact traduction (%)<input type="number" value={adminForm.discountPercent} onChange={(e) => setAdminForm({ ...adminForm, discountPercent: e.target.value })} className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 font-normal" placeholder="10" /></label>
                      <button type="button" onClick={() => saveAdmin('parameters', { code: 'translation_impact_percent', label: 'Impact traduction', valueNumeric: Number(adminForm.discountPercent), unit: '%' })} disabled={adminSaving || !adminForm.discountPercent} className="self-end rounded-xl bg-blue-700 px-4 py-2 text-sm font-black text-white disabled:bg-slate-300">Enregistrer traduction</button>
                    </div>
                    <section className="rounded-3xl border border-blue-100 bg-white p-4">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <h5 className="font-black text-slate-900">Paramètres du coefficient</h5>
                          <p className="mt-1 text-sm text-slate-600">Ces facteurs alimentent le coefficient de complexité comme dans l’onglet Paramètres du Excel: valeur normalisée × poids.</p>
                        </div>
                        <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-black text-blue-700">{complexityFactors.length} facteurs</span>
                      </div>
                      {([
                        ['general', 'Informations générales', groupedComplexityFactors.general],
                        ['loi25', 'Loi 25 — 3 critères du coefficient', groupedComplexityFactors.loi25],
                      ] as const).map(([, title, factors]) => (
                        <div key={title} className="mt-4 rounded-2xl bg-slate-50 p-3">
                          <p className="text-sm font-black text-slate-900">{title}</p>
                          <div className="mt-2 space-y-2">
                            {factors.map((factor) => (
                              <div key={factor.code} className={`rounded-xl border p-3 text-sm ${editingComplexityFactor?.code === factor.code ? 'border-blue-200 bg-blue-50 shadow-sm' : 'border-transparent bg-white'}`}>
                                <div className="grid grid-cols-1 gap-2 md:grid-cols-[1fr_auto] md:items-center">
                                  <div>
                                    <p className="font-black text-slate-900">{factor.label}</p>
                                    <p className="text-xs text-slate-500">{factor.type === 'number' ? `Bas ${factor.low} · Haut ${factor.high}` : factor.type === 'boolean_inverse' ? 'Oui/Non inverse' : 'Oui/Non'} · Poids {Number(factor.weight || 0).toFixed(2)}</p>
                                  </div>
                                  <button type="button" onClick={() => adjustComplexityFactor(factor)} className={`rounded-xl border px-3 py-2 text-xs font-black shadow-sm ${editingComplexityFactor?.code === factor.code ? 'border-blue-700 bg-blue-700 text-white' : 'border-blue-100 bg-blue-50 text-blue-700 hover:bg-blue-100'}`}>{editingComplexityFactor?.code === factor.code ? 'En modification' : 'Modifier ici'}</button>
                                </div>
                                {editingComplexityFactor?.code === factor.code && (
                                  <div className="mt-3 rounded-2xl border border-blue-200 bg-white p-4 shadow-sm">
                                    <div className="flex flex-wrap items-start justify-between gap-3">
                                      <div>
                                        <p className="text-xs font-black uppercase tracking-wide text-blue-700">Édition du facteur</p>
                                        <h6 className="mt-1 text-lg font-black text-slate-950">{editingComplexityFactor.label}</h6>
                                        <p className="mt-1 text-sm font-bold text-slate-700">Modifie ce facteur directement sous la ligne sélectionnée.</p>
                                      </div>
                                      <button type="button" onClick={() => setEditingComplexityFactor(null)} className="rounded-full border border-blue-100 bg-white px-3 py-1.5 text-xs font-black text-slate-700 hover:bg-blue-50">Annuler</button>
                                    </div>
                                    <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3">
                                      {editingComplexityFactor.type === 'number' && (
                                        <>
                                          <label className="text-sm font-black text-slate-700">Seuil bas<input type="number" value={complexityFactorForm.low} onChange={(event) => setComplexityFactorForm((current) => ({ ...current, low: event.target.value }))} className="mt-1 w-full rounded-xl border border-blue-100 bg-white px-3 py-2 font-normal text-slate-900" /></label>
                                          <label className="text-sm font-black text-slate-700">Seuil haut<input type="number" value={complexityFactorForm.high} onChange={(event) => setComplexityFactorForm((current) => ({ ...current, high: event.target.value }))} className="mt-1 w-full rounded-xl border border-blue-100 bg-white px-3 py-2 font-normal text-slate-900" /></label>
                                        </>
                                      )}
                                      <label className="text-sm font-black text-slate-700">Poids du facteur<input type="number" step="0.01" value={complexityFactorForm.weight} onChange={(event) => setComplexityFactorForm((current) => ({ ...current, weight: event.target.value }))} className="mt-1 w-full rounded-xl border border-blue-100 bg-white px-3 py-2 font-normal text-slate-900" /></label>
                                    </div>
                                    <div className="mt-4 flex flex-wrap items-center gap-2">
                                      <button type="button" onClick={saveComplexityFactor} disabled={adminSaving} className="rounded-xl bg-blue-700 px-4 py-2 text-sm font-black text-white shadow-sm hover:bg-blue-800 disabled:bg-slate-300">Enregistrer le facteur</button>
                                      <span className="text-xs font-bold text-blue-800">{editingComplexityFactor.type === 'number' ? 'Le coefficient utilise la position entre seuil bas et seuil haut.' : 'Ce facteur utilise une réponse Oui/Non.'}</span>
                                    </div>
                                  </div>
                                )}
                              </div>
                            ))}
                            {factors.length === 0 && <p className="text-sm font-bold text-slate-500">Aucun facteur configuré.</p>}
                          </div>
                        </div>
                      ))}
                    </section>
                  </div>
                )}

                {adminTab === 'calculation' && (
                  <div className="space-y-4">
                    <h4 className="text-xl font-black text-slate-900">Prix du service</h4>
                    <p className="text-sm text-slate-600">Choisis comment ce service est facturé. Les règles techniques sont générées en arrière-plan.</p>
                    {selectedAdminService?.code === 'audit-rer' && (
                      <section className="rounded-3xl border border-blue-200 bg-white p-5 shadow-sm">
                        <div className="flex flex-wrap items-start justify-between gap-4">
                          <div className="max-w-3xl">
                            <p className="text-xs font-black uppercase tracking-wide text-blue-700">Audit-RER</p>
                            <h5 className="mt-1 text-xl font-black text-slate-950">Calcul de prix — Audit / évaluation des risques</h5>
                            <p className="mt-2 text-sm font-bold leading-6 text-slate-700">Ajuste les paramètres utilisés par Edith pour calculer le prix de l’audit. Les valeurs sont regroupées par étape pour éviter les champs comprimés et les boutons qui débordent.</p>
                          </div>
                          <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-black text-blue-800">Source: logique Audit-RER</span>
                        </div>
                        <div className="mt-5 grid grid-cols-1 gap-4 2xl:grid-cols-2">
                          {auditRerAdminGroups.map((group, groupIndex) => (
                            <div key={group.title} className="rounded-3xl border border-slate-200 bg-slate-50 p-4">
                              <div className="flex items-start gap-3">
                                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-700 text-sm font-black text-white">{groupIndex + 1}</span>
                                <div>
                                  <h6 className="font-black text-slate-950">{group.title}</h6>
                                  <p className="mt-1 text-sm font-semibold leading-5 text-slate-600">{group.description}</p>
                                </div>
                              </div>
                              <div className="mt-4 space-y-3">
                                {group.fields.map(([code, label, unit]) => (
                                  <div key={code} className="rounded-2xl border border-slate-100 bg-white p-3 shadow-sm">
                                    <label className="block text-sm font-black text-slate-900">{label}</label>
                                    <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-[minmax(0,1fr)_96px] sm:items-center lg:grid-cols-[minmax(0,1fr)_112px]">
                                      <div className="relative min-w-0">
                                        <input type="number" step="0.01" value={adminParameterValue(code)} onChange={(event) => setAuditRerDrafts((current) => ({ ...current, [code]: event.target.value }))} className="w-full min-w-0 rounded-xl border border-slate-200 bg-white px-3 py-2 pr-16 font-semibold text-slate-900 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100" />
                                        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs font-black text-slate-500">{unit}</span>
                                      </div>
                                      <button type="button" onClick={() => saveAuditRerParameter(code, label, unit)} disabled={adminSaving} className="w-full rounded-xl bg-blue-700 px-3 py-2 text-xs font-black text-white shadow-sm hover:bg-blue-800 disabled:bg-slate-300">Sauver</button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                        <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950">
                          <p className="font-black">Résumé du calcul</p>
                          <p className="mt-1 leading-6">Edith additionne les modules de l’audit, ajoute les pourcentages de conclusion et de révision, puis ajoute la présentation, la charge de projet et le minimum facturable avant le total final.</p>
                        </div>
                      </section>
                    )}
                    <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                      {[
                        ['fixed_price', 'Prix fixe', 'Même prix pour tous les clients'],
                        ['hourly_complexity', 'Heures × taux', 'Prix basé sur l’effort estimé'],
                        ['unit_monthly', 'Mensuel par utilisateur/unité', 'Facturation récurrente'],
                        ['vulnerability_scan', 'Selon les équipements', 'Serveurs, imprimantes, équipements connectés'],
                        ['manual_nd', 'À définir manuellement', 'Le conseiller confirme le prix'],
                      ].map(([value, label, description]) => <button key={value} type="button" onClick={() => setAdminForm({ ...adminForm, calculationModel: value })} className={`rounded-2xl border p-4 text-left text-sm ${adminForm.calculationModel === value ? 'border-blue-700 bg-blue-700 text-white' : 'border-slate-200 bg-white text-slate-700 hover:border-blue-300'}`}><span className="block font-black">{label}</span><span className="mt-1 block text-xs opacity-80">{description}</span></button>)}
                    </div>
                    <button type="button" onClick={() => saveAdmin('calculation-rules', { serviceId: selectedServiceId(), pricingModel: adminForm.calculationModel === 'fixed_price' ? 'manual_nd' : adminForm.calculationModel, formulaJson: { mode: adminForm.calculationModel }, status: 'draft' })} disabled={adminSaving || !selectedServiceId()} className="rounded-xl bg-blue-700 px-4 py-2 text-sm font-black text-white disabled:bg-slate-300">Enregistrer le mode de prix</button>
                  </div>
                )}

                {adminTab === 'discounts' && (
                  <div className="space-y-4">
                    <h4 className="text-xl font-black text-slate-900">Rabais</h4>
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                      <label className="text-sm font-bold text-slate-700">Type de rabais<select value={adminForm.discountType} onChange={(e) => setAdminForm({ ...adminForm, discountType: e.target.value })} className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 font-normal"><option value="multi_product_auto">Multiproduit automatique</option><option value="multi_service">Multiservice</option><option value="manual">Manuel conseiller</option><option value="tactical_deviation">Déviation tactique</option></select></label>
                      <label className="text-sm font-bold text-slate-700">Pourcentage<input type="number" value={adminForm.discountPercent} onChange={(e) => setAdminForm({ ...adminForm, discountPercent: e.target.value })} className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 font-normal" placeholder="10" /></label>
                      <button type="button" onClick={() => saveAdmin('discount-rules', { name: adminForm.discountType === 'multi_product_auto' ? 'Rabais multiproduit automatique' : 'Rabais conseiller', discountType: adminForm.discountType, percent: Number(adminForm.discountPercent), status: 'draft' })} disabled={adminSaving || !adminForm.discountPercent} className="self-end rounded-xl bg-blue-700 px-4 py-2 text-sm font-black text-white disabled:bg-slate-300">Enregistrer le rabais</button>
                    </div>
                  </div>
                )}

                {adminTab === 'descriptions' && (
                  <div className="space-y-4">
                    <h4 className="text-xl font-black text-slate-900">Services et visuel client</h4>
                    <select value={adminForm.selectedServiceId} onChange={(e) => setAdminForm({ ...adminForm, selectedServiceId: e.target.value })} className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"><option value="">Choisir un service</option>{services.map((service) => <option key={service.id} value={service.id}>{service.name}</option>)}</select>
                    <textarea value={adminForm.descriptionText} onChange={(e) => setAdminForm({ ...adminForm, descriptionText: e.target.value })} className="min-h-[120px] w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm" placeholder="Texte client à afficher dans le visuel…" />
                    <input value={adminForm.deliverableTitle} onChange={(e) => setAdminForm({ ...adminForm, deliverableTitle: e.target.value })} className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm" placeholder="Livrable inclus, ex: Rapport de recommandations" />
                    <div className="flex flex-wrap gap-2"><button type="button" onClick={() => saveAdmin('descriptions', { serviceId: selectedServiceId(), audience: 'client', description: adminForm.descriptionText })} disabled={adminSaving || !selectedServiceId()} className="rounded-xl bg-blue-700 px-4 py-2 text-sm font-black text-white disabled:bg-slate-300">Enregistrer le texte client</button><button type="button" onClick={() => saveAdmin('deliverables', { serviceId: selectedServiceId(), title: adminForm.deliverableTitle, isClientVisible: true })} disabled={adminSaving || !selectedServiceId() || !adminForm.deliverableTitle} className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-black text-white disabled:bg-slate-300">Ajouter le livrable</button></div>
                  </div>
                )}

                {adminTab === 'recommendation' && (
                  <div className="space-y-4">
                    <h4 className="text-xl font-black text-slate-900">Recommandation Edith</h4>
                    <p className="text-sm text-slate-600">Explique quand Edith devrait proposer ce service au conseiller. Pas besoin d’écrire de condition technique.</p>
                    <textarea value={adminForm.recommendationExplanation} onChange={(e) => setAdminForm({ ...adminForm, recommendationExplanation: e.target.value })} className="min-h-[120px] w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm" placeholder="Ex: Recommander si le client collecte des renseignements personnels de clients ou si la gouvernance est absente." />
                    <button type="button" onClick={() => saveAdmin('recommendation-rules', { serviceId: selectedServiceId(), conditionJson: { configuredByAdmin: true, serviceName: selectedAdminService?.name }, explanationTemplate: adminForm.recommendationExplanation || `Edith recommande ${selectedAdminService?.name || 'ce service'} selon les réponses du client.`, priority: 50, isActive: true })} disabled={adminSaving || !selectedServiceId()} className="rounded-xl bg-blue-700 px-4 py-2 text-sm font-black text-white disabled:bg-slate-300">Enregistrer la recommandation</button>
                  </div>
                )}

                {adminTab === 'versions' && (
                  <div className="space-y-4">
                    <h4 className="text-xl font-black text-slate-900">Publication</h4>
                    <p className="text-sm text-slate-600">Publier rend la dernière version brouillon disponible comme référence active.</p>
                    <button type="button" onClick={handlePublishLatest} disabled={adminSaving || !(adminSnapshot?.parameterSets?.length)} className="rounded-xl bg-blue-700 px-4 py-2 text-sm font-black text-white disabled:bg-slate-300">Publier la dernière version brouillon</button>
                  </div>
                )}
              </div>

              <div className="rounded-3xl border border-slate-200 bg-white p-5">
                <h4 className="text-xl font-black text-slate-900">Fiche service</h4>
                {selectedAdminService ? (
                  <div className="mt-4 space-y-4">
                    <div className="rounded-2xl bg-slate-50 p-4">
                      <p className="text-xs font-black uppercase tracking-wide text-slate-500">Service sélectionné</p>
                      <p className="mt-1 text-lg font-black text-slate-900">{selectedAdminService.name}</p>
                      <p className="text-sm text-slate-600">{selectedAdminService.categoryName || 'Catégorie à préciser'} · {selectedAdminService.serviceType || 'type à préciser'}</p>
                    </div>
                    <div className="space-y-2">
                      {[
                        ['Identité', true, 'Nom, catégorie et type de service'],
                        ['Texte client', Boolean(selectedAdminServiceHealth?.descriptions), 'Texte visible dans le visuel client'],
                        ['Livrables', Boolean(selectedAdminServiceHealth?.deliverables), 'Ce qui sera remis au client'],
                        ['Questions requises', true, 'Questions du wizard liées au service'],
                        ['Prix', Boolean(selectedAdminServiceHealth?.calculations), 'Mode de calcul configuré'],
                        ['Recommandation', Boolean(selectedAdminServiceHealth?.recommendations), 'Quand Edith doit proposer le service'],
                        ['Publication', selectedAdminService.status === 'published', 'Statut publié ou brouillon'],
                      ].map(([label, done, help]) => (
                        <div key={String(label)} className="flex items-start gap-3 rounded-2xl bg-slate-50 p-3 text-sm">
                          <CheckCircle2 className={`mt-0.5 h-4 w-4 ${done ? 'text-emerald-600' : 'text-amber-500'}`} />
                          <div>
                            <p className="font-black text-slate-900">{label}</p>
                            <p className="text-xs text-slate-500">{help}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                    {selectedAdminServiceHealth?.missing?.length ? (
                      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                        <p className="font-black">À corriger</p>
                        <p className="mt-1">Il manque: {selectedAdminServiceHealth.missing.join(', ')}.</p>
                      </div>
                    ) : (
                      <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm font-bold text-emerald-800">Cette fiche service est prête côté admin.</div>
                    )}
                  </div>
                ) : <p className="mt-4 text-sm text-slate-600">Choisis un service pour voir sa fiche.</p>}
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedProspectId && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-950/40 p-4 backdrop-blur-sm">
          <div className="max-h-[92vh] w-full max-w-[96vw] overflow-y-auto overflow-x-hidden rounded-3xl bg-white p-6 shadow-2xl">
            <div className="mb-5 flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-black uppercase tracking-wide text-blue-600">{prospectWorkspaceMode === 'dossier' ? 'Dossier prospect' : 'Éditeur de proposition'}</p>
                <h3 className="text-2xl font-black text-slate-900">{prospectDetail?.prospect.organizationName || 'Prospect cyber'}</h3>
                <p className="mt-1 text-sm text-slate-600">{prospectWorkspaceMode === 'dossier' ? 'Profil de base, propositions en cours et nouvelles propositions.' : 'Sélectionner les services visés, répondre aux questions conditionnelles, puis générer ou modifier une proposition.'}</p>
              </div>
              <button type="button" onClick={() => { setSelectedProspectId(null); setProspectDetail(null); }} className="rounded-full p-2 text-slate-500 hover:bg-slate-100"><X className="h-5 w-5" /></button>
            </div>

            {detailLoading ? (
              <div className="flex items-center gap-3 rounded-2xl bg-slate-50 p-5 text-slate-600"><Loader2 className="h-5 w-5 animate-spin" />Chargement du parcours…</div>
            ) : prospectWorkspaceMode === 'dossier' ? (
              <div className="overflow-hidden rounded-[1.75rem] border border-blue-100 bg-white shadow-2xl shadow-slate-900/10">
                <div className="border-b border-slate-100 bg-gradient-to-br from-white via-blue-50 to-slate-50 p-6">
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div>
                      <p className="text-xs font-black uppercase tracking-wide text-blue-700">Dossier prospect</p>
                      <h4 className="mt-1 text-3xl font-black text-slate-950">{prospectDetail?.prospect.organizationName}</h4>
                      <p className="mt-2 max-w-2xl text-sm font-bold text-slate-700">Les 12 questions de base restent ouvertes tant qu’elles ne sont pas complétées. Une fois répondues, elles se réduisent pour laisser la place aux propositions.</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button type="button" onClick={openProfileEditor} className="inline-flex items-center justify-center gap-2 rounded-2xl border border-blue-200 bg-white px-4 py-3 text-sm font-black text-blue-800 shadow-sm hover:bg-blue-50">Modifier le profil</button>
                      {canDeleteSelectedProspect && (
                        <button type="button" onClick={deleteSelectedProspect} disabled={deletingProspect} className="inline-flex items-center justify-center gap-2 rounded-2xl border border-red-200 bg-white px-4 py-3 text-sm font-black text-red-700 shadow-sm hover:bg-red-50 disabled:opacity-60">
                          {deletingProspect ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                          Supprimer le prospect
                        </button>
                      )}
                      {latestProspectProposal && <button type="button" onClick={continueLatestProposal} className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-950 px-4 py-3 text-sm font-black text-white shadow-sm">Continuer la proposition active</button>}
                      <button type="button" onClick={() => startNewProposal()} className="inline-flex items-center justify-center gap-2 rounded-2xl bg-blue-700 px-4 py-3 text-sm font-black text-white shadow-sm hover:bg-blue-800"><Plus className="h-4 w-4" />Nouvelle proposition</button>
                    </div>
                  </div>
                  <div className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-4">
                    <div className="rounded-2xl border border-slate-200 bg-white p-3 text-sm shadow-sm"><p className="text-[11px] font-black uppercase tracking-wide text-slate-500">Organisation</p><p className="mt-1 truncate font-black text-slate-950">{prospectDetail?.prospect.organizationName || 'À préciser'}</p></div>
                    <div className="rounded-2xl border border-slate-200 bg-white p-3 text-sm shadow-sm"><p className="text-[11px] font-black uppercase tracking-wide text-slate-500">Contact</p><p className="mt-1 truncate font-black text-slate-950">{prospectDetail?.prospect.contactName || 'À préciser'}</p></div>
                    <div className="rounded-2xl border border-slate-200 bg-white p-3 text-sm shadow-sm"><p className="text-[11px] font-black uppercase tracking-wide text-slate-500">Courriel</p><p className="mt-1 truncate font-black text-slate-950">{prospectDetail?.prospect.contactEmail || '—'}</p></div>
                    <div className="rounded-2xl border border-slate-200 bg-white p-3 text-sm shadow-sm"><p className="text-[11px] font-black uppercase tracking-wide text-slate-500">Téléphone</p><p className="mt-1 truncate font-black text-slate-950">{prospectDetail?.prospect.contactPhone || '—'}</p></div>
                  </div>
                  {prospectDetail?.prospect.notes && (
                    <div className="mt-3 rounded-2xl border border-slate-200 bg-white p-3 text-sm shadow-sm">
                      <p className="text-[11px] font-black uppercase tracking-wide text-slate-500">Notes</p>
                      <p className="mt-1 text-slate-800">{prospectDetail.prospect.notes}</p>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-[360px_1fr]">
                  <aside className="space-y-4 border-r border-slate-100 bg-slate-50/60 p-5">
                    <section className="rounded-[1.35rem] border border-slate-200 bg-white p-4 shadow-sm">
                      <div className="flex items-center justify-between gap-3">
                        <h4 className="font-black text-slate-950">Profil prospect</h4>
                        <span className="rounded-full bg-blue-700 px-3 py-1 text-xs font-black text-white">{baseQuestionsCompletionPercent}%</span>
                      </div>
                      {missingBaseQuestions.length > 0 ? (
                        <div className="mt-3 rounded-2xl border border-amber-200 bg-amber-50 p-3 text-sm font-bold text-amber-900">⚠ {pluralize(missingBaseQuestions.length, 'question à compléter', 'questions à compléter')}</div>
                      ) : (
                        <div className="mt-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-3 text-sm font-bold text-emerald-800">Profil de base complété</div>
                      )}
                    </section>

                    <section className="rounded-[1.35rem] border border-slate-200 bg-white p-4 shadow-sm">
                      <button type="button" onClick={() => setBaseQuestionsExpanded((current) => !current)} className="flex w-full items-center justify-between gap-3 text-left">
                        <span>
                          <span className="block font-black text-slate-950">{baseQuestionsExpanded ? '▼' : '▶'} Questions de base ({baseQuestionsAnsweredCount}/{baseQuestions.length})</span>
                          <span className="mt-1 block text-xs font-bold text-slate-600">{missingBaseQuestions.length > 0 ? 'À compléter avant de préparer les propositions.' : `Cliquer pour ${baseQuestionsExpanded ? 'réduire' : 'modifier'} le profil.`}</span>
                        </span>
                        <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-black text-slate-700">{baseQuestionsExpanded ? 'Réduire' : 'Ouvrir'}</span>
                      </button>
                      {!baseQuestionsExpanded && (
                        <div className="mt-3 divide-y divide-slate-100 border-t border-slate-100 pt-2">
                          {baseQuestionPreview.map((question) => <div key={question.id} className="py-2 text-sm"><p className="font-bold text-slate-700">{question.label}</p><p className="mt-0.5 font-black text-slate-950">{formatQuestionAnswer(question)}</p></div>)}
                        </div>
                      )}
                      {baseQuestionsExpanded && (
                        <div className="mt-4 border-t border-slate-100 pt-4">
                          <div className="mb-4 h-2 overflow-hidden rounded-full bg-slate-100"><div className="h-full rounded-full bg-blue-600" style={{ width: `${baseQuestionsCompletionPercent}%` }} /></div>
                          <div className="max-h-[520px] space-y-3 overflow-y-auto pr-1">
                            {baseQuestions.map((question) => (
                              <div key={question.id} className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                                <span className="mb-2 block text-sm font-bold text-slate-800">{question.label}</span>
                                {renderQuestionField(question)}
                              </div>
                            ))}
                          </div>
                          <button type="button" onClick={saveBaseAnswers} disabled={savingAnswers} className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-blue-700 px-4 py-3 text-sm font-black text-white disabled:bg-slate-300">{savingAnswers && <Loader2 className="h-4 w-4 animate-spin" />}Enregistrer le profil</button>
                        </div>
                      )}
                    </section>

                    {proposalTimeline.length > 0 && (
                      <section className="rounded-[1.35rem] border border-slate-200 bg-white p-4 shadow-sm">
                        <h4 className="font-black text-slate-950">Activité récente</h4>
                        <div className="mt-3 space-y-2">
                          {proposalTimeline.slice(0, 4).map((event, index) => (
                            <div key={`${event.label}-${index}`} className="flex items-start gap-3 rounded-2xl bg-slate-50 p-3 text-sm">
                              <span className="mt-1 h-2 w-2 rounded-full bg-blue-600" />
                              <div><p className="font-black text-slate-900">{event.label}</p><p className="text-xs font-bold text-slate-600">{event.detail}{event.date ? ` · ${new Date(event.date).toLocaleDateString('fr-CA')}` : ''}</p></div>
                            </div>
                          ))}
                        </div>
                      </section>
                    )}
                  </aside>

                  <main className="space-y-5 p-5">
                    {activeDossierProposal ? (
                      <section className="overflow-hidden rounded-[1.35rem] bg-gradient-to-br from-blue-700 via-blue-800 to-slate-950 p-5 text-white shadow-xl shadow-blue-950/20">
                        <div className="flex flex-wrap items-start justify-between gap-4">
                          <div>
                            <p className="text-xs font-black uppercase tracking-wide text-blue-100">Proposition active</p>
                            <h4 className="mt-2 text-2xl font-black">{proposalDisplayTitle(activeDossierProposal)}</h4>
                            <p className="mt-1 text-sm font-bold text-blue-100">{proposalSubtitle(activeDossierProposal)} · {statusLabel(activeDossierProposal.status)}</p>
                          </div>
                          <div className="text-right"><p className="text-3xl font-black">{activeDossierProposal.finalOneTimeTotal.toLocaleString('fr-CA', { maximumFractionDigits: 0 })} $</p><p className="text-sm font-bold text-blue-100">{activeDossierProposal.finalRecurringMonthlyTotal.toFixed(0)} $ / mois</p></div>
                        </div>
                        <div className="mt-5 flex flex-wrap gap-2">
                          <button type="button" onClick={() => openProposal(activeDossierProposal.id)} className="rounded-2xl bg-white px-4 py-3 text-sm font-black text-blue-900 shadow-sm">Modifier cette offre</button>
                          <button type="button" onClick={() => duplicateProposal(activeDossierProposal.id)} disabled={generatingProposal} className="rounded-2xl border border-white/30 px-4 py-3 text-sm font-black text-white disabled:opacity-60">Créer une variante</button>
                          <button type="button" onClick={() => requestArchiveProposal(activeDossierProposal)} className="rounded-2xl border border-red-200 bg-white/10 px-4 py-3 text-sm font-black text-white hover:bg-red-500/20">Archiver cette version</button>
                        </div>
                      </section>
                    ) : (
                      <section className="rounded-[1.35rem] border-2 border-dashed border-blue-200 bg-blue-50 p-6 text-center">
                        <h4 className="text-xl font-black text-slate-950">Aucune proposition active</h4>
                        <p className="mt-2 text-sm font-bold text-slate-700">Créer une nouvelle offre à partir du profil du prospect.</p>
                        <button type="button" onClick={() => startNewProposal()} className="mt-4 inline-flex items-center gap-2 rounded-2xl bg-blue-700 px-5 py-3 text-sm font-black text-white"><Plus className="h-4 w-4" />Nouvelle proposition</button>
                      </section>
                    )}

                    <section className="rounded-[1.35rem] border border-slate-200 bg-white p-5 shadow-sm">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div>
                          <h4 className="text-xl font-black text-slate-950">Autres propositions</h4>
                          <p className="text-sm font-bold text-slate-600">Versions actives à comparer pour ce prospect.</p>
                        </div>
                        {(prospectDetail?.proposals?.length || 0) > 1 && <button type="button" onClick={() => setIsCompareOpen(true)} className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-black text-slate-700 hover:border-blue-300 hover:text-blue-700">Comparer</button>}
                      </div>
                      <div className="mt-4 space-y-3">
                        {secondaryDossierProposals.length ? secondaryDossierProposals.map((proposal) => (
                          <div key={proposal.id} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-100 bg-slate-50 p-4">
                            <div className="min-w-0"><p className="truncate font-black text-slate-950">{proposalDisplayTitle(proposal)}</p><p className="mt-1 text-xs font-bold text-slate-600">{proposalSubtitle(proposal)} · {statusLabel(proposal.status)}</p></div>
                            <div className="flex flex-wrap items-center gap-2"><strong className="text-slate-950">{proposal.finalOneTimeTotal.toLocaleString('fr-CA', { maximumFractionDigits: 0 })} $</strong><button type="button" onClick={() => openProposal(proposal.id)} className="rounded-full bg-blue-700 px-3 py-1.5 text-xs font-black text-white">Ouvrir</button><button type="button" onClick={() => requestArchiveProposal(proposal)} className="rounded-full border border-red-200 bg-white px-3 py-1.5 text-xs font-black text-red-700 hover:bg-red-50">Archiver</button></div>
                          </div>
                        )) : <p className="rounded-2xl bg-slate-50 p-4 text-sm font-bold text-slate-600">Aucune autre proposition active pour le moment.</p>}
                      </div>
                    </section>

                    {activeDossierProposal && (
                      <section className="rounded-[1.35rem] border-2 border-dashed border-blue-200 bg-blue-50 p-6 text-center">
                        <h4 className="text-xl font-black text-slate-950">Nouvelle proposition</h4>
                        <p className="mt-2 text-sm font-bold text-slate-700">Créer une nouvelle offre à partir des réponses déjà saisies dans le profil du prospect.</p>
                        <button type="button" onClick={() => startNewProposal()} className="mt-4 inline-flex items-center gap-2 rounded-2xl bg-blue-700 px-5 py-3 text-sm font-black text-white"><Plus className="h-4 w-4" />Nouvelle proposition</button>
                      </section>
                    )}

                    <section className="rounded-[1.35rem] border border-slate-200 bg-white p-5 shadow-sm">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div>
                          <h4 className="text-xl font-black text-slate-950">Archives</h4>
                          <p className="text-sm font-bold text-slate-600">Offres retirées de la liste active, avec la justification conservée.</p>
                        </div>
                        <button type="button" onClick={() => setArchivesExpanded((current) => !current)} className="rounded-full bg-slate-100 px-3 py-1.5 text-xs font-black text-slate-700 hover:bg-slate-200">
                          {archivesExpanded ? 'Masquer' : `Voir ${pluralize(archivedDossierProposals.length, 'proposition archivée', 'propositions archivées')}`}
                        </button>
                      </div>
                      {archivesExpanded && (
                        <div className="mt-4 space-y-3">
                          {archivedDossierProposals.length ? archivedDossierProposals.map((proposal) => (
                            <div key={proposal.id} className="rounded-2xl border border-slate-100 bg-slate-50 p-4 text-sm">
                              <div className="flex flex-wrap items-center justify-between gap-3">
                                <div className="min-w-0"><p className="truncate font-black text-slate-950">{proposalDisplayTitle(proposal)}</p><p className="mt-1 text-xs font-bold text-slate-600">Archivée le {new Date(proposal.archivedAt || proposal.updatedAt).toLocaleDateString('fr-CA')} · {proposal.finalOneTimeTotal.toLocaleString('fr-CA', { maximumFractionDigits: 0 })} $</p></div>
                                <button type="button" onClick={() => openProposal(proposal.id)} className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-black text-slate-700 hover:border-blue-300 hover:text-blue-700">Consulter</button>
                              </div>
                              {proposal.archivedReason && <p className="mt-2 rounded-xl bg-white px-3 py-2 text-xs font-bold text-slate-600">Justification: {proposal.archivedReason}</p>}
                            </div>
                          )) : <p className="rounded-2xl bg-slate-50 p-4 text-sm font-bold text-slate-600">Aucune offre archivée.</p>}
                        </div>
                      )}
                    </section>
                  </main>
                </div>
              </div>
            ) : (
              <div className="fixed inset-0 z-[45] flex h-screen flex-col overflow-hidden bg-slate-100">
                <header className="z-40 shrink-0 border-b border-blue-100 bg-white shadow-sm">
                  <div className="flex flex-wrap items-center justify-between gap-4 px-5 py-4">
                    <div className="flex min-w-0 items-center gap-4">
                      <button type="button" onClick={() => { setProspectWorkspaceMode('dossier'); setGeneratedProposal(null); }} className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-black text-blue-900 hover:border-blue-300 hover:text-blue-700"><ArrowLeft className="h-4 w-4" />Retour au dossier prospect</button>
                      <div className="min-w-0">
                        <p className="text-xs font-black uppercase tracking-wide text-blue-700">Éditeur de proposition</p>
                        <h3 className="truncate text-2xl font-black text-slate-950">{prospectDetail?.prospect.organizationName || 'Prospect'} — {generatedProposalDisplayName || 'Nouvelle proposition'}</h3>
                        <p className="mt-1 text-xs font-bold text-slate-500">Sélectionner les services, qualifier, générer le visuel client et suivre la décision.</p>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button type="button" onClick={() => generatedProposal && duplicateProposal(generatedProposal.proposal.id)} disabled={!generatedProposal || generatingProposal} className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-black text-slate-700 disabled:opacity-50">Dupliquer</button>
                      <button type="button" onClick={() => generatedProposal && setIsClientVisualOpen(true)} disabled={!generatedProposal?.clientVisualHtml} className="rounded-xl bg-slate-950 px-4 py-2 text-sm font-black text-white disabled:bg-slate-300">Aperçu client</button>
                      <button type="button" onClick={currentAdvisorStep === advisorWizardSteps.length - 1 ? () => saveWizardAnswers({ closeAfterSave: true }) : () => saveWizardAnswers()} disabled={savingAnswers} className="rounded-xl bg-blue-700 px-4 py-2 text-sm font-black text-white disabled:bg-slate-300">{savingAnswers ? 'Sauvegarde…' : 'Enregistrer'}</button>
                    </div>
                  </div>
                  <div className="px-5 pb-4">
                    <nav className="grid grid-cols-2 gap-2 rounded-3xl border border-blue-100 bg-blue-50 p-2 md:grid-cols-3 xl:grid-cols-6">
                      {advisorWizardSteps.map((step, index) => (
                        <button key={step} type="button" onClick={() => setAdvisorStep(index === 1 && !shouldShowQualificationStep ? 2 : index)} className={`relative min-h-[68px] rounded-2xl border p-3 text-left text-sm transition ${currentAdvisorStep === index ? 'border-blue-700 bg-blue-700 text-white shadow-lg shadow-blue-700/20' : index < currentAdvisorStep ? 'border-emerald-200 bg-emerald-50 text-emerald-900' : 'border-blue-100 bg-white text-slate-700'}`}>
                          {index < currentAdvisorStep && <span className="absolute right-3 top-3 h-2.5 w-2.5 rounded-full bg-emerald-500" />}
                          <span className="block text-[10px] font-black uppercase tracking-wide opacity-70">Étape {index + 1}</span>
                          <span className="mt-1 block font-black">{step}</span>
                          {index === 1 && !shouldShowQualificationStep && <span className="mt-1 block text-[11px] font-bold opacity-75">Sautée</span>}
                        </button>
                      ))}
                    </nav>
                  </div>
                </header>

                <main className="mx-auto grid min-h-0 w-full max-w-[1800px] flex-1 grid-cols-1 gap-5 overflow-y-auto overscroll-contain p-5 xl:grid-cols-[300px_minmax(0,1fr)_320px]">
                  <aside className="space-y-4 xl:sticky xl:top-4 xl:self-start">
                    <section className="rounded-3xl bg-gradient-to-br from-blue-950 to-blue-700 p-5 text-white shadow-lg">
                      <p className="text-xs font-black uppercase tracking-wide text-blue-200">Prospect</p>
                      <h4 className="mt-2 text-3xl font-black">{prospectDetail?.prospect.organizationName || 'Prospect'}</h4>
                      <p className="mt-2 text-sm font-bold text-blue-100">{prospectDetail?.prospect.contactName || 'Contact à préciser'}{prospectDetail?.prospect.contactEmail ? ` · ${prospectDetail.prospect.contactEmail}` : ''}</p>
                      <div className="mt-4 flex flex-wrap gap-2">
                        {['CRM cyber', `${baseQuestionsCompletionPercent}% profil`, pluralize(selectedServiceCodes.length, 'service')].map((chip) => <span key={chip} className="rounded-full border border-white/20 bg-white/15 px-3 py-1 text-xs font-bold">{chip}</span>)}
                      </div>
                    </section>
                    <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                      <h4 className="font-black text-slate-950">Profil prospect</h4>
                      <div className="mt-3 divide-y divide-slate-100 text-sm">
                        <div className="flex justify-between py-2"><span className="text-slate-500">Questions de base</span><strong>{baseQuestionsAnsweredCount}/{baseQuestions.length}</strong></div>
                        <div className="flex justify-between py-2"><span className="text-slate-500">Services visés</span><strong>{selectedServiceCodes.length}</strong></div>
                        <div className="flex justify-between py-2"><span className="text-slate-500">Statut offre</span><strong>{generatedProposal ? statusLabel(generatedProposal.proposal.status) : 'Brouillon'}</strong></div>
                      </div>
                      <button type="button" onClick={() => setProspectWorkspaceMode('dossier')} className="mt-4 w-full rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-black text-slate-700">Modifier le profil</button>
                    </section>
                    <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                      <div className="flex items-center justify-between gap-3"><h4 className="font-black text-slate-950">Questions répondues</h4><span className="text-xs font-black text-slate-500">{baseQuestionsAnsweredCount}/{baseQuestions.length}</span></div>
                      <div className="mt-3 space-y-2">
                        {baseQuestionPreview.map((question) => <div key={question.id} className="rounded-2xl border border-slate-100 bg-slate-50 p-3 text-sm"><p className="font-black text-slate-900">{question.label}</p><p className="mt-1 text-xs font-bold text-slate-500">{formatQuestionAnswer(question)}</p></div>)}
                      </div>
                    </section>
                  </aside>

                  <section className="min-w-0 space-y-4">
                    {missingBaseQuestions.length > 0 && (
                      <div className="rounded-3xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                        <p className="font-black">Profil prospect incomplet</p>
                        <p className="mt-1">Il reste {pluralize(missingBaseQuestions.length, 'question de base à répondre', 'questions de base à répondre')} dans le dossier avant de générer une proposition.</p>
                        <button type="button" onClick={() => setProspectWorkspaceMode('dossier')} className="mt-3 rounded-xl bg-amber-700 px-4 py-2 text-xs font-black text-white">Compléter le dossier</button>
                      </div>
                    )}

                    <section className="grid grid-cols-1 gap-4 rounded-3xl border border-blue-100 bg-white p-5 shadow-sm md:grid-cols-[1fr_auto] md:items-center">
                      <div>
                        <span className={`inline-flex rounded-full px-3 py-1 text-xs font-black ${proposalReadiness.missing.length === 0 ? 'bg-emerald-100 text-emerald-800' : 'bg-blue-50 text-blue-800'}`}>{proposalReadiness.missing.length === 0 ? '● Prête à présenter' : '● En préparation'}</span>
                        <h2 className="mt-3 text-3xl font-black tracking-tight text-slate-950">{generatedProposalDisplayName || 'Plan d’action cybersécurité'}</h2>
                        <p className="mt-1 text-sm font-bold text-slate-500">Offre active · {proposalMode === 'budget' ? 'Budget client' : 'Services ciblés'} · Mandat {String(generatedProposal?.proposal.calculationTrace?.contractTermMonths || contractTermMonths)} mois</p>
                      </div>
                      <div className="text-left md:text-right">
                        <p className="text-xs font-bold text-slate-500">Total proposition</p>
                        <p className="text-4xl font-black tracking-tight text-blue-700">{generatedProposal ? generatedProposal.proposal.finalOneTimeTotal.toLocaleString('fr-CA', { maximumFractionDigits: 0 }) : '0'} $</p>
                      </div>
                    </section>

                    {missingRequiredQuestions.length > 0 && (
                      <section className="rounded-3xl border-2 border-amber-300 bg-amber-50 p-5 shadow-sm">
                        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                          <div className="flex gap-3">
                            <span className="mt-0.5 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-amber-600 text-white"><AlertCircle className="h-5 w-5" /></span>
                            <div>
                              <p className="text-sm font-black uppercase tracking-wide text-amber-800">Questions obligatoires à compléter</p>
                              <h4 className="mt-1 text-xl font-black text-slate-950">Il reste {pluralize(missingRequiredQuestions.length, 'question requise', 'questions requises')} avant de générer l’offre.</h4>
                              <ul className="mt-3 space-y-2 text-sm font-bold text-amber-950">
                                {missingRequiredQuestions.slice(0, 5).map((question) => <li key={question.id} className="flex gap-2"><span>•</span><span>{question.label}</span></li>)}
                              </ul>
                              {missingRequiredQuestions.length > 5 && <p className="mt-2 text-xs font-black text-amber-800">+ {missingRequiredQuestions.length - 5} autre{missingRequiredQuestions.length - 5 > 1 ? 's' : ''} question{missingRequiredQuestions.length - 5 > 1 ? 's' : ''} à compléter</p>}
                            </div>
                          </div>
                          <div className="flex shrink-0 flex-wrap gap-2 lg:justify-end">
                            {missingBaseQuestions.length > 0 && <button type="button" onClick={() => setProspectWorkspaceMode('dossier')} className="rounded-xl border border-amber-300 bg-white px-4 py-2 text-sm font-black text-amber-900 hover:bg-amber-100">Compléter le profil</button>}
                            {missingProposalQuestions.length > 0 && <button type="button" onClick={() => setAdvisorStep(1)} className="rounded-xl bg-amber-700 px-4 py-2 text-sm font-black text-white hover:bg-amber-800">Aller aux questions</button>}
                          </div>
                        </div>
                      </section>
                    )}

                    {activeDossierProposals.length ? (
                      <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                          <div>
                            <h4 className="font-black text-slate-900">Propositions actives</h4>
                            <p className="mt-1 text-xs font-bold text-slate-600">Ouvre une proposition active sans changer de contexte, ou duplique-la pour créer une variante.</p>
                          </div>
                          <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-black text-blue-800">{pluralize(activeDossierProposals.length, 'proposition active', 'propositions actives')}</span>
                        </div>
                        <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 2xl:grid-cols-3">
                          {activeDossierProposals.map((proposal) => (
                            <div key={proposal.id} className={`rounded-2xl border p-3 text-sm shadow-sm ${generatedProposal?.proposal.id === proposal.id ? 'border-blue-300 bg-blue-50' : 'border-slate-100 bg-slate-50'}`}>
                              <div className="min-w-0"><p className="truncate font-black text-slate-900">{proposalDisplayTitle(proposal)}</p><p className="mt-0.5 text-xs font-bold text-slate-600">{proposalSubtitle(proposal)} · {statusLabel(proposal.status)}</p></div>
                              <div className="mt-3 flex items-center justify-between gap-2"><span className="font-black text-blue-800">{proposal.finalOneTimeTotal.toFixed(0)} $</span><div className="flex flex-wrap gap-2"><button type="button" onClick={() => openProposal(proposal.id)} className="rounded-full bg-blue-700 px-3 py-1.5 text-xs font-black text-white">Modifier</button><button type="button" onClick={() => duplicateProposal(proposal.id)} disabled={generatingProposal} className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-black text-slate-700 disabled:opacity-60">Dupliquer</button></div></div>
                            </div>
                          ))}
                        </div>
                      </section>
                    ) : null}

                    <main className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                    {currentAdvisorStep === 0 && (
                      <div className="space-y-4">
                        <div>
                          <h4 className="text-xl font-black text-slate-900">1. Services visés</h4>
                          <HelpText>Choisis le point de départ le plus naturel: les services déjà ciblés par le conseiller ou l’enveloppe budgétaire du client.</HelpText>
                        </div>
                        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                          <button type="button" onClick={() => setProposalMode('services')} className={`rounded-2xl border p-4 text-left text-sm ${proposalMode === 'services' ? 'border-blue-700 bg-blue-50 text-blue-950' : 'border-slate-200 bg-white text-slate-700'}`}><span className="block font-black">Je sais quels services proposer</span><span className="mt-1 block text-xs">Sélectionne les services à évaluer, puis Edith calcule la proposition.</span></button>
                          <button type="button" onClick={() => { setProposalMode('budget'); setSelectedServiceCodes([]); }} className={`rounded-2xl border p-4 text-left text-sm ${proposalMode === 'budget' ? 'border-blue-700 bg-blue-50 text-blue-950' : 'border-slate-200 bg-white text-slate-700'}`}><span className="block font-black">Je pars d’un budget client</span><span className="mt-1 block text-xs">Edith recommande les services qui respectent l’enveloppe disponible.</span></button>
                        </div>
                        {proposalMode === 'budget' && (
                          <div className="rounded-2xl border border-blue-100 bg-blue-50 p-4">
                            <p className="text-sm font-black text-blue-950">Budget cible</p>
                            <p className="mt-1 text-xs text-blue-800">Indique si le budget du client est mensuel ou annuel, puis saisis le montant.</p>
                            <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-[auto_1fr] md:items-end">
                              <div className="flex rounded-2xl bg-white p-1 ring-1 ring-blue-100">
                                {(['monthly', 'annual'] as const).map((period) => (
                                  <button key={period} type="button" onClick={() => setBudgetPeriod(period)} className={`rounded-xl px-4 py-2 text-sm font-black ${budgetPeriod === period ? 'bg-blue-700 text-white' : 'text-slate-700 hover:bg-blue-50'}`}>{period === 'monthly' ? 'Mensuel' : 'Annuel'}</button>
                                ))}
                              </div>
                              <label className="text-sm font-bold text-slate-700">Montant du budget<input type="number" min="0" value={targetBudget} onChange={(event) => setTargetBudget(event.target.value)} className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 font-normal" placeholder={budgetPeriod === 'monthly' ? 'Ex.: 2500' : 'Ex.: 50000'} /></label>
                            </div>
                          </div>
                        )}
                        {proposalMode === 'services' && (services.length === 0 ? <EmptyState title="Aucun service configuré" description="Ouvre Admin pour créer les premiers services vendables." /> : (
                          <div className="max-h-[520px] space-y-2 overflow-y-auto pr-1">
                            {services.map((service) => (
                              <label key={service.id} className="flex cursor-pointer items-start gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-3 hover:border-blue-200">
                                <input type="checkbox" checked={selectedServiceCodes.includes(service.code)} onChange={() => toggleService(service.code)} className="mt-1" />
                                <span><span className="block text-sm font-black text-slate-900">{service.name}</span><span className="text-xs text-slate-500">{service.categoryName || 'Service cyber'}</span></span>
                              </label>
                            ))}
                          </div>
                        ))}
                      </div>
                    )}

                    {currentAdvisorStep === 1 && (
                      <div className="space-y-4">
                        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                          <div><h4 className="text-xl font-black text-slate-900">2. {proposalMode === 'budget' ? 'Mini-diagnostic cyber' : 'Questions conditionnelles'}</h4><HelpText>{proposalMode === 'budget' ? 'Réponds à 7 questions rapides inspirées du questionnaire NIST pour qu’Edith recommande les bons services selon le besoin et le budget.' : 'Ces questions apparaissent seulement selon les services ajoutés à cette proposition. Les 12 questions de base restent dans le dossier prospect.'}</HelpText></div>
                          <button type="button" onClick={() => saveWizardAnswers()} disabled={savingAnswers} className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-xs font-black text-slate-700">{savingAnswers && <Loader2 className="h-4 w-4 animate-spin" />}Sauvegarder</button>
                        </div>
                        {visibleQuestions.length === 0 ? <EmptyState title="Aucune question à afficher" description={proposalMode === 'budget' ? 'Les questions du mini-diagnostic seront chargées après la mise à jour de la configuration.' : 'Aucune question conditionnelle pour les services sélectionnés. Tu peux passer à la proposition.'} /> : (
                          <div className="space-y-4">
                            <div className="sticky top-0 z-20 rounded-3xl border border-blue-100 bg-white/95 p-4 shadow-lg shadow-slate-200/60 backdrop-blur">
                              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                                <div>
                                  <p className="text-xs font-black uppercase tracking-wide text-blue-700">Navigation des questions</p>
                                  <p className="mt-1 text-sm font-bold text-slate-600">{visibleQuestions.length - unansweredVisibleQuestionCount}/{visibleQuestions.length} répondues · {unansweredVisibleQuestionCount} à compléter</p>
                                </div>
                                {firstMissingQuestion && <button type="button" onClick={() => scrollToQuestion(firstMissingQuestion.id)} className="rounded-xl bg-amber-700 px-4 py-2 text-sm font-black text-white">Question suivante à compléter</button>}
                              </div>
                              <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
                                {questionSections.map((section) => (
                                  <button key={section.name} type="button" onClick={() => scrollToQuestion(section.questions[0]?.id)} className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-black ring-1 ${section.missing ? 'bg-amber-50 text-amber-900 ring-amber-200' : 'bg-emerald-50 text-emerald-800 ring-emerald-200'}`}>{section.name} · {section.answered}/{section.questions.length}</button>
                                ))}
                              </div>
                            </div>
                            <div className="space-y-4 pr-1">
                              {questionSections.map((section) => (
                                <section key={section.name} className="space-y-3 rounded-3xl border border-slate-100 bg-slate-50/70 p-3">
                                  <div className="flex flex-wrap items-center justify-between gap-2 px-1">
                                    <h5 className="font-black text-slate-950">{section.name}</h5>
                                    <span className={`rounded-full px-3 py-1 text-xs font-black ${section.missing ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'}`}>{section.answered}/{section.questions.length} répondues</span>
                                  </div>
                                  {section.questions.map((question) => (
                                    <div id={`question-${question.id}`} key={question.id} className={`scroll-mt-36 rounded-2xl border p-4 ${isAnswerMissing(answerValues[question.id]) ? 'border-amber-200 bg-amber-50/70' : 'border-slate-100 bg-white'}`}>
                                      <span className="mb-2 block text-sm font-bold text-slate-800">{question.label}</span>
                                      {renderQuestionField(question)}
                                    </div>
                                  ))}
                                </section>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {currentAdvisorStep === 2 && (
                      <div className="space-y-4">
                        <div>
                          <h4 className="text-xl font-black text-slate-900">3. Proposition</h4>
                          <HelpText>Génère une nouvelle proposition ou continue celle qui est ouverte. Les services peuvent ensuite être retirés ou réintégrés.</HelpText>
                        </div>
                        <div className="grid grid-cols-1 gap-3">
                          <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                            <label className="text-sm font-bold text-slate-700">Nom de la proposition<input value={proposalCustomName} onChange={(event) => setProposalCustomName(event.target.value)} className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 font-normal" placeholder="Ex.: Budget 50k, Phase 1, Audit + M365" /><span className="mt-1 block text-xs font-normal text-slate-500">Ce nom servira à distinguer les propositions dans le dossier et la comparaison.</span></label>
                          </div>
                        </div>
                        <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                          <label className="text-sm font-bold text-slate-700">Terme du contrat<input type="number" min="1" max="60" value={contractTermMonths} onChange={(event) => setContractTermMonths(event.target.value)} className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 font-normal" placeholder="12" /><span className="mt-1 block text-xs font-normal text-slate-500">Utilisé pour calculer la mensualité et interpréter un budget mensuel.</span></label>
                        </div>
                        <button type="button" onClick={handleGenerateProposal} disabled={generatingProposal || (proposalMode === 'services' && selectedServiceCodes.length === 0) || (proposalMode === 'budget' && !targetBudget)} className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-blue-700 px-4 py-3 text-sm font-black text-white disabled:cursor-not-allowed disabled:bg-slate-300">
                          {generatingProposal && <Loader2 className="h-4 w-4 animate-spin" />}
                          Générer prix + visuel client
                        </button>
                        {generatedProposal && <div className="rounded-2xl bg-blue-50 p-3 text-sm text-blue-950"><strong>{generatedProposalDisplayName || 'Proposition'}:</strong> {generatedProposal.proposal.finalRecurringMonthlyTotal.toFixed(0)} $ / mois sur {String(generatedProposal.proposal.calculationTrace?.contractTermMonths || contractTermMonths)} mois · {budgetStatusLabel(generatedProposal.proposal)}.</div>}
                        {generatedProposal && (
                          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                            <div className="rounded-2xl border border-blue-100 bg-blue-50 p-4 text-sm text-blue-950">
                              <p className="font-black">Résumé exécutif Edith</p>
                              <p className="mt-2 leading-relaxed">{activeExecutiveSummary}</p>
                            </div>
                            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
                              <p className="font-black text-slate-900">Plan d’action client</p>
                              <div className="mt-2 space-y-2">
                                {activeActionPlan.map((phase, index) => (
                                  <div key={phase.phaseName} className="rounded-xl bg-white p-3 ring-1 ring-slate-100">
                                    <p className="font-black text-slate-900">{actionPlanTitle(phase, index, activeActionPlan.length)}</p>
                                    <p className="mt-1 text-xs font-bold text-slate-500">{actionPlanSubtitle(phase, activeActionPlan.length)}</p>
                                    <p className="mt-1 text-xs text-slate-500">{phase.services.map((line) => line.serviceName).join(' · ')}</p>
                                  </div>
                                ))}
                              </div>
                            </div>
                            {activeReportedServices.length > 0 && (
                              <div className="lg:col-span-2 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950">
                                <p className="font-black">Services reportés / phase 2</p>
                                <ul className="mt-2 list-disc space-y-1 pl-5">
                                  {activeReportedServices.slice(0, 6).map((line) => <li key={line.id}><strong>{line.serviceName}</strong> — {line.recommendationReason || 'Service pertinent à planifier plus tard.'}</li>)}
                                </ul>
                              </div>
                            )}
                          </div>
                        )}
                        {generatedProposal && (
                          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                            <div className="rounded-2xl border border-blue-100 bg-blue-50 p-4 text-sm text-blue-950">
                              <p className="font-black">Pourquoi ces services?</p>
                              <ul className="mt-2 list-disc space-y-1 pl-5">
                                {generatedProposal.lineItems.filter((line) => line.selected).slice(0, 6).map((line) => <li key={line.id}>{line.serviceName}: {line.recommendationReason || 'service sélectionné par le conseiller.'}</li>)}
                              </ul>
                            </div>
                            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
                              <p className="font-black text-slate-900">Hypothèses de prix</p>
                              <div className="mt-2 grid grid-cols-2 gap-2">
                                <span>Terme: {String(generatedProposal.proposal.calculationTrace?.contractTermMonths || contractTermMonths)} mois</span>
                                <span>Budget: {generatedProposal.proposal.calculationTrace?.targetBudgetInput ? `${Number(generatedProposal.proposal.calculationTrace.targetBudgetInput).toFixed(0)} $ ${generatedProposal.proposal.calculationTrace?.budgetPeriod === 'monthly' ? '/ mois' : '/ an'}` : '—'}</span>
                                <span>Mode: {generatedProposal.proposal.calculationTrace?.proposalMode === 'budget' ? 'Budget' : 'Services'}</span>
                                <span>Services retenus: {generatedProposal.lineItems.filter((line) => line.selected).length}</span>
                              </div>
                            </div>
                          </div>
                        )}
                        {!generatedProposal ? <EmptyState title="Aucune proposition générée" description="Clique sur Générer prix + visuel client pour produire les services recommandés et les montants." /> : (
                          <div className="space-y-4">
                            <div className="grid grid-cols-1 gap-3 text-sm md:grid-cols-4">
                              <div className="rounded-2xl bg-slate-50 p-3"><p className="text-slate-500">Services</p><p className="text-2xl font-black text-slate-900">{generatedProposal.lineItems.length}</p></div>
                              <div className="rounded-2xl bg-slate-50 p-3"><p className="text-slate-500">Sous-total</p><p className="text-2xl font-black text-slate-900">{generatedProposal.proposal.subtotalOneTime.toFixed(0)} $</p></div>
                              <div className="rounded-2xl bg-slate-50 p-3"><p className="text-slate-500">Rabais</p><p className="text-2xl font-black text-slate-900">{generatedProposal.proposal.discountTotal.toFixed(0)} $</p></div>
                              <div className="rounded-2xl bg-slate-50 p-3"><p className="text-slate-500">Total</p><p className="text-2xl font-black text-slate-900">{generatedProposal.proposal.finalOneTimeTotal.toFixed(0)} $</p></div>
                            </div>
                            <div className="overflow-hidden rounded-2xl border border-slate-100">
                              {generatedProposal.lineItems.map((line) => (
                                <div key={line.id} className={`grid grid-cols-1 gap-3 border-b border-slate-100 p-4 text-sm last:border-0 md:grid-cols-[1fr_auto] md:items-center ${line.selected ? '' : 'bg-slate-50 opacity-70'}`}>
                                  <div>
                                    <div className="flex flex-wrap items-center gap-2"><p className="font-black text-slate-900">{line.serviceName}</p><span className={`rounded-full px-2 py-0.5 text-[11px] font-black ${line.selected ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'}`}>{line.selected ? 'Retenu' : 'Retiré'}</span></div>
                                    <p className="mt-1 text-xs text-slate-500">{line.recommendationReason || line.selectionSource}</p>
                                    <div className="mt-2 flex flex-wrap gap-3 text-xs font-bold text-slate-600"><span>Ponctuel: {line.roundedAmount.toFixed(0)} $</span><span>{line.recurringAmount > 0 ? `Mensuel: ${line.recurringAmount.toFixed(0)} $ / mois` : 'Aucun mensuel'}</span>{typeof line.calculationTrace?.coefficientComplexite === 'number' && <span>Coefficient de complexité: {line.calculationTrace.coefficientComplexite.toFixed(2)}</span>}</div>
                                  </div>
                                  <button type="button" onClick={() => toggleProposalLine(line.id, !line.selected)} disabled={updatingLineItemId === line.id} className={`inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2 text-xs font-black ${line.selected ? 'border border-slate-200 bg-white text-slate-700 hover:bg-slate-50' : 'bg-blue-700 text-white'}`}>
                                    {updatingLineItemId === line.id && <Loader2 className="h-4 w-4 animate-spin" />}
                                    {line.selected ? 'Retirer' : 'Réintégrer'}
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {currentAdvisorStep === 3 && (
                      <div className="space-y-4">
                        <div>
                          <h4 className="text-xl font-black text-slate-900">4. Rabais</h4>
                          <HelpText>L’escompte multiproduit de 10 % est appliqué automatiquement dès que trois produits ou plus sont retenus. L’escompte multiservice de 5 % peut être ajouté quand le client choisit plusieurs services BZ en une seule signature.</HelpText>
                        </div>
                        {!generatedProposal ? <EmptyState title="Génère d’abord une proposition" description="Les escomptes deviennent disponibles après le calcul initial." /> : (
                          <div className="space-y-4 rounded-2xl bg-slate-50 p-4">
                            <div className="rounded-2xl border border-blue-100 bg-blue-50 p-4 text-sm text-blue-950">
                              Les escomptes multiproduit et multiservices accordés dans le cadre de la présente transaction sont exclusivement applicables à cette vente. Ils constituent une condition promotionnelle temporaire et ne peuvent en aucun cas être rétroactivement appliqués à des achats ou services facturés antérieurement.
                            </div>
                            <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
                              <div className="rounded-2xl bg-white p-4 ring-1 ring-slate-100">
                                <p className="text-sm font-black text-slate-900">Escompte multiproduit</p>
                                <p className="mt-1 text-xs text-slate-500">Automatique · 10 % · appliqué dès que trois produits ou plus sont sélectionnés.</p>
                              </div>
                              <div className="rounded-2xl bg-white p-4 ring-1 ring-slate-100">
                                <p className="text-sm font-black text-slate-900">Escompte multiservice</p>
                                <p className="mt-1 text-xs text-slate-500">Manuel · 5 % · pour plusieurs services BZ signés dans une même transaction.</p>
                                <input value={discountForm.reason} onChange={(event) => setDiscountForm({ ...discountForm, reason: event.target.value })} className="mt-3 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="Raison / contexte de l’escompte multiservice" />
                                <button type="button" onClick={handleApplyDiscount} disabled={applyingDiscount} className="mt-3 inline-flex items-center gap-2 rounded-xl bg-emerald-700 px-4 py-2 text-sm font-black text-white disabled:bg-slate-300">{applyingDiscount && <Loader2 className="h-4 w-4 animate-spin" />}Ajouter l’escompte multiservice 5 %</button>
                              </div>
                              <div className="rounded-2xl bg-white p-4 ring-1 ring-slate-100">
                                <p className="text-sm font-black text-slate-900">Déviation tactique</p>
                                <p className="mt-1 text-xs text-slate-500">Manuelle · maximum 10 % · justification obligatoire.</p>
                                <input type="number" min="0" max="10" value={tacticalDeviationForm.percent} onChange={(event) => setTacticalDeviationForm({ ...tacticalDeviationForm, percent: event.target.value })} className="mt-3 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="Pourcentage, max 10" />
                                <textarea value={tacticalDeviationForm.reason} onChange={(event) => setTacticalDeviationForm({ ...tacticalDeviationForm, reason: event.target.value })} className="mt-3 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="Justification obligatoire" rows={2} />
                                <button type="button" onClick={applyTacticalDeviation} disabled={applyingDiscount || !tacticalDeviationForm.percent || !tacticalDeviationForm.reason.trim()} className="mt-3 inline-flex items-center gap-2 rounded-xl bg-amber-700 px-4 py-2 text-sm font-black text-white disabled:bg-slate-300">{applyingDiscount && <Loader2 className="h-4 w-4 animate-spin" />}Ajouter la déviation tactique</button>
                              </div>
                            </div>
                            {generatedProposal.discounts?.length ? (
                              <div className="space-y-2">
                                <p className="text-sm font-black text-slate-900">Rabais appliqués</p>
                                {generatedProposal.discounts.map((discount) => (
                                  <div key={discount.id} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-white px-4 py-3 text-sm ring-1 ring-slate-100">
                                    <div>
                                      <p className="font-black text-slate-900">{discount.discountType === 'multi_product_auto' ? 'Escompte multiproduit' : discount.discountType === 'multi_service' ? 'Escompte multiservice' : discount.discountType === 'tactical_deviation' ? 'Déviation tactique' : 'Rabais manuel'}</p>
                                      <p className="text-xs text-slate-500">{discount.percent}% · -{discount.amount.toFixed(0)} $ — {discount.reason || 'Aucune raison inscrite'}</p>
                                    </div>
                                    <button type="button" onClick={() => removeDiscount(discount.id)} disabled={applyingDiscount} className="rounded-full border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs font-black text-rose-700 disabled:opacity-60">Supprimer</button>
                                  </div>
                                ))}
                              </div>
                            ) : <EmptyState title="Aucun rabais appliqué" description="L’escompte multiproduit s’ajoute automatiquement à 3 produits ou plus. L’escompte multiservice peut être ajouté ici." />}
                          </div>
                        )}
                      </div>
                    )}

                    {currentAdvisorStep === 4 && (
                      <div className="space-y-4">
                        <div><h4 className="text-xl font-black text-slate-900">5. Visuel client</h4><HelpText>Le visuel est le document clair à présenter au client. L’export PDF ouvre la fenêtre d’impression.</HelpText></div>
                        {!generatedProposal ? <EmptyState title="Aucun visuel client" description="Génère une proposition avant d’ouvrir ou exporter le visuel." /> : <><div className="rounded-2xl border border-blue-100 bg-blue-50 p-4"><p className="font-black text-blue-950">Courriel client prêt à copier</p><p className="mt-1 text-sm text-blue-900">Edith prépare un courriel à envoyer au client avec les inclusions de l’offre, une invitation à planifier une rencontre ou un appel, et deux pièces jointes: <strong>Version complète</strong> et <strong>Version sans prix</strong>.</p><button type="button" onClick={requestOfferEmail} disabled={proposalActionLoading !== null || !generatedProposal.clientVisualHtml} className="mt-3 inline-flex items-center gap-2 rounded-xl bg-blue-700 px-4 py-2 text-sm font-black text-white disabled:bg-slate-300">{proposalActionLoading === 'offer-email' && <Loader2 className="h-4 w-4 animate-spin" />}Préparer le courriel client</button></div><div className="flex flex-wrap gap-2"><button type="button" onClick={() => setIsClientVisualOpen(true)} className="rounded-xl bg-blue-700 px-4 py-2 text-sm font-black text-white">Aperçu plein écran</button><button type="button" onClick={exportClientVisual} disabled={proposalActionLoading !== null} className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-sm font-black text-white disabled:bg-slate-300">{proposalActionLoading === 'export' && <Loader2 className="h-4 w-4 animate-spin" />}Exporter PDF</button><button type="button" onClick={createDeliveryBriefing} disabled={proposalActionLoading !== null} className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-black text-slate-800 disabled:opacity-60">{proposalActionLoading === 'briefing' && <Loader2 className="h-4 w-4 animate-spin" />}Briefing delivery</button></div><div className="max-h-[420px] overflow-y-auto rounded-2xl bg-slate-50 p-4 text-sm text-slate-700" dangerouslySetInnerHTML={{ __html: generatedProposal.clientVisualHtml }} />{generatedProposal.outputs?.length ? <div className="rounded-2xl bg-slate-50 p-4"><h5 className="font-black text-slate-900">Documents générés</h5><div className="mt-2 space-y-2 text-sm text-slate-600">{generatedProposal.outputs.slice(0, 6).map((output) => <div key={output.id} className="flex items-center justify-between gap-3 rounded-xl bg-white px-3 py-2"><span>{output.title}</span><span className="text-xs font-black uppercase text-slate-500">{output.outputType === 'internal_summary' ? 'Briefing' : output.outputType === 'pdf_export' ? 'PDF' : 'Visuel'}</span></div>)}</div></div> : <EmptyState title="Aucun document exporté" description="Export PDF, version sans prix ou briefing delivery apparaîtront ici." />}</>}
                      </div>
                    )}

                    {currentAdvisorStep === 5 && (
                      <div className="space-y-4">
                        <div>
                          <h4 className="text-xl font-black text-slate-900">6. Suivi de la proposition</h4>
                          <HelpText>Fais avancer l’offre dans le bon ordre: prête à présenter ou briefing préparé, présentée au client, puis décision acceptée ou refusée.</HelpText>
                        </div>
                        {!generatedProposal ? (
                          <EmptyState title="Aucune proposition active" description="Ouvre ou génère une proposition avant de préparer la présentation, le briefing ou la décision client." />
                        ) : (
                          <div className="space-y-4">
                            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                              <div className="flex flex-wrap items-center justify-between gap-3">
                                <div>
                                  <p className="text-xs font-black uppercase tracking-wide text-slate-500">Statut actuel</p>
                                  <p className="text-lg font-black text-slate-950">{statusLabel(generatedProposal.proposal.status)}</p>
                                </div>
                                <span className="rounded-full bg-white px-3 py-1 text-xs font-black text-slate-700 ring-1 ring-slate-200">Offre #{generatedProposal.proposal.id.slice(0, 8)}</span>
                              </div>
                            </div>

                            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                              <section className={`rounded-2xl border p-4 ${['ready', 'sent', 'presented', 'review', 'accepted', 'refused', 'converted'].includes(generatedProposal.proposal.status) ? 'border-emerald-200 bg-emerald-50' : 'border-blue-100 bg-blue-50'}`}>
                                <p className="text-xs font-black uppercase tracking-wide text-blue-700">1. Préparer la présentation</p>
                                <h5 className="mt-1 font-black text-slate-950">Prête à présenter ou briefing</h5>
                                <p className="mt-2 text-sm font-bold text-slate-700">Marque l’offre prête quand le visuel est final, ou demande à Edith d’envoyer le briefing de proposition.</p>
                                <div className="mt-4 grid gap-2">
                                  <button type="button" onClick={() => changeProposalStatus('ready')} disabled={proposalActionLoading !== null || ['ready', 'sent', 'presented', 'review', 'accepted', 'refused', 'converted'].includes(generatedProposal.proposal.status)} className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-700 px-4 py-3 text-sm font-black text-white disabled:bg-slate-300">
                                    {proposalActionLoading === 'ready' && <Loader2 className="h-4 w-4 animate-spin" />}
                                    Marquer prête à présenter
                                  </button>
                                  <button type="button" onClick={requestOfferEmail} disabled={proposalActionLoading !== null || ['presented', 'accepted', 'refused', 'converted'].includes(generatedProposal.proposal.status)} className="inline-flex items-center justify-center gap-2 rounded-xl border border-blue-200 bg-white px-4 py-3 text-sm font-black text-blue-800 disabled:opacity-60">
                                    {proposalActionLoading === 'offer-email' && <Loader2 className="h-4 w-4 animate-spin" />}
                                    Préparer le courriel client
                                  </button>
                                </div>
                              </section>

                              <section className={`rounded-2xl border p-4 ${['presented', 'review', 'accepted', 'refused', 'converted'].includes(generatedProposal.proposal.status) ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200 bg-white'}`}>
                                <p className="text-xs font-black uppercase tracking-wide text-blue-700">2. Rencontre client</p>
                                <h5 className="mt-1 font-black text-slate-950">Offre présentée</h5>
                                <p className="mt-2 text-sm font-bold text-slate-700">Utilise cette action une fois que la proposition a réellement été présentée ou revue avec le client.</p>
                                <button type="button" onClick={() => changeProposalStatus('presented')} disabled={proposalActionLoading !== null || !['ready', 'sent', 'review'].includes(generatedProposal.proposal.status)} className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-blue-700 px-4 py-3 text-sm font-black text-white disabled:bg-slate-300">
                                  {proposalActionLoading === 'presented' && <Loader2 className="h-4 w-4 animate-spin" />}
                                  Marquer comme présentée
                                </button>
                              </section>

                              <section className={`rounded-2xl border p-4 ${['accepted', 'refused', 'converted'].includes(generatedProposal.proposal.status) ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200 bg-white'}`}>
                                <p className="text-xs font-black uppercase tracking-wide text-blue-700">3. Décision client</p>
                                <h5 className="mt-1 font-black text-slate-950">Acceptée ou refusée</h5>
                                <p className="mt-2 text-sm font-bold text-slate-700">Inscris la décision seulement après le retour final du client. Le briefing s’envoie uniquement avec l’action de briefing.</p>
                                <div className="mt-4 grid grid-cols-1 gap-2">
                                  <button type="button" onClick={() => changeProposalStatus('accepted')} disabled={proposalActionLoading !== null || !['presented', 'review'].includes(generatedProposal.proposal.status)} className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-700 px-4 py-3 text-sm font-black text-white disabled:bg-slate-300">
                                    {proposalActionLoading === 'accepted' && <Loader2 className="h-4 w-4 animate-spin" />}
                                    Client a accepté
                                  </button>
                                  <button type="button" onClick={() => changeProposalStatus('refused')} disabled={proposalActionLoading !== null || !['presented', 'review'].includes(generatedProposal.proposal.status)} className="inline-flex items-center justify-center gap-2 rounded-xl border border-rose-200 bg-white px-4 py-3 text-sm font-black text-rose-700 disabled:opacity-60">
                                    {proposalActionLoading === 'refused' && <Loader2 className="h-4 w-4 animate-spin" />}
                                    Client a refusé
                                  </button>
                                  {generatedProposal.proposal.status === 'accepted' ? (
                                    <button type="button" onClick={createDeliveryProjectFromAcceptedOffer} disabled={proposalActionLoading !== null} className="inline-flex items-center justify-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm font-black text-blue-900 disabled:opacity-60">
                                      {proposalActionLoading === 'delivery-project' && <Loader2 className="h-4 w-4 animate-spin" />}
                                      Créer le projet de livraison
                                    </button>
                                  ) : null}
                                </div>
                              </section>
                            </div>

                            <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-4">
                              <p className="text-sm font-bold text-slate-600">Besoin de retirer cette offre du dossier actif?</p>
                              <button type="button" onClick={() => requestArchiveProposal(generatedProposal.proposal)} disabled={proposalActionLoading !== null} className="rounded-full px-3 py-1.5 text-xs font-black text-slate-500 underline disabled:opacity-60">Archiver la proposition</button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    <div className="mt-6 flex items-center justify-between border-t border-slate-100 pt-4">
                      <button type="button" onClick={() => setAdvisorStep(previousAdvisorStep())} disabled={currentAdvisorStep === 0} className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-black text-slate-700 disabled:opacity-40">Retour</button>
                      {currentAdvisorStep === advisorWizardSteps.length - 1 ? (
                        <button type="button" onClick={() => saveWizardAnswers({ closeAfterSave: true })} disabled={savingAnswers} className="rounded-xl bg-blue-700 px-4 py-2 text-sm font-black text-white disabled:bg-slate-300">{savingAnswers ? 'Sauvegarde…' : 'Sauvegarder et quitter'}</button>
                      ) : (
                        <button type="button" onClick={async () => { await saveWizardAnswers(); setAdvisorStep(nextAdvisorStep()); }} disabled={savingAnswers} className="rounded-xl bg-blue-700 px-4 py-2 text-sm font-black text-white disabled:bg-slate-300">{savingAnswers ? 'Sauvegarde…' : 'Enregistrer et continuer'}</button>
                      )}
                    </div>
                  </main>
                  </section>

                  <aside className="space-y-4 xl:sticky xl:top-4 xl:self-start">
                    <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                      <h4 className="font-black text-slate-950">Résumé offre</h4>
                      <p className="mt-3 text-4xl font-black tracking-tight text-blue-700">{generatedProposal ? generatedProposal.proposal.finalOneTimeTotal.toLocaleString('fr-CA', { maximumFractionDigits: 0 }) : '0'} $</p>
                      <div className="mt-4 divide-y divide-slate-100 text-sm">
                        <div className="flex justify-between py-2"><span className="text-slate-500">Sous-total</span><strong>{generatedProposal ? generatedProposal.proposal.subtotalOneTime.toLocaleString('fr-CA', { maximumFractionDigits: 0 }) : '0'} $</strong></div>
                        <div className="flex justify-between py-2"><span className="text-slate-500">Rabais</span><strong>{generatedProposal ? generatedProposal.proposal.discountTotal.toLocaleString('fr-CA', { maximumFractionDigits: 0 }) : '0'} $</strong></div>
                        <div className="flex justify-between py-2"><span className="text-slate-500">Terme</span><strong>{String(generatedProposal?.proposal.calculationTrace?.contractTermMonths || contractTermMonths)} mois</strong></div>
                        <div className="flex justify-between py-2"><span className="text-slate-500">Statut</span><strong>{generatedProposal ? statusLabel(generatedProposal.proposal.status) : 'Brouillon'}</strong></div>
                      </div>
                      <div className="mt-4 h-2 overflow-hidden rounded-full bg-slate-100"><span className="block h-full rounded-full bg-gradient-to-r from-emerald-500 to-blue-700" style={{ width: `${Math.max(8, Math.round(((proposalReadiness.items.length - proposalReadiness.missing.length) / Math.max(1, proposalReadiness.items.length)) * 100))}%` }} /></div>
                      <p className="mt-2 text-xs font-bold text-slate-500">Progression : {Math.round(((proposalReadiness.items.length - proposalReadiness.missing.length) / Math.max(1, proposalReadiness.items.length)) * 100)} %</p>
                    </section>
                    <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                      <h4 className="font-black text-slate-950">Services retenus</h4>
                      <div className="mt-3 divide-y divide-slate-100 text-sm">
                        {(generatedProposal?.lineItems.filter((line) => line.selected).slice(0, 6) || []).map((line) => <div key={line.id} className="flex items-start justify-between gap-3 py-2"><span className="text-slate-600">{line.serviceName}</span><strong className="shrink-0 text-slate-950">{line.roundedAmount.toLocaleString('fr-CA', { maximumFractionDigits: 0 })} $</strong></div>)}
                        {!generatedProposal && selectedServiceCodes.slice(0, 6).map((code) => <div key={code} className="flex items-center justify-between gap-3 py-2"><span className="text-slate-600">{serviceNameByCode.get(code) || code}</span><strong className="text-blue-700">Choisi</strong></div>)}
                      </div>
                      <button type="button" onClick={() => setAdvisorStep(0)} className="mt-4 w-full rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-black text-slate-700">Modifier services</button>
                    </section>
                    <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                      <h4 className="font-black text-slate-950">Aperçu rapide</h4>
                      <div className="mt-3 rounded-2xl bg-gradient-to-br from-slate-950 to-blue-900 p-5 text-white">
                        <p className="font-black">Plan d’action cybersécurité</p>
                        <p className="mt-4 text-sm font-bold text-amber-200">Mandat {String(generatedProposal?.proposal.calculationTrace?.contractTermMonths || contractTermMonths)} mois</p>
                        <p className="mt-4 text-sm text-blue-100">{prospectDetail?.prospect.organizationName || 'Client'} · Proposition BZ</p>
                      </div>
                      <button type="button" onClick={() => generatedProposal && setIsClientVisualOpen(true)} disabled={!generatedProposal?.clientVisualHtml} className="mt-4 w-full rounded-xl bg-slate-950 px-4 py-2 text-sm font-black text-white disabled:bg-slate-300">Ouvrir aperçu</button>
                    </section>
                  </aside>
                </main>
              </div>            )}
          </div>
        </div>
      )}

      {isClientVisualOpen && generatedProposal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm">
          <div className="max-h-[94vh] w-full max-w-5xl overflow-y-auto rounded-3xl bg-white shadow-2xl">
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-100 bg-white/95 p-5 backdrop-blur">
              <div><p className="text-xs font-black uppercase tracking-wide text-blue-600">Aperçu client</p><h3 className="text-xl font-black text-slate-900">Visuel client prêt à présenter</h3></div>
              <button type="button" onClick={() => setIsClientVisualOpen(false)} className="rounded-full p-2 text-slate-500 hover:bg-slate-100"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-6" dangerouslySetInnerHTML={{ __html: generatedProposal.clientVisualHtml }} />
          </div>
        </div>
      )}

      {isOfferEmailConfirmOpen && generatedProposal && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/50 p-4 backdrop-blur-sm">
          <div className="max-h-[92vh] w-full max-w-4xl overflow-y-auto rounded-3xl bg-white p-6 shadow-2xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-black uppercase tracking-wide text-blue-600">Courriel client</p>
                <h3 className="mt-1 text-2xl font-black text-slate-900">Courriel prêt à copier</h3>
                <p className="mt-2 text-sm font-bold text-slate-600">Copie le message dans Gmail ou Outlook, puis joins les deux fichiers préparés par Edith.</p>
              </div>
              <button type="button" onClick={() => setIsOfferEmailConfirmOpen(false)} className="rounded-full p-2 text-slate-500 hover:bg-slate-100"><X className="h-5 w-5" /></button>
            </div>
            <div className="mt-5 grid grid-cols-1 gap-4 lg:grid-cols-[1fr_260px]">
              <div className="space-y-4">
                <label className="block text-sm font-black text-slate-700">Sujet
                  <input readOnly value={clientEmailSubject} className="mt-2 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 font-bold text-slate-900" />
                </label>
                <label className="block text-sm font-black text-slate-700">Message à envoyer au client
                  <textarea readOnly value={clientEmailBody} rows={14} className="mt-2 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 font-mono text-sm leading-relaxed text-slate-900" />
                </label>
              </div>
              <aside className="space-y-4">
                <div className="rounded-2xl border border-blue-100 bg-blue-50 p-4 text-sm font-bold text-blue-950">
                  <p className="font-black">Pièces jointes</p>
                  <p className="mt-1 text-xs leading-relaxed text-blue-800">Télécharge les fichiers HTML, puis ajoute-les au courriel client.</p>
                  <div className="mt-3 grid gap-2">
                    <a href={attachmentHref(fullBriefingOutput?.htmlSnapshot || generatedProposal.clientVisualHtml)} download={safeAttachmentName('complet')} className="rounded-xl bg-blue-700 px-4 py-2 text-center text-xs font-black text-white">Télécharger version complète</a>
                    <a href={attachmentHref(noPriceBriefingOutput?.htmlSnapshot)} download={safeAttachmentName('sans_prix')} className={`rounded-xl px-4 py-2 text-center text-xs font-black ${noPriceBriefingOutput?.htmlSnapshot ? 'bg-white text-blue-800 ring-1 ring-blue-200' : 'pointer-events-none bg-slate-200 text-slate-500'}`}>Télécharger version sans prix</a>
                  </div>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-xs font-bold text-slate-700">
                  <p className="font-black text-slate-900">Destinataire</p>
                  <p className="mt-1">À compléter dans Gmail selon le contact client.</p>
                  {prospectDetail?.prospect.contactEmail && <p className="mt-2 rounded-xl bg-white px-3 py-2 text-slate-900">{prospectDetail.prospect.contactEmail}</p>}
                </div>
                {proposalReadiness.missing.length > 0 && (
                  <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm font-bold text-amber-900">
                    Il reste {pluralize(proposalReadiness.missing.length, 'élément à valider', 'éléments à valider')} dans la proposition.
                  </div>
                )}
              </aside>
            </div>
            <div className="mt-6 flex flex-wrap justify-end gap-3">
              <button type="button" onClick={() => setIsOfferEmailConfirmOpen(false)} className="rounded-2xl border border-slate-200 px-5 py-3 text-sm font-black text-slate-700">Fermer</button>
              <button type="button" onClick={copyClientEmail} className="rounded-2xl bg-blue-700 px-5 py-3 text-sm font-black text-white">Copier le courriel</button>
            </div>
          </div>
        </div>
      )}

      {isProfileEditOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/50 p-4 backdrop-blur-sm">
          <div className="w-full max-w-2xl rounded-3xl bg-white p-6 shadow-2xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-black uppercase tracking-wide text-blue-600">Profil prospect</p>
                <h3 className="mt-1 text-2xl font-black text-slate-900">Modifier les coordonnées</h3>
                <p className="mt-2 text-sm font-bold text-slate-600">Ces champs sont le profil CRM du dossier. Les 12 questions de qualification restent dans la section Questions répondues.</p>
              </div>
              <button type="button" onClick={() => setIsProfileEditOpen(false)} className="rounded-full p-2 text-slate-500 hover:bg-slate-100"><X className="h-5 w-5" /></button>
            </div>
            <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
              <label className="md:col-span-2 text-sm font-bold text-slate-700">Organisation *<input value={profileEditForm.organizationName} onChange={(event) => setProfileEditForm({ ...profileEditForm, organizationName: event.target.value })} className="mt-1 w-full rounded-2xl border border-slate-200 px-4 py-3 font-normal outline-none focus:border-blue-400" placeholder="Nom de l’organisation" /></label>
              <label className="text-sm font-bold text-slate-700">Contact<input value={profileEditForm.contactName} onChange={(event) => setProfileEditForm({ ...profileEditForm, contactName: event.target.value })} className="mt-1 w-full rounded-2xl border border-slate-200 px-4 py-3 font-normal outline-none focus:border-blue-400" placeholder="Nom du contact" /></label>
              <label className="text-sm font-bold text-slate-700">Courriel<input value={profileEditForm.contactEmail} onChange={(event) => setProfileEditForm({ ...profileEditForm, contactEmail: event.target.value })} className="mt-1 w-full rounded-2xl border border-slate-200 px-4 py-3 font-normal outline-none focus:border-blue-400" placeholder="contact@client.ca" /></label>
              <label className="text-sm font-bold text-slate-700">Téléphone<input value={profileEditForm.contactPhone} onChange={(event) => setProfileEditForm({ ...profileEditForm, contactPhone: event.target.value })} className="mt-1 w-full rounded-2xl border border-slate-200 px-4 py-3 font-normal outline-none focus:border-blue-400" placeholder="Téléphone" /></label>
              <label className="md:col-span-2 text-sm font-bold text-slate-700">Notes<textarea value={profileEditForm.notes} onChange={(event) => setProfileEditForm({ ...profileEditForm, notes: event.target.value })} className="mt-1 min-h-[110px] w-full rounded-2xl border border-slate-200 px-4 py-3 font-normal outline-none focus:border-blue-400" placeholder="Contexte, besoin initial, prochaine action…" /></label>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button type="button" onClick={() => setIsProfileEditOpen(false)} className="rounded-2xl border border-slate-200 px-5 py-3 text-sm font-black text-slate-700">Annuler</button>
              <button type="button" onClick={saveProspectProfile} disabled={savingProspectProfile || !profileEditForm.organizationName.trim()} className="inline-flex items-center gap-2 rounded-2xl bg-blue-700 px-5 py-3 text-sm font-black text-white disabled:bg-slate-300">{savingProspectProfile && <Loader2 className="h-4 w-4 animate-spin" />}Enregistrer le profil</button>
            </div>
          </div>
        </div>
      )}

      {archiveProposalTarget && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/50 p-4 backdrop-blur-sm">
          <div className="w-full max-w-xl rounded-3xl bg-white p-6 shadow-2xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-black uppercase tracking-wide text-red-600">Archiver une proposition</p>
                <h3 className="mt-1 text-2xl font-black text-slate-900">Justification requise</h3>
                <p className="mt-2 text-sm font-bold text-slate-600">La proposition sera retirée du pipeline actif et conservée dans les archives avec la justification.</p>
              </div>
              <button type="button" onClick={() => setArchiveProposalTarget(null)} className="rounded-full p-2 text-slate-500 hover:bg-slate-100"><X className="h-5 w-5" /></button>
            </div>
            <div className="mt-5 rounded-2xl border border-slate-100 bg-slate-50 p-4">
              <p className="font-black text-slate-950">{proposalDisplayTitle(archiveProposalTarget)}</p>
              <p className="mt-1 text-sm font-bold text-slate-600">{archiveProposalTarget.organizationName} · {archiveProposalTarget.finalOneTimeTotal.toLocaleString('fr-CA', { maximumFractionDigits: 0 })} $</p>
            </div>
            <label className="mt-5 block text-sm font-black text-slate-700">Pourquoi archiver cette proposition?<textarea value={archiveJustification} onChange={(event) => setArchiveJustification(event.target.value)} className="mt-2 min-h-[120px] w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 font-normal text-slate-900 outline-none focus:border-red-300" placeholder="Ex: doublon remplacé par une nouvelle version, erreur de préparation, offre annulée par le conseiller…" /></label>
            <div className="mt-5 flex flex-wrap justify-end gap-3">
              <button type="button" onClick={() => setArchiveProposalTarget(null)} className="rounded-2xl border border-slate-200 px-5 py-3 text-sm font-black text-slate-700">Annuler</button>
              <button type="button" onClick={confirmArchiveProposal} disabled={archivingProposal || !archiveJustification.trim()} className="inline-flex items-center gap-2 rounded-2xl bg-red-700 px-5 py-3 text-sm font-black text-white disabled:bg-slate-300">{archivingProposal && <Loader2 className="h-4 w-4 animate-spin" />}Archiver la proposition</button>
            </div>
          </div>
        </div>
      )}

      {isCreateOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4 backdrop-blur-sm">
          <form onSubmit={handleCreateProspect} className="w-full max-w-2xl rounded-3xl bg-white p-6 shadow-2xl">
            <div className="mb-5 flex items-start justify-between gap-4">
              <div>
                <h3 className="text-2xl font-black text-slate-900">Créer un prospect cyber</h3>
                <p className="mt-1 text-sm text-slate-600">Ce prospect sera créé dans la BD métier PRIsME.</p>
              </div>
              <button type="button" onClick={() => setIsCreateOpen(false)} className="rounded-full p-2 text-slate-500 hover:bg-slate-100"><X className="h-5 w-5" /></button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="md:col-span-2 text-sm font-bold text-slate-700">Organisation *<input value={form.organizationName} onChange={(e) => setForm({ ...form, organizationName: e.target.value })} className="mt-1 w-full rounded-2xl border border-slate-200 px-4 py-3 font-normal outline-none focus:border-blue-400" placeholder="Nom de l’organisation" /></label>
              <label className="text-sm font-bold text-slate-700">Contact<input value={form.contactName} onChange={(e) => setForm({ ...form, contactName: e.target.value })} className="mt-1 w-full rounded-2xl border border-slate-200 px-4 py-3 font-normal outline-none focus:border-blue-400" placeholder="Nom du contact" /></label>
              <label className="text-sm font-bold text-slate-700">Courriel<input value={form.contactEmail} onChange={(e) => setForm({ ...form, contactEmail: e.target.value })} className="mt-1 w-full rounded-2xl border border-slate-200 px-4 py-3 font-normal outline-none focus:border-blue-400" placeholder="contact@client.ca" /></label>
              <label className="text-sm font-bold text-slate-700">Téléphone<input value={form.contactPhone} onChange={(e) => setForm({ ...form, contactPhone: e.target.value })} className="mt-1 w-full rounded-2xl border border-slate-200 px-4 py-3 font-normal outline-none focus:border-blue-400" placeholder="Téléphone" /></label>
              <label className="md:col-span-2 text-sm font-bold text-slate-700">Notes<textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1 min-h-[110px] w-full rounded-2xl border border-slate-200 px-4 py-3 font-normal outline-none focus:border-blue-400" placeholder="Contexte, besoin initial, prochaine action…" /></label>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button type="button" onClick={() => setIsCreateOpen(false)} className="rounded-2xl border border-slate-200 px-5 py-3 text-sm font-bold text-slate-700">Annuler</button>
              <button type="submit" disabled={creating || !form.organizationName.trim()} className="inline-flex items-center gap-2 rounded-2xl bg-blue-700 px-5 py-3 text-sm font-black text-white disabled:cursor-not-allowed disabled:bg-slate-300">
                {creating && <Loader2 className="h-4 w-4 animate-spin" />}
                Créer le prospect
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function HelpText({ children }: { children: ReactNode }) {
  return <p className="mt-1 rounded-xl bg-blue-50 px-3 py-2 text-sm text-blue-900">{children}</p>;
}

function EmptyState({ title, description }: { title: string; description: string }) {
  return (
    <div className="rounded-3xl border border-dashed border-slate-200 bg-slate-50 p-6 text-center">
      <p className="font-black text-slate-900">{title}</p>
      <p className="mt-2 text-sm text-slate-600">{description}</p>
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: number }) {
  return (
    <div className="relative overflow-hidden rounded-[1.35rem] border border-blue-200 bg-white p-5 shadow-md shadow-blue-950/5 ring-1 ring-blue-50">
      <div className="absolute inset-y-0 left-0 w-1.5 bg-gradient-to-b from-blue-700 to-cyan-400" />
      <p className="text-[11px] font-black uppercase tracking-wide text-blue-700">{label}</p>
      <p className="mt-2 text-3xl font-black text-slate-950">{value}</p>
    </div>
  );
}
